import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";

/*
  ╔═══════════════════════════════════════════════════════════════╗
  ║  NEXUS TRADES — Protected Mentorship Platform                 ║
  ║  Features: Hostinger API, DRM, Discord Auth, VPN Blk, UI/UX   ║
  ╚═══════════════════════════════════════════════════════════════╝
*/

// ─── HOSTINGER API DATABASE SETUP (JWT-AUTHENTICATED) ────────────────────
const IS_DEV = process.env.NODE_ENV === 'development';
function _log(...args) { if (IS_DEV) console.warn('[NT]', ...args); }

const VALID_COLLECTIONS = new Set([
  "nt-students","nt-videos","nt-breakdowns","nt-notes","nt-chatlogs",
  "nt-activity-log","nt-announcements","nt-homework","nt-friend-requests",
  "nt-assignments","nt-prop-accounts"
]);

const DB = {
  _jwt: null,
  _csrf: null,
  _timeout: 15000,
  setToken(jwt) { DB._jwt = jwt; },
  setCsrf(token) {
    DB._csrf = token;
    try { if (token) localStorage.setItem('nt_csrf', token); else localStorage.removeItem('nt_csrf'); } catch (e) { _log("csrf persist failed:", e.name); }
  },
  _authHeaders() {
    const h = { "Content-Type": "application/json" };
    if (DB._jwt) h["Authorization"] = `Bearer ${DB._jwt}`;
    if (DB._csrf) h["X-CSRF-Token"] = DB._csrf;
    return h;
  },
  uploadHeaders() {
    const h = DB._jwt ? { "Authorization": `Bearer ${DB._jwt}` } : {};
    if (DB._csrf) h["X-CSRF-Token"] = DB._csrf;
    return h;
  },
  async get(k) {
    if (!VALID_COLLECTIONS.has(k)) { _log("Invalid collection:", k); return null; }
    try {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), DB._timeout);
      const res = await fetch(`https://nexustrades.net/api.php?action=get&col=${encodeURIComponent(k)}`, {
        headers: DB._authHeaders(), credentials: "include", signal: ctrl.signal
      });
      clearTimeout(t);
      if (res.status === 401 || res.status === 403) { _log("Auth rejected"); return null; }
      if (res.status === 429) { _log("Rate limited"); return null; }
      const items = await res.json();
      if (!items || !Array.isArray(items)) return null;
      if (items.length === 0) return k === SK.activityLog ? [] : null;
      items.sort((a, b) => (b.id && a.id) ? b.id.localeCompare(a.id) : 0);
      return items;
    } catch (e) {
      _log("Read error:", e.name);
      return null;
    }
  },
  async set(k, v) {
    if (!VALID_COLLECTIONS.has(k)) { _log("Invalid collection:", k); return false; }
    try {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), DB._timeout);
      const res = await fetch(`https://nexustrades.net/api.php?action=set&col=${encodeURIComponent(k)}`, {
        method: "POST",
        headers: DB._authHeaders(),
        body: JSON.stringify(v),
        credentials: "include",
        signal: ctrl.signal
      });
      clearTimeout(t);
      if (res.status === 401 || res.status === 403) { _log("Auth rejected"); return false; }
      if (res.status === 429) { _log("Rate limited"); return false; }
      const result = await res.json();
      return result.success === true;
    } catch (e) {
      _log("Write error:", e.name);
      return false;
    }
  },
};

const SK = { students:"nt-students", videos:"nt-videos", breakdowns:"nt-breakdowns", notes:"nt-notes", chatlogs:"nt-chatlogs", activityLog:"nt-activity-log", announcements:"nt-announcements", homework:"nt-homework", friendRequests:"nt-friend-requests", assignments:"nt-assignments", propAccounts:"nt-prop-accounts" };

const LEGACY_ADMIN_USER = "kev1827";

// ─── SERVER-SIDE IP DETECTION ──────────────────────────────────────────────
let cachedIP = null;
async function getIP() {
  if (cachedIP) return cachedIP;
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 5000);
    const res = await fetch("https://nexustrades.net/ip.php", { credentials: "include", signal: ctrl.signal });
    clearTimeout(t);
    const data = await res.json();
    cachedIP = data.raw || data.ip || "unknown";
    return cachedIP;
  } catch (e) {
    _log("Server IP fetch failed:", e.name);
    cachedIP = "unknown";
    return cachedIP;
  }
}

// ─── SECURITY UTILITIES ─────────────────────────────────────────────────────
function safeURL(url) {
  if (!url || typeof url !== 'string') return null;
  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.protocol === 'https:' || parsed.protocol === 'http:') return url;
    return null;
  } catch { return null; }
}

function secureId() {
  try { return crypto.randomUUID(); }
  catch { return Date.now().toString(36) + Math.random().toString(36).slice(2, 10); }
}

// ─── TEXT SANITIZATION ─────────────────────────────────────────────────────
function sanitizeText(str) {
  if (typeof str !== 'string') return '';
  return str.replace(/[<>"'&]/g, c => ({ '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;', '&': '&amp;' }[c] || c));
}

function sanitizeObj(obj) {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) return obj.map(sanitizeObj);
  if (typeof obj === 'object') {
    const clean = Object.create(null);
    for (const key of Object.keys(obj)) {
      if (key === '__proto__' || key === 'constructor' || key === 'prototype') continue;
      clean[key] = sanitizeObj(obj[key]);
    }
    return clean;
  }
  return obj;
}

// ─── SESSION INTEGRITY (tamper detection via SHA-256) ──────────────────────
async function computeSessionHash(userData) {
  const payload = `${userData.discordId}:${userData.role}:${userData.name}`;
  try {
    const data = new TextEncoder().encode(payload);
    const buf = await crypto.subtle.digest("SHA-256", data);
    return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
  } catch {
    let h1 = 0xdeadbeef, h2 = 0x41c6ce57;
    for (let i = 0; i < payload.length; i++) {
      const c = payload.charCodeAt(i);
      h1 = Math.imul(h1 ^ c, 2654435761);
      h2 = Math.imul(h2 ^ c, 1597334677);
    }
    h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
    h2 = Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    return ((h1 ^ h2) >>> 0).toString(36) + ((h2 ^ h1) >>> 0).toString(36);
  }
}

async function createSecureSession(userData, jwt) {
  const { _jwt, ...profileWithoutJwt } = userData;
  const hashVal = await computeSessionHash(profileWithoutJwt);
  const sessionData = { ...profileWithoutJwt, _h: hashVal, _t: Date.now() };
  localStorage.setItem("nt_user", JSON.stringify(sessionData));
  if (jwt) DB.setToken(jwt);
  return sessionData;
}

function validateSession(stored) {
  if (!stored || !stored._h || !stored.discordId || !stored.role) return null;
  return stored;
}

async function validateSessionAsync(stored) {
  if (!validateSession(stored)) return null;
  const expected = await computeSessionHash(stored);
  if (stored._h !== expected) return null;
  return stored;
}

// ─── ANTI-DEVTOOLS DETECTION ──────────────────────────────────────────────
let devToolsOpen = false;
function detectDevTools(onDetected) {
  // Method 1: debugger timing
  const checkTiming = () => {
    const start = performance.now();
    // eslint-disable-next-line no-debugger
    debugger;
    const elapsed = performance.now() - start;
    if (elapsed > 100 && !devToolsOpen) {
      devToolsOpen = true;
      onDetected("DevTools detected via debugger timing.");
    }
  };

  // Method 2: Window size discrepancy (outer - inner)
  const checkSize = () => {
    const widthDiff = window.outerWidth - window.innerWidth;
    const heightDiff = window.outerHeight - window.innerHeight;
    if ((widthDiff > 200 || heightDiff > 200) && !devToolsOpen) {
      devToolsOpen = true;
      onDetected("DevTools detected via window size discrepancy.");
    }
  };

  // Method 3: Console property access trap
  const el = new Image();
  Object.defineProperty(el, 'id', {
    get: function() {
      if (!devToolsOpen) {
        devToolsOpen = true;
        onDetected("DevTools console opened (property access trap).");
      }
      return '';
    }
  });

  // Run checks periodically
  const intervalId = setInterval(() => {
    checkSize();
    // eslint-disable-next-line no-console
    console.log('%c', el);
  }, 3000);

  // Also run timing check less frequently (it causes a breakpoint)
  const timingId = setInterval(checkTiming, 15000);

  return () => { clearInterval(intervalId); clearInterval(timingId); };
}

// Strictly PC + Google Chrome ONLY
const isAllowedDevice = () => {
  const ua = navigator.userAgent;
  const isChrome = /Chrome/.test(ua) && /Google Inc/.test(navigator.vendor) && !/Edge|Edg|OPR|Opera/.test(ua);
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
  return isChrome && !isMobile;
};

const safePnl = (val) => {
  if (typeof val === 'number') return val;
  if (!val) return 0;
  const str = val.toString().replace(/[^0-9.-]+/g, "");
  return parseFloat(str) || 0;
};

// ─── PROP FIRM CONFIGURATION ─────────────────────────────────────────────
const PROP_FIRMS = {
  lucid: {
    name: "Lucid Trading", icon: "💎",
    plans: {
      flex: {
        name: "LucidFlex", sizes: [25000, 50000, 100000, 150000], profitSplit: 0.9,
        minDailyProfit: { 25000: 100, 50000: 150, 100000: 200, 150000: 250 },
        profitableDaysRequired: 5, minPayout: 500,
        maxPayout: { 25000: 1000, 50000: 2000, 100000: 2500, 150000: 3000 },
        maxPayoutPct: 0.5, buffer: 0, maxPayouts: 6,
        payoutFrequency: null, consistencyRule: "profitable_days",
      },
      pro: {
        name: "LucidPro", sizes: [25000, 50000, 100000, 150000], profitSplit: 0.9,
        minDailyProfit: {},
        minProfitGoal: { 25000: 250, 50000: 500, 100000: 750, 150000: 1000 },
        profitableDaysRequired: 0, minPayout: 500,
        maxPayout: { 25000: 1500, 50000: 2500, 100000: 3000, 150000: 3500 },
        maxPayoutFirst: { 25000: 1000, 50000: 2000, 100000: 2500, 150000: 3000 },
        maxPayoutPct: 1.0,
        buffer: { 25000: 1100, 50000: 2100, 100000: 3100, 150000: 4600 },
        maxPayouts: null, payoutFrequency: null,
        consistencyRule: "forty_percent",
      }
    }
  },
  mffu: {
    name: "MyFundedFutures", icon: "🚀",
    plans: {
      rapid: {
        name: "Rapid", sizes: [50000, 100000, 150000], profitSplit: 0.9,
        minDailyProfit: {}, profitableDaysRequired: 0, minPayout: 500,
        maxPayout: {}, maxPayoutPct: 1.0,
        buffer: { 50000: 2100, 100000: 3100, 150000: 4600 },
        maxPayouts: null, payoutFrequency: "daily", firstPayoutAfterHours: 24,
        consistencyRule: "none",
      },
      flex: {
        name: "Flex", sizes: [25000, 50000], profitSplit: 0.8,
        minDailyProfit: { 25000: 100, 50000: 150 },
        profitableDaysRequired: 5, minPayout: 250,
        maxPayout: { 25000: 3000, 50000: 5000 },
        maxPayoutPct: 0.5, buffer: 0, maxPayouts: null,
        payoutFrequency: null, consistencyRule: "winning_days",
      },
      pro: {
        name: "Pro", sizes: [50000, 100000, 150000], profitSplit: 0.8,
        minDailyProfit: {}, profitableDaysRequired: 0, minPayout: 1000,
        maxPayout: { 50000: 100000, 100000: 100000, 150000: 100000 },
        maxPayoutPct: 1.0,
        buffer: { 50000: 2100, 100000: 3100, 150000: 4600 },
        bufferWithdrawPct: 0.6, maxPayouts: null,
        payoutFrequency: 14, consistencyRule: "none",
      }
    }
  },
  topstep: {
    name: "Topstep", icon: "📈",
    plans: {
      express: {
        name: "Express Standard", sizes: [50000, 100000, 150000], profitSplit: 0.9,
        minDailyProfit: { default: 150 },
        profitableDaysRequired: 5, minPayout: 0.01,
        maxPayout: { default: 5000 }, maxPayoutPct: 0.5,
        buffer: 0, maxPayouts: null,
        payoutFrequency: null, consistencyRule: "profitable_since_last",
      },
      consistency: {
        name: "Express Consistency", sizes: [50000, 100000, 150000], profitSplit: 0.9,
        minDailyProfit: {},
        profitableDaysRequired: 3, minPayout: 0.01,
        maxPayout: { default: 6000 }, maxPayoutPct: 0.5,
        buffer: 0, maxPayouts: null,
        payoutFrequency: null, consistencyRule: "forty_percent",
      }
    }
  },
  alpha: {
    name: "Alpha Futures", icon: "⚡",
    plans: {
      standard: {
        name: "Standard", sizes: [50000, 100000, 150000],
        tieredSplit: { 0: 0.7, 2: 0.8, 4: 0.9 }, profitSplit: 0.7,
        minDailyProfit: {}, profitableDaysRequired: 0, minPayout: 200,
        maxPayout: { default: 15000 }, maxPayoutPct: 1.0,
        buffer: 0, maxPayouts: null,
        payoutFrequency: 14, consistencyRule: "forty_percent",
      },
      advanced: {
        name: "Advanced", sizes: [50000, 100000, 150000], profitSplit: 0.9,
        minDailyProfit: { default: 200 },
        profitableDaysRequired: 5, minPayout: 1000,
        maxPayout: { default: 15000 }, maxPayoutPct: 0.5,
        fullPayoutAfterDays: 30,
        buffer: 0, maxPayouts: null,
        payoutFrequency: null, consistencyRule: "none",
      },
      zero: {
        name: "Zero", sizes: [50000, 100000], profitSplit: 0.9,
        minDailyProfit: { default: 200 },
        profitableDaysRequired: 5, minPayout: 200,
        maxPayout: { 50000: 1500, 100000: 3000 }, maxPayoutPct: 0.5,
        fullPayoutAfterDays: 30,
        buffer: 0, maxPayouts: null,
        payoutFrequency: null, consistencyRule: "forty_percent",
      }
    }
  }
};

function computePropStatus(account, breakdowns) {
  const emptyResult = { checks: [], eligible: false, availablePayout: 0, currentBalance: 0, totalPnl: 0, winningDays: 0, firmName: "?", planName: "?", firmIcon: "?", payoutsTaken: 0, trades: [], winningDaysRemaining: null, calendarDaysRemaining: null, consistencyTarget: null, consistencyHighestDay: null };
  const firm = PROP_FIRMS[account.firm];
  if (!firm) return emptyResult;
  const plan = firm.plans[account.plan];
  if (!plan) return { ...emptyResult, firmName: firm.name, firmIcon: firm.icon };
  const size = account.accountSize;
  const trades = (breakdowns || []).filter(b => (b.propAccountIds && b.propAccountIds.includes(account.id)) || b.propAccountId === account.id);
  const payouts = account.payouts || [];

  // Aggregate by date
  const dailyMap = {};
  let totalPnl = 0;
  trades.forEach(t => { const p = safePnl(t.pnl); totalPnl += p; dailyMap[t.date] = (dailyMap[t.date] || 0) + p; });
  const totalPayoutGross = payouts.reduce((s, p) => s + (p.amount || 0), 0);
  const currentBalance = size + totalPnl - totalPayoutGross;
  const netProfit = totalPnl - totalPayoutGross;

  // PnL since last payout
  const lastPayout = payouts.length > 0 ? payouts[payouts.length - 1] : null;
  let pnlSinceLastPayout = totalPnl;
  if (lastPayout) {
    pnlSinceLastPayout = trades.filter(t => t.date > lastPayout.date || (t.date === lastPayout.date && t.id > lastPayout.id)).reduce((s, t) => s + safePnl(t.pnl), 0);
  }

  // Winning days
  const minDaily = plan.minDailyProfit[size] || plan.minDailyProfit?.default || 0;
  const winningDays = Object.entries(dailyMap).filter(([, pnl]) => pnl >= (minDaily || 0.01)).length;

  // Buffer
  const bufferRequired = typeof plan.buffer === 'object' ? (plan.buffer[size] || 0) : (plan.buffer || 0);
  const bufferMet = bufferRequired === 0 || netProfit >= bufferRequired;

  // First trade date
  const sortedDates = trades.map(t => t.date).filter(Boolean).sort();
  const firstTradeDate = sortedDates.length > 0 ? sortedDates[0] : null;
  let daysSinceFirstTrade = 0;
  if (firstTradeDate) daysSinceFirstTrade = Math.floor((Date.now() - new Date(firstTradeDate).getTime()) / 86400000);

  // 40% consistency calculation
  const dailyPnls = Object.values(dailyMap);
  const highestDay = dailyPnls.length > 0 ? Math.max(...dailyPnls) : 0;
  const consistencyTarget = highestDay > 0 ? Math.round((highestDay / 0.4) * 100) / 100 : 0;

  // Tiered profit split (Alpha Standard)
  let effectiveSplit = plan.profitSplit;
  if (plan.tieredSplit) {
    const payoutCount = payouts.length;
    const tiers = Object.entries(plan.tieredSplit).sort((a, b) => Number(b[0]) - Number(a[0]));
    for (const [threshold, split] of tiers) {
      if (payoutCount >= Number(threshold)) { effectiveSplit = split; break; }
    }
  }

  // Full payout unlock after N winning days (Alpha Advanced/Zero)
  let effectiveMaxPct = plan.maxPayoutPct || 1.0;
  if (plan.fullPayoutAfterDays && winningDays >= plan.fullPayoutAfterDays) {
    effectiveMaxPct = 1.0;
  }

  // Checks
  const checks = [];
  let eligible = true;

  if (plan.profitableDaysRequired > 0) {
    const met = winningDays >= plan.profitableDaysRequired;
    checks.push({ label: `Winning Days${minDaily > 0 ? ` ($${minDaily}+ each)` : ""}`, current: winningDays, target: plan.profitableDaysRequired, met });
    if (!met) eligible = false;
  }

  if (bufferRequired > 0) {
    const met = bufferMet;
    checks.push({ label: "Buffer Target", current: Math.max(0, Math.round(netProfit * 100) / 100), target: bufferRequired, met, isBuffer: true, isCurrency: true });
    if (!met && !(plan.bufferWithdrawPct > 0)) eligible = false;
  }

  // 40% Consistency Rule
  if (plan.consistencyRule === "forty_percent") {
    const met = totalPnl > 0 && (highestDay <= 0 || totalPnl >= consistencyTarget);
    checks.push({ label: "40% Consistency", current: Math.round(totalPnl * 100) / 100, target: consistencyTarget, met, isCurrency: true, isConsistency: true, highestDay: Math.round(highestDay * 100) / 100 });
    if (!met && totalPnl > 0) eligible = false;
  }

  if (plan.consistencyRule === "profitable_since_last" && payouts.length > 0) {
    const met = pnlSinceLastPayout > 0;
    checks.push({ label: "Net Positive Since Last Payout", current: Math.round(pnlSinceLastPayout * 100) / 100, target: 0.01, met, isCurrency: true });
    if (!met) eligible = false;
  }

  if (plan.consistencyRule === "profitable_days" && netProfit <= 0 && trades.length > 0) {
    checks.push({ label: "Net Positive Profit", current: Math.round(netProfit * 100) / 100, target: 0.01, met: false, isCurrency: true });
    eligible = false;
  }

  // Min profit goal per cycle (LucidPro)
  if (plan.minProfitGoal) {
    const goalAmount = plan.minProfitGoal[size] || 0;
    if (goalAmount > 0) {
      const met = pnlSinceLastPayout >= goalAmount;
      checks.push({ label: "Min Profit Goal", current: Math.round(pnlSinceLastPayout * 100) / 100, target: goalAmount, met, isCurrency: true });
      if (!met) eligible = false;
    }
  }

  if (typeof plan.payoutFrequency === 'number') {
    const daysSinceLast = account.lastPayoutDate ? Math.floor((Date.now() - new Date(account.lastPayoutDate).getTime()) / 86400000) : daysSinceFirstTrade;
    const met = daysSinceLast >= plan.payoutFrequency;
    checks.push({ label: `${plan.payoutFrequency}-Day Cycle`, current: daysSinceLast, target: plan.payoutFrequency, met, suffix: "days" });
    if (!met) eligible = false;
  }

  if (plan.firstPayoutAfterHours && payouts.length === 0 && firstTradeDate) {
    const hoursSince = (Date.now() - new Date(firstTradeDate).getTime()) / 3600000;
    const met = hoursSince >= plan.firstPayoutAfterHours;
    checks.push({ label: "24hrs After First Trade", current: Math.floor(hoursSince), target: plan.firstPayoutAfterHours, met, suffix: "hrs" });
    if (!met) eligible = false;
  }

  // Remaining counters for payout countdown
  const winningDaysRemaining = plan.profitableDaysRequired > 0 ? Math.max(0, plan.profitableDaysRequired - winningDays) : null;
  let calendarDaysRemaining = null;
  if (typeof plan.payoutFrequency === 'number') {
    const daysSinceLast = account.lastPayoutDate ? Math.floor((Date.now() - new Date(account.lastPayoutDate).getTime()) / 86400000) : daysSinceFirstTrade;
    calendarDaysRemaining = Math.max(0, plan.payoutFrequency - daysSinceLast);
  }

  // Available payout
  let availablePayout = 0;
  if (eligible || (plan.bufferWithdrawPct > 0 && !bufferMet && netProfit > 0)) {
    const maxCap = (plan.maxPayout && typeof plan.maxPayout === 'object') ? (plan.maxPayout[size] || plan.maxPayout.default || Infinity) : Infinity;
    // LucidPro first payout cap
    let finalMaxCap = maxCap;
    if (plan.maxPayoutFirst && payouts.length === 0) {
      finalMaxCap = plan.maxPayoutFirst[size] || maxCap;
    }
    let withdrawable = netProfit;
    if (bufferRequired > 0) {
      withdrawable = bufferMet ? netProfit - bufferRequired : (plan.bufferWithdrawPct > 0 ? netProfit * plan.bufferWithdrawPct : 0);
    }
    availablePayout = Math.min(withdrawable * effectiveMaxPct, finalMaxCap);
    availablePayout = Math.max(0, availablePayout * effectiveSplit);
    if (effectiveMaxPct <= 0.5 && !plan.fullPayoutAfterDays) {
      availablePayout = Math.min(availablePayout, currentBalance * 0.5);
    }
    if (availablePayout < (plan.minPayout || 0)) { availablePayout = 0; eligible = false; }
  }

  return {
    currentBalance: Math.round(currentBalance * 100) / 100, totalPnl: Math.round(totalPnl * 100) / 100,
    netProfit: Math.round(netProfit * 100) / 100, winningDays, totalDays: Object.keys(dailyMap).length,
    bufferRequired, bufferMet, checks, eligible: eligible && availablePayout > 0,
    availablePayout: Math.round(availablePayout * 100) / 100,
    payoutsTaken: payouts.length, maxPayouts: plan.maxPayouts,
    profitSplit: effectiveSplit, firmName: firm.name, planName: plan.name, firmIcon: firm.icon,
    trades, winningDaysRemaining, calendarDaysRemaining,
    consistencyTarget, consistencyHighestDay: Math.round(highestDay * 100) / 100,
    fullPayoutUnlocked: plan.fullPayoutAfterDays ? winningDays >= plan.fullPayoutAfterDays : false,
    fullPayoutDaysRemaining: plan.fullPayoutAfterDays ? Math.max(0, plan.fullPayoutAfterDays - winningDays) : null,
  };
}

// ─── CSS (Light/Dark Mode Support & UI) ────────────────────────────────────
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=JetBrains+Mono:wght@400;500;600;800&display=swap');
*{margin:0;padding:0;box-sizing:border-box}
:root {
  --bg0:#08090c; --bg1:#0c0d12; --bg2:#111218; --bg3:#15161e; --bg4:#1e1f2a;
  --bdr:#22232f; --bdr2:#2a2b3a; --t1:#eae8e2; --t2:#9a978d; --t3:#5e5c54;
  --gold:#c9a84c; --gold2:#ddc06a; --gold3:#a88a32;
  --gdim:rgba(201,168,76,0.10); --gglow:rgba(201,168,76,0.20); --gbdr:rgba(201,168,76,0.15);
  --grn:#22c55e; --grn-d:rgba(34,197,94,0.12);
  --red:#ef4444; --red-d:rgba(239,68,68,0.12);
  --blu:#3b82f6; --blu-d:rgba(59,130,246,0.15);
  --close-btn:#9a978d;
  --sp-1:4px; --sp-2:8px; --sp-3:12px; --sp-4:16px; --sp-5:20px; --sp-6:24px; --sp-8:32px; --sp-10:40px; --sp-12:48px;
}
.light-theme {
  --bg0:#f8fafc; --bg1:#ffffff; --bg2:#f1f5f9; --bg3:#ffffff; --bg4:#e2e8f0;
  --bdr:#e2e8f0; --bdr2:#cbd5e1; --t1:#0f172a; --t2:#475569; --t3:#94a3b8;
  --gold:#b4851c; --gold2:#d9a022; --gold3:#8c6511;
  --gdim:rgba(180,133,28,0.10); --gglow:rgba(180,133,28,0.20); --gbdr:rgba(180,133,28,0.25);
  --grn:#16a34a; --grn-d:rgba(22,163,74,0.15);
  --red:#dc2626; --red-d:rgba(220,38,38,0.15);
  --blu:#2563eb; --blu-d:rgba(37,99,235,0.15);
  --close-btn:#475569;
}
html,body,#root{height:100%;font-family:'Outfit',sans-serif;background:var(--bg1);color:var(--t1);transition:background 0.3s,color 0.3s;}
::-webkit-scrollbar{width:5px}::-webkit-scrollbar-track{background:var(--bg2)}
::-webkit-scrollbar-thumb{background:var(--bdr2);border-radius:3px}
::-webkit-scrollbar-thumb:hover{background:var(--t3)}
input,textarea,select{font-family:'Outfit',sans-serif;transition:border-color 0.2s, box-shadow 0.2s}
input:focus,textarea:focus,select:focus{border-color:var(--gold) !important;box-shadow:0 0 0 3px var(--gdim) !important}
button:focus-visible,a:focus-visible,input:focus-visible,select:focus-visible,textarea:focus-visible{outline:2px solid var(--gold);outline-offset:2px}
@keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
@keyframes slideIn{from{opacity:0;transform:translateX(-12px)}to{opacity:1;transform:translateX(0)}}
@keyframes slideUp{from{opacity:0;transform:translateY(16px) scale(0.99)}to{opacity:1;transform:translateY(0) scale(1)}}
@keyframes staggerFadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
@keyframes toastIn{from{opacity:0;transform:translateX(100%)}to{opacity:1;transform:translateX(0)}}
@keyframes toastOut{from{opacity:1;transform:translateX(0)}to{opacity:0;transform:translateX(100%)}}
@keyframes toastProgress{from{width:100%}to{width:0%}}
@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.5}}
@keyframes shimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}
@keyframes gradientShift{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}
@keyframes spin{to{transform:rotate(360deg)}}
.nt-card-hover{transition:transform 0.2s, border-color 0.2s, box-shadow 0.2s}
.nt-card-hover:hover{transform:translateY(-2px);box-shadow:0 8px 25px rgba(0,0,0,0.15);border-color:var(--bdr2) !important}
.nt-btn-hover{transition:transform 0.15s, box-shadow 0.15s, opacity 0.15s}
.nt-btn-hover:hover{transform:translateY(-1px);box-shadow:0 4px 12px rgba(0,0,0,0.2)}
.nt-btn-hover:active{transform:translateY(0);box-shadow:none}
.nt-page-enter{animation:slideUp 0.35s ease-out}
video::-webkit-media-controls-fullscreen-button{display:none !important}
video::-webkit-media-controls-picture-in-picture-button{display:none !important}
`;

// ─── SHARED UI ─────────────────────────────────────────────────────────────
const inp = {width:"100%",padding:"var(--sp-2) var(--sp-3)",background:"var(--bg0)",border:"1px solid var(--bdr)",borderRadius:9,color:"var(--t1)",fontSize:13,outline:"none",transition:"border-color 0.2s"};
const lbl = {display:"block",fontSize:10,fontWeight:600,color:"var(--t3)",marginBottom:"var(--sp-1)",letterSpacing:"0.06em",textTransform:"uppercase"};
const btnG = {padding:"var(--sp-2) var(--sp-5)",background:"linear-gradient(135deg,var(--gold2),var(--gold3))",border:"none",borderRadius:"var(--sp-2)",color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'Outfit',sans-serif",boxShadow:"0 2px 12px var(--gglow)"};
const btnO = {padding:"var(--sp-2) var(--sp-4)",background:"var(--bg2)",border:"1px solid var(--bdr)",borderRadius:"var(--sp-2)",color:"var(--t2)",fontSize:12,fontWeight:500,cursor:"pointer",fontFamily:"'Outfit',sans-serif"};
const btnD = {padding:"var(--sp-1) var(--sp-3)",background:"var(--red-d)",border:"1px solid rgba(239,68,68,0.2)",borderRadius:6,color:"var(--red)",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"'Outfit',sans-serif"};
const card = {background:"var(--bg3)",border:"1px solid var(--bdr)",borderRadius:"var(--sp-3)",padding:"var(--sp-5)"};
const mOverlay = {position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",backdropFilter:"blur(6px)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:999,padding:"var(--sp-5)"};
const mBox = {background:"var(--bg3)",border:"1px solid var(--bdr)",borderRadius:"var(--sp-4)",padding:"var(--sp-6)",width:"100%",maxWidth:560,maxHeight:"85vh",overflowY:"auto",boxShadow:"0 24px 80px rgba(0,0,0,0.5)",color:"var(--t1)"};

function NTLogo({size=40,glow=false}){
  return(<div style={{width:size,height:size,borderRadius:size*0.22,background:"linear-gradient(145deg,var(--gold2),var(--gold3),var(--gold),var(--gold2))",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Outfit'",fontSize:size*0.48,fontWeight:900,color:"#fff",flexShrink:0,boxShadow:glow?"0 0 24px var(--gglow)":"0 2px 8px rgba(0,0,0,0.3)",position:"relative",overflow:"hidden"}}><span style={{position:"relative",zIndex:1,textShadow:"0 2px 4px rgba(0,0,0,0.2)"}}>NT</span></div>);
}
function Avatar({ src, name, size = 40, borderRadius = 10, style = {} }) {
  const [failed, setFailed] = useState(false);
  if (src && !failed) {
    return <img src={src} alt={name || "?"} onError={() => setFailed(true)} style={{ width: size, height: size, borderRadius, objectFit: "cover", flexShrink: 0, ...style }} />;
  }
  return (
    <div style={{ width: size, height: size, borderRadius, background: "var(--bg4)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.4, fontWeight: 700, color: "var(--t1)", flexShrink: 0, ...style }}>
      {name ? name[0] : "?"}
    </div>
  );
}

function Empty({ icon, title, sub, action, onAction, variant }) {
  const illustrations = {
    videos: (<svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="1.5" strokeLinecap="round" style={{ opacity: 0.5, marginBottom: 12 }}><rect x="2" y="4" width="20" height="16" rx="2" /><polygon points="10 8 16 12 10 16 10 8" /></svg>),
    resources: (<svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="1.5" style={{ opacity: 0.5, marginBottom: 12 }}><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" /><path d="M8 7h8" /><path d="M8 11h8" /></svg>),
    trades: (<svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="1.5" style={{ opacity: 0.5, marginBottom: 12 }}><path d="M3 3v18h18" /><path d="M18 9l-5 5-4-4-3 3" /></svg>),
    search: (<svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="1.5" style={{ opacity: 0.5, marginBottom: 12 }}><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>),
  };
  const pic = variant && illustrations[variant] ? illustrations[variant] : <div style={{ fontSize: 44, marginBottom: 10, opacity: 0.6 }}>{icon}</div>;
  return (<div style={{ textAlign: "center", padding: "50px 20px", animation: "fadeIn 0.4s" }}>{pic}<div style={{ fontSize: 15, fontWeight: 600, color: "var(--t1)", marginBottom: 5 }}>{title}</div><div style={{ fontSize: 12, color: "var(--t3)", marginBottom: 18, maxWidth: 320, margin: "0 auto 18px" }}>{sub}</div>{action && <button onClick={onAction} style={btnG}>{action}</button>}</div>);
}
function Confirm({msg,onY,onN}){
  return(<div style={mOverlay} onClick={onN}><div style={{...mBox,maxWidth:360,textAlign:"center"}} onClick={e=>e.stopPropagation()}><div style={{fontSize:28,marginBottom:10}}>⚠️</div><p style={{fontSize:13,color:"var(--t1)",marginBottom:20}}>{msg}</p><div style={{display:"flex",gap:10,justifyContent:"center"}}><button onClick={onN} style={btnO}>Cancel</button><button onClick={onY} style={btnD}>Delete</button></div></div></div>);
}

// ─── TOAST NOTIFICATION SYSTEM ────────────────────────────────────────────
const ToastContext = React.createContext(null);
function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timerRefs = useRef(new Map());
  useEffect(() => {
    const timers = timerRefs.current;
    return () => { timers.forEach(t => clearTimeout(t)); timers.clear(); };
  }, []);
  const addToast = useCallback((msg, type = "success") => {
    const id = Date.now() + Math.random();
    setToasts(t => [...t, { id, msg, type }]);
    const timer = setTimeout(() => { setToasts(t => t.filter(x => x.id !== id)); timerRefs.current.delete(id); }, 3500);
    timerRefs.current.set(id, timer);
  }, []);
  return (
    <ToastContext.Provider value={addToast}>
      {children}
      <div style={{ position: "fixed", top: 20, right: 20, zIndex: 99999, display: "flex", flexDirection: "column", gap: 8, pointerEvents: "none" }} role="status" aria-live="polite">
        {toasts.map(t => (
          <div key={t.id} style={{
            animation: "toastIn 0.3s ease-out",
            padding: "12px 20px", borderRadius: 10, fontSize: 13, fontWeight: 600, fontFamily: "'Outfit',sans-serif",
            background: t.type === "error" ? "var(--red)" : t.type === "warn" ? "var(--gold)" : "var(--grn)",
            color: "#fff", boxShadow: "0 8px 30px rgba(0,0,0,0.3)", pointerEvents: "auto",
            display: "flex", alignItems: "center", gap: 8, maxWidth: 360, position: "relative", overflow: "hidden"
          }}>
            <span>{t.type === "error" ? "!" : t.type === "warn" ? "!" : "\u2713"}</span>
            {t.msg}
            <div style={{ position: "absolute", bottom: 0, left: 0, height: 3, background: "rgba(255,255,255,0.4)", animation: "toastProgress 3.5s linear forwards", transformOrigin: "left" }} />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
function useToast() { return React.useContext(ToastContext); }

// ─── SPINNER COMPONENT ──────────────────────────────────────────────────────
function Spinner({ size = 14, color = "#fff" }) {
  return <div style={{ width: size, height: size, border: `2px solid ${color}33`, borderTop: `2px solid ${color}`, borderRadius: "50%", animation: "spin 0.6s linear infinite", flexShrink: 0 }} />;
}

// ─── LOADING BUTTON ─────────────────────────────────────────────────────────
function LoadingButton({ children, loading, disabled, style = {}, ...props }) {
  return (
    <button disabled={disabled || loading} style={{ ...style, opacity: (disabled || loading) ? 0.7 : 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }} {...props}>
      {loading ? <><Spinner size={14} color={typeof style.color === 'string' ? style.color : '#fff'} /> Saving...</> : children}
    </button>
  );
}

// ─── MODAL FOCUS TRAP (ARIA + KEYBOARD) ─────────────────────────────────────
function useModalFocusTrap(containerRef, isOpen, onClose) {
  React.useEffect(() => {
    if (!isOpen || !containerRef?.current) return;
    const el = containerRef.current;
    const focusables = el.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    const first = focusables[0];
    if (first) first.focus();
    const handleKey = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key !== 'Tab') return;
      const list = [...el.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')];
      const idx = list.indexOf(document.activeElement);
      if (idx === -1) { if (list.length) list[e.shiftKey ? list.length - 1 : 0].focus(); e.preventDefault(); return; }
      const next = e.shiftKey ? (idx <= 0 ? list.length - 1 : idx - 1) : (idx >= list.length - 1 ? 0 : idx + 1);
      list[next].focus();
      e.preventDefault();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [containerRef, isOpen, onClose]);
}

// ─── SKELETON LOADERS ──────────────────────────────────────────────────────
function SkeletonBlock({ width, height, style = {} }) {
  return <div style={{ width: width || '100%', height: height || 20, background: 'linear-gradient(90deg, var(--bg4) 0%, var(--bdr2) 50%, var(--bg4) 100%)', backgroundSize: '200% 100%', animation: 'shimmer 1.5s ease-in-out infinite', borderRadius: 8, ...style }} />;
}
function LoadingSkeleton() {
  return (
    <div style={{ minHeight: '100vh', padding: 24, animation: 'fadeIn 0.3s' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, marginBottom: 24 }}>
        {[1, 2, 3, 4].map(i => <SkeletonBlock key={i} height={80} />)}
      </div>
      <SkeletonBlock height={24} width="40%" style={{ marginBottom: 12 }} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
        {[1, 2, 3, 4, 5, 6].map(i => <SkeletonBlock key={i} height={120} />)}
      </div>
    </div>
  );
}

// ─── DENSE RANDOM POSITION WATERMARK ───────────────────────────────────────
function RandomWatermark({ text }) {
  const [positions, setPositions] = useState([]);
  const genPositions = useCallback(() => {
    // Generate 3 watermarks for screen-record protection
    return Array.from({ length: 3 }, () => ({
      top: 10 + Math.random() * 70,
      left: 10 + Math.random() * 60,
      rot: -20 + Math.random() * 40,
      key: Math.random()
    }));
  }, []);

  useEffect(() => {
    setPositions(genPositions());
    const interval = setInterval(() => setPositions(genPositions()), 4000 + Math.random() * 3000);
    return () => clearInterval(interval);
  }, [genPositions]);

  return (
    <>
      {positions.map((p) => (
        <div key={p.key} data-nt="true" style={{
          position: "absolute", top: `${p.top}%`, left: `${p.left}%`, zIndex: 9999,
          color: "rgba(255,255,255,0.25)", fontSize: 13, fontWeight: 800,
          fontFamily: "'JetBrains Mono',monospace", pointerEvents: "none", userSelect: "none", 
          whiteSpace: "pre-wrap", textAlign: "center", lineHeight: 1.4,
          textShadow: "0 1px 4px rgba(0,0,0,0.9)", transform: `rotate(${p.rot}deg)`,
          transition: "top 0.8s ease, left 0.8s ease", letterSpacing: "0.02em"
        }}>
          {text}
        </div>
      ))}
    </>
  );
}

// ─── SIGNED VIDEO URL HELPER ────────────────────────────────────────────────
async function getSignedVideoUrl(rawUrl) {
  if (!rawUrl || rawUrl.startsWith("data:") || rawUrl.startsWith("blob:")) return rawUrl;
  try {
    const urlObj = new URL(rawUrl);
    if (!urlObj.pathname.startsWith("/uploads/")) return rawUrl;
    const filename = urlObj.pathname.split("/uploads/")[1];
    if (!filename) return rawUrl;
    const res = await fetch("https://nexustrades.net/sign-url.php", {
      method: "POST",
      headers: { ...DB._authHeaders(), "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ file: filename })
    });
    if (!res.ok) return rawUrl;
    const data = await res.json();
    return data.url || rawUrl;
  } catch { return rawUrl; }
}

// ─── PROTECTED VIDEO PLAYER (ANTI-PIRACY & ANTI-SKIP) ──────────────────────
const VIDEO_SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

function ProtectedVideoPlayer({ src, embedUrl, embedType, userEmail, userIP, userName, userDiscordId, onSecurityBreach, onVideoComplete, isFirstWatch = true }) {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const maxTimeRef = useRef(0);
  const [isFS, setIsFS] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const [isObscured, setIsObscured] = useState(false);
  const [nuked, setNuked] = useState(false);
  const [speed, setSpeed] = useState(() => {
    try { const v = localStorage.getItem("nt_video_speed"); return v ? parseFloat(v) : 1; } catch { return 1; }
  });

  const ipDisplay = (!userIP || userIP === "ALL_BLOCKED" || userIP === "Blocked") ? "—" : userIP;
  const wmText = `${userName}\nID: ${userDiscordId || "Unknown"}\nIP: ${ipDisplay}`;

  useEffect(() => {
    if (!videoRef.current) return;
    videoRef.current.playbackRate = speed;
    try { localStorage.setItem("nt_video_speed", String(speed)); } catch (e) { _log("localStorage write failed:", e.name); }
  }, [speed]);

  useEffect(() => {
    let currentBlobUrl = null;
    let cancelled = false;
    (async () => {
      if (src && src.startsWith("data:video")) {
        const blob = await fetch(src).then(r => r.blob());
        if (cancelled) return;
        currentBlobUrl = URL.createObjectURL(blob);
        setBlobUrl(currentBlobUrl);
      } else if (src) {
        const signed = await getSignedVideoUrl(src);
        if (cancelled) return;
        setBlobUrl(signed);
      }
    })();
    return () => { 
      cancelled = true;
      if (currentBlobUrl && currentBlobUrl.startsWith("blob:")) URL.revokeObjectURL(currentBlobUrl);
    };
  }, [src]);

  // SMART BLACKOUT & SECURITY TRAPS
  useEffect(() => {
    const handleContext = e => e.preventDefault();
    const handleVisibility = () => { if (document.hidden) setIsObscured(true); };

    const handleKeyDown = (e) => {
      // Screen record / Snip block (PrintScreen, Win+Shift+S, Cmd+Shift)
      if (
        e.key === "PrintScreen" ||
        (e.metaKey && e.shiftKey) ||
        (e.ctrlKey && e.shiftKey && e.key === "S")
      ) {
        e.preventDefault();
        setIsObscured(true);
        if (onSecurityBreach) onSecurityBreach("Attempted screenshot via PrintScreen / Snipping Tool.");
        setTimeout(() => setIsObscured(false), 3000);
      }

      // DEVTOOLS & DOWNLOAD TRAPS (F12, Ctrl+Shift+I, Ctrl+S, Ctrl+U)
      if (
        e.key === "F12" ||
        (e.ctrlKey && e.shiftKey && (e.key === "I" || e.key === "J" || e.key === "C")) ||
        (e.ctrlKey && (e.key === "U" || e.key === "u")) ||
        (e.ctrlKey && !e.shiftKey && (e.key === "S" || e.key === "s")) ||
        (e.metaKey && (e.key === "S" || e.key === "s")) ||
        (e.metaKey && e.altKey && e.key === "I")
      ) {
        e.preventDefault();
        setNuked(true); // Trigger Lockdown UI
        if (onSecurityBreach) onSecurityBreach("Attempted to inspect page code or download via shortcut.");
      }
    };

    // Detect window focus loss (alt-tab while recording) - only obscure briefly
    const handleBlur = () => {
      // Short delay to avoid false triggers from iframe clicks
      setTimeout(() => {
        if (!document.hasFocus()) {
          setIsObscured(true);
          if (onSecurityBreach) onSecurityBreach("Window focus lost during video playback (possible screen capture).");
        }
      }, 200);
    };
    const handleFocus = () => setIsObscured(false);
    const handleWindowClick = () => setIsObscured(false);

    document.addEventListener("contextmenu", handleContext);
    window.addEventListener("blur", handleBlur);
    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("click", handleWindowClick);

    const fsHandler = () => {
      const fsEl = document.fullscreenElement;
      // If the video element itself went fullscreen (not the container), exit and re-enter with container
      if (fsEl && fsEl === videoRef.current && containerRef.current) {
        document.exitFullscreen().then(() => {
          containerRef.current.requestFullscreen().catch(() => {});
        }).catch(() => {});
        return;
      }
      setIsFS(!!fsEl);
    };
    document.addEventListener("fullscreenchange", fsHandler);

    // DOM INJECTION SNIFFER + ATTRIBUTE TAMPER GUARD
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        // Detect injected elements (download buttons from extensions)
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1 && !node.getAttribute("data-nt")) {
            if (node.tagName === "BUTTON" || node.tagName === "A" || node.tagName === "DIV") {
              node.remove();
              if (onSecurityBreach) onSecurityBreach(`Blocked browser extension injection (${node.tagName})`);
            }
          }
        });
        // Detect attribute tampering on the video element (e.g. removing controlsList)
        if (mutation.type === "attributes" && mutation.target.tagName === "VIDEO") {
          const vid = mutation.target;
          if (vid.getAttribute("controlsList") !== "nodownload noremoteplayback nofullscreen") {
            vid.setAttribute("controlsList", "nodownload noremoteplayback nofullscreen");
            if (onSecurityBreach) onSecurityBreach("Video controlsList attribute was tampered with");
          }
          if (!vid.disablePictureInPicture) {
            vid.disablePictureInPicture = true;
          }
        }
      });
    });

    if (containerRef.current) {
      observer.observe(containerRef.current, { childList: true, subtree: true, attributes: true, attributeFilter: ["controlsList", "controls", "src"] });
    }
    
    return () => {
      document.removeEventListener("contextmenu", handleContext);
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("click", handleWindowClick);
      document.removeEventListener("fullscreenchange", fsHandler);
      observer.disconnect();
    };
  }, [onSecurityBreach]);

  // --- ANTI-SKIP LOGIC FOR NATIVE VIDEOS (only enforced on first watch) ---
  const lastTimeRef = useRef(0); // Tracks the previous timeupdate value to detect jumps

  const handleTimeUpdate = () => {
    if (!videoRef.current || !isFirstWatch) return;
    const ct = videoRef.current.currentTime;
    // Only advance maxTime if this is a natural progression (< 1.5 sec since last update)
    // This prevents a seek from inflating maxTime via a timeupdate that fires after the jump
    if (ct <= maxTimeRef.current + 1.5) {
      maxTimeRef.current = Math.max(maxTimeRef.current, ct);
    }
    lastTimeRef.current = ct;
  };

  const handleSeeking = () => {
    if (!videoRef.current || !isFirstWatch) return;
    // Snap back if they try to skip ahead past their furthest watched point
    if (videoRef.current.currentTime > maxTimeRef.current + 0.5) {
      videoRef.current.currentTime = maxTimeRef.current;
    }
  };

  let finalEmbedUrl = safeURL(embedUrl) || "";
  const isEmbed = !src && finalEmbedUrl;
  if (isEmbed) {
    try {
      const urlObj = new URL(finalEmbedUrl);
      if (urlObj.hostname === 'www.youtube.com' || urlObj.hostname === 'youtube.com') {
        const vid = urlObj.searchParams.get('v');
        if (vid && /^[\w-]{11}$/.test(vid)) finalEmbedUrl = `https://www.youtube.com/embed/${vid}`;
      } else if (urlObj.hostname === 'youtu.be') {
        const vid = urlObj.pathname.slice(1);
        if (vid && /^[\w-]{11}$/.test(vid)) finalEmbedUrl = `https://www.youtube.com/embed/${vid}`;
      }
    } catch { /* not a YouTube URL, use as-is */ }
  }

  // If a security trap is triggered, display the red nuke screen
  if (nuked) {
    return (
      <div style={{ width: "100%", aspectRatio: "16/9", background: "var(--red)", color: "#fff", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", borderRadius: "12px 12px 0 0" }}>
         <span style={{ fontSize: 48, marginBottom: 16 }}>🚨</span>
         <h2 style={{ fontSize: 20, fontWeight: 800, textTransform: "uppercase" }}>Security Lockdown</h2>
         <p style={{ fontSize: 13, marginTop: 8 }}>Unauthorized action detected. This incident has been logged.</p>
         <p style={{ fontSize: 11, marginTop: 4, opacity: 0.8 }}>Please refresh the page to restore access.</p>
      </div>
    );
  }

  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      containerRef.current.requestFullscreen().catch(() => {});
    }
  };

  return (
    <div ref={containerRef} data-nt="true" style={{ position: "relative", width: "100%", background: "#000", overflow: "hidden", borderRadius: isFS ? 0 : "12px 12px 0 0", ...(isEmbed || blobUrl ? { aspectRatio: "16/9" } : {}), ...(isFS ? { position: "fixed", inset: 0, zIndex: 999999, height: "100vh", width: "100vw" } : {}) }} onContextMenu={e => e.preventDefault()}>
      {isObscured && (
        <div data-nt="true" style={{ position: "absolute", inset: 0, zIndex: 99999, background: "#000", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", color: "var(--red)" }}>
          <span style={{fontSize: 40, marginBottom: 10}}>📷</span>
          <div style={{fontSize:14, fontWeight: 700}}>Recording / Screenshots Disabled</div>
          <div style={{fontSize:11, color: "var(--t3)", marginTop: 8}}>Click window to resume playback.</div>
        </div>
      )}

      {isEmbed ? (
        <iframe data-nt="true" title={`Video Player - ${finalEmbedUrl}`} src={finalEmbedUrl} style={{ width: "100%", height: "100%", border: "none", pointerEvents: "auto" }} allow="accelerometer;autoplay;clipboard-write;encrypted-media;gyroscope;picture-in-picture" />
      ) : blobUrl ? (
        <video
          data-nt="true"
          ref={videoRef}
          src={blobUrl}
          onTimeUpdate={handleTimeUpdate}
          onSeeking={handleSeeking}
          onEnded={() => { if(onVideoComplete) onVideoComplete(); }}
          onDoubleClick={(e) => { e.preventDefault(); toggleFullscreen(); }}
          style={{ width: "100%", height: "100%", display: "block", objectFit: "contain" }}
          controls
          controlsList="nodownload noremoteplayback nofullscreen"
          disablePictureInPicture
          playsInline
        />
      ) : null}

      <RandomWatermark text={wmText} />

      {/* Speed control (native video only) + Fullscreen */}
      <div data-nt="true" style={{ position: "absolute", bottom: isEmbed ? 10 : 50, right: 10, zIndex: 10000, display: "flex", alignItems: "center", gap: 6 }}>
        {blobUrl && (
          <div data-nt="true" style={{ display: "flex", alignItems: "center", gap: 4, background: "rgba(0,0,0,0.7)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 6, padding: "4px 6px", backdropFilter: "blur(4px)" }}>
            <span style={{ fontSize: 10, color: "rgba(255,255,255,0.8)", marginRight: 4 }}>Speed</span>
            {VIDEO_SPEEDS.map((s) => (
              <button key={s} data-nt="true" onClick={() => setSpeed(s)} style={{
                background: speed === s ? "rgba(255,255,255,0.25)" : "transparent", border: "none", borderRadius: 4,
                color: "#fff", padding: "4px 8px", cursor: "pointer", fontSize: 11, fontWeight: speed === s ? 700 : 400
              }} title={`${s}x`}>{s}x</button>
            ))}
          </div>
        )}
        <button data-nt="true" onClick={toggleFullscreen} style={{
          background: "rgba(0,0,0,0.7)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 6,
          color: "#fff", padding: "6px 10px", cursor: "pointer", fontSize: 14, lineHeight: 1,
          display: "flex", alignItems: "center", gap: 4, backdropFilter: "blur(4px)"
        }} title={isFS ? "Exit Fullscreen" : "Fullscreen"}>
          {isFS ? "⛶" : "⛶"}
        </button>
      </div>

      {isEmbed && (
        <div data-nt="true" style={{ position: "absolute", inset: 0, zIndex: 9990, background: "transparent", cursor: "pointer" }}
          onClick={() => { const el = document.querySelector('[data-shield]'); if (el) { el.style.display = 'none'; setTimeout(() => { if (el) el.style.display = 'block'; }, 3000); } }} data-shield="true" />
      )}
    </div>
  );
}

// ─── LOGIN (OAUTH VIA DISCORD) ─────────────────────────────────────────────
function LoginPage({ onLogin }) {
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const DISCORD_CLIENT_ID = "1478254952693629070"; 
  const REDIRECT_URI = encodeURIComponent("https://nexustrades.net");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const err = params.get('error');
    const returnedState = params.get('state');

    if (err) {
      setError("Login was cancelled or failed.");
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    if (code) {
      const savedState = sessionStorage.getItem("oauth_state");
      sessionStorage.removeItem("oauth_state");
      if (!savedState || savedState !== returnedState) {
        setError("Security check failed (state mismatch). Please try logging in again.");
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      }

      setLoading(true);
      fetch("https://nexustrades.net/discord.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ code })
      })
      .then(res => {
        if (!res.ok) return res.json().then(d => { throw new Error(d.error || "Authentication failed"); });
        return res.json();
      })
      .then(data => {
        if (data.token && data.id) {
          if (data.csrfToken) DB.setCsrf(data.csrfToken);
          // IP for watermark/logs comes from getIP() (ip.php) only, not from login response.
          onLogin({
            discordId: data.id,
            username: data.username,
            name: data.name || data.username,
            role: data.role,
            email: data.email || "",
            avatar: data.avatar || "",
          }, data.token);
          window.history.replaceState({}, document.title, window.location.pathname);
        } else {
          setError("Failed to authenticate with Discord.");
          setLoading(false);
        }
      })
      .catch((e) => {
        _log("Login error:", e.name);
        setError(e.message || "Server error connecting to Discord auth.");
        setLoading(false);
      });
    }
  }, [onLogin]);

  const handleDiscordLogin = () => {
    const state = crypto.randomUUID();
    sessionStorage.setItem("oauth_state", state);
    window.location.href = `https://discord.com/api/oauth2/authorize?client_id=${DISCORD_CLIENT_ID}&redirect_uri=${REDIRECT_URI}&response_type=code&scope=identify%20email&state=${state}`;
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: `radial-gradient(ellipse 70% 50% at 50% 0%,var(--gdim),transparent 60%),var(--bg0)`, position: "relative", overflow: "hidden" }}>
      {/* Animated background orbs */}
      <div style={{ position: "absolute", width: 300, height: 300, borderRadius: "50%", background: "radial-gradient(circle, rgba(201,168,76,0.08), transparent 70%)", top: "-10%", left: "10%", animation: "gradientShift 8s ease infinite", backgroundSize: "200% 200%" }} />
      <div style={{ position: "absolute", width: 200, height: 200, borderRadius: "50%", background: "radial-gradient(circle, rgba(59,130,246,0.06), transparent 70%)", bottom: "10%", right: "15%", animation: "gradientShift 12s ease infinite", backgroundSize: "200% 200%" }} />

      <div style={{ position: "relative", zIndex: 1, width: "100%", maxWidth: 400, padding: "0 20px", animation: "fadeIn 0.6s" }}>
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <div style={{ display: "inline-block", marginBottom: 14 }}><NTLogo size={56} glow /></div>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: "var(--t1)", marginBottom: 4, letterSpacing: "-0.02em" }}>Nexus Trades</h1>
          <p style={{ color: "var(--t3)", fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase" }}>Mentorship Portal</p>
        </div>
        <div style={{ ...card, borderRadius: 16, padding: "30px 22px", textAlign: "center", boxShadow: "0 24px 80px rgba(0,0,0,0.2)", backdropFilter: "blur(10px)" }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Members Only</h2>
          <p style={{ color: "var(--t3)", fontSize: 12, marginBottom: 26 }}>Authenticate via Discord to verify your access.</p>

          {error && <div style={{ padding: "10px 14px", background: "var(--red-d)", borderRadius: 8, color: "var(--red)", fontSize: 12, marginBottom: 20, border: "1px solid rgba(239,68,68,0.2)", animation: "fadeIn 0.3s" }}>{error}</div>}

          <button onClick={handleDiscordLogin} disabled={loading} className="nt-btn-hover" style={{ ...btnG, width: "100%", padding: "14px 0", fontSize: 14, background: loading ? "#4752C4" : "#5865F2", boxShadow: "0 4px 14px rgba(88, 101, 242, 0.3)", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, opacity: loading ? 0.8 : 1 }}>
            {loading ? <><Spinner size={16} /> Verifying...</> : <><svg width="20" height="15" viewBox="0 0 71 55" fill="none"><path d="M60.1 4.9A58.5 58.5 0 0045.4.2a.2.2 0 00-.2.1 40.7 40.7 0 00-1.8 3.7c-5.5-.8-11-.8-16.3 0A37.3 37.3 0 0025.3.3a.2.2 0 00-.2-.1A58.4 58.4 0 0010.4 4.9a.2.2 0 00-.1.1A60 60 0 00.2 45.3a.2.2 0 00.1.2 58.8 58.8 0 0017.7 9 .2.2 0 00.3-.1c1.4-1.9 2.6-3.9 3.6-6a.2.2 0 00-.1-.3 38.8 38.8 0 01-5.5-2.6.2.2 0 01-.1-.4l1.1-.9a.2.2 0 01.2 0 42 42 0 0035.8 0 .2.2 0 01.2 0l1.1.9a.2.2 0 01-.1.4c-1.8 1-3.6 1.9-5.5 2.6a.2.2 0 00-.1.3 53.7 53.7 0 003.6 6 .2.2 0 00.3.1 58.6 58.6 0 0017.7-9 .2.2 0 00.1-.2c1.5-15.5-2.5-29-10.6-40.3a.2.2 0 00-.1-.1zM23.7 37.1c-3.5 0-6.4-3.2-6.4-7.1s2.8-7.1 6.4-7.1 6.5 3.2 6.4 7.1c0 3.9-2.8 7.1-6.4 7.1zm23.6 0c-3.5 0-6.4-3.2-6.4-7.1s2.8-7.1 6.4-7.1 6.5 3.2 6.4 7.1c0 3.9-2.9 7.1-6.4 7.1z" fill="white"/></svg> Log in with Discord</>}
          </button>
        </div>
        <p style={{ textAlign: "center", fontSize: 10, color: "var(--t3)", marginTop: 20, opacity: 0.6 }}>PC + Google Chrome required</p>
      </div>
    </div>
  );
}

// ─── DASHBOARD ─────────────────────────────────────────────────────────────
function Dashboard({ onNav, D, isAdmin, announcements, setAnnouncements, user, students = [], actLog = [], homework = [] }) {
  const [showAnnounce, setShowAnnounce] = useState(false);
  const [newAnnounce, setNewAnnounce] = useState("");
  const [postingAnnounce, setPostingAnnounce] = useState(false);

  const myTrades = D.breakdowns.filter(b => {
    const ownerId = b.userId || LEGACY_ADMIN_USER;
    if (ownerId === user.discordId) return true;
    if (ownerId === user.username || ownerId === user.name) return true;
    if (isAdmin && ownerId === LEGACY_ADMIN_USER) return true;
    return false;
  });
  const w = myTrades.filter(b => safePnl(b.pnl) > 0).length;
  const l = myTrades.filter(b => safePnl(b.pnl) <= 0).length;
  const wr = myTrades.length > 0 ? Math.round((w / (w + l)) * 100) : 0;

  const loginsByDay = React.useMemo(() => {
    const now = Date.now();
    const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000;
    const dayCounts = {};
    for (let i = 0; i < 7; i++) {
      const d = new Date(now);
      d.setDate(d.getDate() - (6 - i));
      dayCounts[d.toDateString()] = 0;
    }
    (actLog || []).filter(e => e.action === "login" && new Date(e.timestamp).getTime() >= sevenDaysAgo).forEach(e => {
      const day = new Date(e.timestamp).toDateString();
      if (dayCounts[day] !== undefined) dayCounts[day]++;
    });
    return Object.keys(dayCounts).sort().map(day => ({ day: new Date(day).toLocaleDateString("en-US", { weekday: "short" }), count: dayCounts[day] }));
  }, [actLog]);

  const maxLogins = Math.max(1, ...loginsByDay.map(d => d.count));
  const pendingHomework = homework.filter(h => h.status === "pending").length;
  const securityAlerts = (actLog || []).filter(e => e.action === "security_breach").length;
  const activeThisWeek = React.useMemo(() => {
    const cut = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return new Set((actLog || []).filter(e => e.action === "login" && new Date(e.timestamp).getTime() >= cut).map(e => e.user)).size;
  }, [actLog]);

  const stats = [
    { label: "Videos", value: D.videos.length, color: "var(--gold)" },
    { label: "My Trades", value: myTrades.length, color: "var(--blu)" },
    { label: "My Win Rate", value: myTrades.length > 0 ? wr + "%" : "—", color: "var(--grn)" },
  ];
  const adminStats = isAdmin ? [
    { label: "Total Students", value: students.length, color: "var(--blu)" },
    { label: "Active This Week", value: activeThisWeek, color: "var(--grn)" },
    { label: "Pending Homework", value: pendingHomework, color: "var(--gold)" },
    { label: "Security Alerts", value: securityAlerts, color: securityAlerts > 0 ? "var(--red)" : "var(--t3)" },
  ] : [];

  const handlePostAnnounce = async () => {
    if (!newAnnounce) return;
    setPostingAnnounce(true);
    try {
      const latest = await DB.get(SK.announcements) || [];
      const u = [{ id: secureId(), text: newAnnounce, date: new Date().toLocaleDateString() }, ...latest];
      setAnnouncements(u); await DB.set(SK.announcements, u);
      setNewAnnounce(""); setShowAnnounce(false);
    } finally { setPostingAnnounce(false); }
  };

  const handleDeleteAnnounce = async (id) => {
    const latest = await DB.get(SK.announcements) || [];
    const u = latest.filter(a => a.id !== id);
    setAnnouncements(u); await DB.set(SK.announcements, u);
  };

  return (
    <div style={{ animation: "fadeIn 0.4s" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 26 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800 }}>Dashboard</h1>
          <p style={{ color: "var(--t2)", fontSize: 13, marginTop: 4 }}>Welcome back to Nexus Trades.</p>
        </div>
        {isAdmin && <button onClick={() => setShowAnnounce(true)} style={btnO}>+ Announcement</button>}
      </div>

      {announcements?.length > 0 && (
        <div style={{ ...card, marginBottom: 22, borderLeft: "4px solid var(--gold)", padding: "16px 20px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "var(--gold)", textTransform: "uppercase", marginBottom: 8, letterSpacing: "0.05em" }}>📢 Latest Announcement</div>
            {isAdmin && (
              <button onClick={() => handleDeleteAnnounce(announcements[0].id)} style={{ background: "none", border: "none", color: "var(--close-btn)", cursor: "pointer", fontSize: 16, fontWeight: 800, opacity: 0.7, transition: "color 0.15s" }} title="Delete Announcement">✕</button>
            )}
          </div>
          <p style={{ fontSize: 13, lineHeight: 1.6, color: "var(--t1)" }}>{announcements[0].text}</p>
          <div style={{ fontSize: 10, color: "var(--t3)", marginTop: 8 }}>{announcements[0].date}</div>
        </div>
      )}

      {showAnnounce && (
        <div style={mOverlay} onClick={() => setShowAnnounce(false)}><div style={mBox} onClick={e => e.stopPropagation()}>
          <h2 style={{ fontSize: 17, fontWeight: 700, marginBottom: 12 }}>Post Announcement</h2>
          <textarea style={{ ...inp, minHeight: 100, resize: "vertical" }} value={newAnnounce} onChange={e => setNewAnnounce(e.target.value)} placeholder="Type announcement here..." maxLength={500} />
          <div style={{ fontSize: 11, color: newAnnounce.length >= 480 ? "var(--red)" : "var(--t3)", marginTop: 4 }}>{newAnnounce.length} / 500</div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}><button onClick={() => setShowAnnounce(false)} style={btnO}>Cancel</button><LoadingButton loading={postingAnnounce} disabled={!newAnnounce.trim()} onClick={handlePostAnnounce} style={btnG}>Post</LoadingButton></div>
        </div></div>
      )}

      <div style={{ display: "flex", gap: 12, marginBottom: 22, flexWrap: "wrap" }}>
        {stats.map((s, i) => (
          <div key={i} className="nt-card-hover" style={{ ...card, flex: 1, minWidth: 140, position: "relative", overflow: "hidden", animation: "staggerFadeIn 0.4s ease-out both", animationDelay: `${i * 0.05}s` }}>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: s.color, opacity: 0.5 }} />
            <div style={{ fontSize: 10, fontWeight: 600, color: "var(--t3)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>{s.label}</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: s.color, fontFamily: "'JetBrains Mono'" }}>{s.value}</div>
          </div>
        ))}
        {adminStats.map((s, i) => (
          <div key={`admin-${i}`} className="nt-card-hover" style={{ ...card, flex: 1, minWidth: 140, position: "relative", overflow: "hidden", animation: "staggerFadeIn 0.4s ease-out both", animationDelay: `${(stats.length + i) * 0.05}s` }}>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: s.color, opacity: 0.5 }} />
            <div style={{ fontSize: 10, fontWeight: 600, color: "var(--t3)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>{s.label}</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: s.color, fontFamily: "'JetBrains Mono'" }}>{s.value}</div>
          </div>
        ))}
      </div>

      {isAdmin && loginsByDay.length > 0 && (
        <div style={{ ...card, marginBottom: 22 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--t3)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12 }}>Logins (last 7 days)</div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height: 80 }}>
            {loginsByDay.map((d, i) => (
              <div key={d.day} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                <div style={{ width: "100%", height: 56, background: "var(--bg4)", borderRadius: 6, overflow: "hidden", display: "flex", alignItems: "flex-end" }}>
                  <div style={{ width: "100%", height: `${(d.count / maxLogins) * 100}%`, minHeight: d.count ? 4 : 0, background: "var(--gold)", borderRadius: "6px 6px 0 0", transition: "height 0.3s" }} />
                </div>
                <span style={{ fontSize: 10, color: "var(--t3)" }}>{d.day}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {(D.videos.some(v => v.isPinned) || D.notes.some(n => n.isPinned)) && (
        <div style={{ marginBottom: 22 }}>
          <h2 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}><span style={{color:"var(--gold)"}}>★</span> Featured Content</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(250px,1fr))", gap: 12 }}>
            {D.videos.filter(v => v.isPinned).map(v => (
              <div key={v.id} onClick={() => onNav("videos")} style={{ ...card, padding: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 10, border: "1px solid var(--gbdr)" }}>
                <div style={{ width: 40, height: 40, borderRadius: 8, background: "var(--gdim)", color: "var(--gold)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>▶</div>
                <div><div style={{ fontSize: 12, fontWeight: 600 }}>{v.title}</div><div style={{ fontSize: 10, color: "var(--t3)" }}>Video Lesson</div></div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── VIDEO LIBRARY ─────────────────────────────────────────────────────────
function VideoLibrary({ videos, setVideos, isAdmin, user, updateUser, actLog, setActLog, students, setStudents, homework, assignments, onNav }) {
  const toast = useToast();
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [newComment, setNewComment] = useState("");
  const commentCooldownRef = useRef(0);
  const [filterType, setFilterType] = useState("All");
  const [videoSaving, setVideoSaving] = useState(false);

  const saveVideo = async (data) => {
    setVideoSaving(true);
    try {
      const latest = await DB.get(SK.videos) || [];
      let updated;
      const isNew = !data.id;
      if (data.id) { updated = latest.map(v => v.id === data.id ? { ...v, ...data } : v); }
      else { updated = [{ ...data, id: secureId(), date: new Date().toISOString().split("T")[0], comments: [] }, ...latest]; }
      setVideos(updated); await DB.set(SK.videos, updated);
      if (selected && data.id === selected.id) setSelected({ ...selected, ...data });
      setShowAdd(false); setEditItem(null);
      if (toast) toast(isNew ? "Video added!" : "Video updated!");
    } finally { setVideoSaving(false); }
  };

  const deleteVideo = async (id) => {
    const latest = await DB.get(SK.videos) || [];
    const updated = latest.filter(v => v.id !== id);
    setVideos(updated); await DB.set(SK.videos, updated);
    setDeleting(null); setSelected(null);
    if (toast) toast("Video deleted.", "warn");
  };

  const togglePin = async (e, v) => { e.stopPropagation(); saveVideo({ ...v, isPinned: !v.isPinned }); };

  const postComment = async () => {
    if (!newComment) return;
    const now = Date.now();
    if (now - commentCooldownRef.current < 2000) return;
    commentCooldownRef.current = now;
    const comment = { id: secureId(), user: user.name, text: sanitizeText(newComment.trim()), date: new Date().toLocaleString() };
    const updatedVideo = { ...selected, comments: [...(selected.comments || []), comment] };
    saveVideo(updatedVideo);
    setNewComment("");
  };

  // Called ONLY when the video ends natively, or when they manually click the complete button for embeds
  const markWatched = async (video) => {
    if (isAdmin) return;
    if (!user.watchedVideos?.includes(video.id)) {
      const updatedUser = { ...user, watchedVideos: [...(user.watchedVideos||[]), video.id] };
      updateUser(updatedUser); // Instantly update frontend state to unlock tabs!
      
      const updatedStudents = students.map(s => s.id === user.id ? updatedUser : s);
      setStudents(updatedStudents); 
      await DB.set(SK.students, updatedStudents);
    }
    getIP().then(ip => {
        DB.get(SK.activityLog).then(currentLogs => {
          const entry = { id: secureId(), action: "video_view", videoTitle: video.title, ip, user: user.name, timestamp: new Date().toISOString() };
          const updatedAct = [entry, ...(currentLogs || [])].slice(0, 500);
          setActLog(updatedAct);
        DB.set(SK.activityLog, updatedAct);
      });
    });
  };

  const logSecurityBreach = (reason) => {
    getIP().then(ip => {
      DB.get(SK.activityLog).then(currentLogs => {
        const entry = { id: secureId(), action: "security_breach", videoTitle: selected?.title || "Unknown", ip, user: user.name, timestamp: new Date().toISOString(), details: reason };
        const updatedAct = [entry, ...(currentLogs || [])].slice(0, 500);
        setActLog(updatedAct);
        DB.set(SK.activityLog, updatedAct);
      });
    });
  };

  // Sort course videos specifically by Video Number to establish strict sequence 1, 2, 3...
  const courseVids = videos
     .filter(v => v.category === "Course")
     .sort((a, b) => (parseInt(a.videoNumber) || 999) - (parseInt(b.videoNumber) || 999) || a.id.localeCompare(b.id));
  
  const isUnlocked = (v) => {
    if (isAdmin) return true;
    if (v.category !== "Course") return true; 
    const idx = courseVids.findIndex(x => x.id === v.id);
    if (idx <= 0) return true; 
    
    // Strict sequential watch & approved homework check
    const prevVid = courseVids[idx - 1];
    const hasWatchedPrev = user.watchedVideos?.includes(prevVid.id);
    // Find assignments linked to the previous video, then check for approved homework
    const linkedAssignments = (assignments || []).filter(a => a.linkedVideoId === prevVid.id);
    const prevHwApproved = linkedAssignments.length === 0 ? true : linkedAssignments.every(a =>
      homework.find(h => h.assignmentId === a.id && h.studentId === user.discordId && h.status === "approved")
    );
    return hasWatchedPrev && prevHwApproved;
  };

  const VideoForm = ({ item, onSave, onClose }) => {
    const [f, setF] = useState(item || { title: "", desc: "", category: "Course", topic: "Chapter 1", url: "", embedType: "youtube", isPinned: false, videoNumber: 1, fileData: "" });
    const [uploading, setUploading] = useState(false);
    const [err, setErr] = useState("");

    const handleFileChange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setUploading(true);
      const formData = new FormData();
      formData.append("file", file);
      try {
        const res = await fetch("https://nexustrades.net/upload.php", { method: "POST", headers: DB.uploadHeaders(), credentials: "include", body: formData });
        const data = await res.json();
        if (data.url) setF(prev => ({ ...prev, fileData: data.url, url: data.url }));
        else if (toast) toast(data.error || "Upload failed on server.", "error");
      } catch (err) {
        if (toast) toast("Upload error: " + err.message, "error");
      }
      setUploading(false);
    };

    const handleSave = () => {
      if (!f.title.trim()) {
        setErr("Title is required.");
        return;
      }
      if (f.embedType !== "file" && !f.url.trim()) {
        setErr("Video URL is required (or upload a file).");
        return;
      }
      setErr("");
      onSave(f);
    };

    return (
      <div style={mOverlay} onClick={onClose}><div style={mBox} onClick={e => e.stopPropagation()}>
        <h2 style={{ fontSize: 17, fontWeight: 700, marginBottom: 18 }}>{item ? "Edit Video" : "Add Video"}</h2>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div><label style={lbl}>Title</label><input style={inp} value={f.title} onChange={e => setF({ ...f, title: e.target.value })} /></div>
          <div><label style={lbl}>Description / Homework Prompt</label><textarea style={{ ...inp, minHeight: 70 }} value={f.desc} onChange={e => setF({ ...f, desc: e.target.value })} /></div>
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1 }}><label style={lbl}>Type</label><select style={inp} value={f.category || "Course"} onChange={e => setF({ ...f, category: e.target.value })}><option value="Course">Course (Linear Progression)</option><option value="Trade Recap">Trade Recap (Unlocked)</option></select></div>
            <div style={{ flex: 1 }}><label style={lbl}>Module / Topic</label><input style={inp} value={f.topic || f.module} onChange={e => setF({ ...f, topic: e.target.value, module: e.target.value })} placeholder="e.g. Chapter 1" /></div>
          </div>

          {f.category === "Course" && (
             <div><label style={lbl}>Course Video Number (Sequence)</label><input type="number" style={inp} value={f.videoNumber || 1} onChange={e => setF({...f, videoNumber: Number(e.target.value)})} placeholder="e.g. 1" /></div>
          )}

          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1 }}><label style={lbl}>Source</label><select style={inp} value={f.embedType} onChange={e => setF({ ...f, embedType: e.target.value })}><option value="youtube">YouTube</option><option value="gdrive">Google Drive</option><option value="file">File (Blob Upload)</option></select></div>
            
            {f.embedType === "file" ? (
              <div style={{ flex: 2 }}>
                 <label style={lbl}>Upload Video File</label>
                 <input type="file" accept="video/*" onChange={handleFileChange} style={{fontSize: 11, color: "var(--t2)", marginTop: 6}} />
                 {uploading && <div style={{fontSize: 10, color: "var(--gold)", marginTop: 4, fontWeight: 600}}>Uploading to server... please wait.</div>}
                 {f.fileData && !uploading && <div style={{fontSize: 10, color: "var(--grn)", marginTop: 4, fontWeight: 600}}>✅ File Ready</div>}
              </div>
            ) : (
              <div style={{ flex: 2 }}><label style={lbl}>Video URL</label><input style={inp} value={f.url} onChange={e => setF({ ...f, url: e.target.value })} /></div>
            )}
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, cursor: "pointer", marginTop: 5 }}>
            <input type="checkbox" checked={f.isPinned} onChange={e => setF({ ...f, isPinned: e.target.checked })} /> Pin to Dashboard
          </label>
        </div>
        {err && <div style={{ fontSize: 11, color: "var(--red)", marginTop: 8 }}>{err}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}><button onClick={onClose} style={btnO}>Cancel</button><LoadingButton loading={videoSaving} disabled={uploading} onClick={handleSave} style={btnG}>Save</LoadingButton></div>
      </div></div>
    );
  };

  if (selected) {
    const isWatched = user.watchedVideos?.includes(selected.id);
    let displayTitle = selected.title;
    
    if (selected.category === "Course") {
       const cIdx = courseVids.findIndex(cv => cv.id === selected.id);
       const num = selected.videoNumber || (cIdx > -1 ? cIdx + 1 : "?");
       displayTitle = `Video ${num}: ${selected.title}`;
    }

    return (
      <div style={{ animation: "fadeIn 0.3s" }}>
        <button onClick={() => setSelected(null)} style={{ background: "none", border: "none", color: "var(--gold)", fontSize: 12, cursor: "pointer", marginBottom: 14, fontWeight: 600 }}>← Back to Library</button>
        <div style={{ ...card, padding: 0, overflow: "hidden", marginBottom: 20 }}>
          <ProtectedVideoPlayer
            src={selected.fileData}
            embedUrl={selected.url}
            embedType={selected.embedType}
            userEmail={user.email}
            userIP={cachedIP || "hidden"}
            userName={user.name}
            userDiscordId={user.discordId}
            onSecurityBreach={logSecurityBreach}
            onVideoComplete={() => markWatched(selected)}
            isFirstWatch={!isAdmin && !user.watchedVideos?.includes(selected.id)}
          />
          <div style={{ padding: "20px" }}>
            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <span style={{ padding: "3px 8px", borderRadius: 5, fontSize: 10, fontWeight: 700, background: "var(--bg4)", color: selected.category === "Course" ? "var(--blu)" : "var(--gold)" }}>{selected.category || "General"}</span>
              <span style={{ padding: "3px 8px", borderRadius: 5, fontSize: 10, fontWeight: 700, background: "var(--bg4)", color: "var(--t2)" }}>{selected.topic || selected.module}</span>
            </div>
            <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>{displayTitle}</h1>
            <p style={{ fontSize: 13, color: "var(--t2)", lineHeight: 1.6, whiteSpace:"pre-wrap" }}>{selected.desc}</p>
          </div>
        </div>

        {selected.category === "Course" && !isAdmin && (
          <div style={{ ...card, marginBottom: 20, border: "1px solid var(--blu-d)", background: "var(--bg2)" }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 10, color: "var(--blu)" }}>Homework Assignment</h3>
            {isWatched ? (() => {
              const linkedAssigns = (assignments || []).filter(a => a.linkedVideoId === selected.id);
              if (linkedAssigns.length === 0) return (
                <div style={{ fontSize: 13, color: "var(--t2)" }}>
                  <span style={{ fontSize: 20, marginRight: 8, verticalAlign: "middle" }}>✅</span>
                  Video complete! No homework assignments are linked to this video yet.
                  <div style={{ marginTop: 12 }}>
                    <button onClick={() => onNav("student_homework")} style={{ ...btnO, fontSize: 11 }}>View All Assignments →</button>
                  </div>
                </div>
              );
              return (
                <div>
                  <div style={{ fontSize: 12, color: "var(--grn)", marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
                    <span>✅</span> Video complete! Submit your homework below.
                  </div>
                  {linkedAssigns.map(a => {
                    const myHw = homework.find(h => h.assignmentId === a.id && h.studentId === user.discordId);
                    const entries = myHw?.entries || [];
                    const target = a.targetCount || 10;
                    const pct = Math.min(100, Math.round((entries.length / target) * 100));
                    const statusLabel = myHw?.status === "approved" ? "Approved" : myHw?.status === "pending" ? "Pending Review" : myHw?.status === "rejected" ? "Rejected" : entries.length > 0 ? "In Progress" : "Not Started";
                    const statusColor = myHw?.status === "approved" ? "var(--grn)" : myHw?.status === "pending" ? "var(--blu)" : myHw?.status === "rejected" ? "var(--red)" : entries.length > 0 ? "var(--gold)" : "var(--t3)";
                    return (
                      <div key={a.id} style={{ padding: 12, background: "var(--bg3)", borderRadius: 8, border: "1px solid var(--bdr)", marginBottom: 8 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                          <span style={{ fontSize: 13, fontWeight: 700 }}>{a.title}</span>
                          <span style={{ fontSize: 9, fontWeight: 800, color: statusColor, textTransform: "uppercase" }}>{statusLabel}</span>
                        </div>
                        <p style={{ fontSize: 11, color: "var(--t2)", marginBottom: 8, lineHeight: 1.5 }}>{a.prompt}</p>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "var(--t3)", marginBottom: 3 }}>
                          <span>{entries.length} / {target} entries</span>
                          <span style={{ fontWeight: 700, color: pct >= 100 ? "var(--grn)" : "var(--gold)" }}>{pct}%</span>
                        </div>
                        <div style={{ height: 4, background: "var(--bg0)", borderRadius: 3, overflow: "hidden" }}>
                          <div style={{ height: "100%", width: `${pct}%`, background: pct >= 100 ? "var(--grn)" : "var(--gold)", borderRadius: 3 }} />
                        </div>
                      </div>
                    );
                  })}
                  <div style={{ marginTop: 10 }}>
                    <button onClick={() => onNav("student_homework")} style={{ ...btnG, background: "var(--blu)", boxShadow: "none", fontSize: 12 }}>Go to Homework Tab to Submit →</button>
                  </div>
                </div>
              );
            })() : (
              <div style={{ fontSize: 13, color: "var(--t3)", textAlign: "center", padding: "20px 0" }}>
                 <span style={{ fontSize: 24, display: "block", marginBottom: 10 }}>🔒</span>
                 You must watch the entire video to unlock the homework submission form.
                 {selected.embedType !== "file" && (
                   <div style={{ marginTop: 16 }}>
                     <button onClick={() => markWatched(selected)} style={btnO}>I have finished watching this external video</button>
                   </div>
                 )}
              </div>
            )}
          </div>
        )}

        <div style={card}>
          <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Q&A / Comments</h3>
          <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
            <div style={{ flex: 1 }}>
              <input style={{ ...inp, flex: 1 }} value={newComment} onChange={e => setNewComment(e.target.value)} placeholder="Ask a question..." onKeyDown={e => e.key === "Enter" && postComment()} maxLength={280} />
              <div style={{ fontSize: 10, color: newComment.length >= 260 ? "var(--red)" : "var(--t3)", marginTop: 4 }}>{newComment.length} / 280</div>
            </div>
            <button onClick={postComment} style={btnG}>Post</button>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {(selected.comments || []).map(c => (
              <div key={c.id} style={{ padding: 12, background: "var(--bg2)", borderRadius: 8, border: "1px solid var(--bdr)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}><span style={{ fontSize: 12, fontWeight: 600, color: c.user === "Kev" ? "var(--gold)" : "var(--t1)" }}>{c.user}</span><span style={{ fontSize: 10, color: "var(--t3)" }}>{c.date}</span></div>
                <div style={{ fontSize: 13, color: "var(--t2)" }}>{c.text}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const filters = ["All", "Course", "Trade Recap"];

  const filtered = videos.filter(v => filterType === "All" || (v.category || "Course") === filterType).sort((a,b) => (b.isPinned === a.isPinned ? 0 : b.isPinned ? 1 : -1));

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 22 }}>
        <div><h1 style={{ fontSize: 22, fontWeight: 800 }}>Video Library</h1><p style={{ color: "var(--t2)", fontSize: 13 }}>Courses & Recaps.</p></div>
        {isAdmin && <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => setShowAdd(true)} style={btnG}>+ Add Video</button>
        </div>}
      </div>

      <div style={{ display: "flex", gap: 6, marginBottom: 16, overflowX: "auto", paddingBottom: 5 }}>
        {filters.map(m => (
          <button key={m} onClick={() => setFilterType(m)} style={{ padding: "6px 14px", borderRadius: 20, border: "1px solid", borderColor: filterType === m ? "var(--gold)" : "var(--bdr)", background: filterType === m ? "var(--gdim)" : "var(--bg3)", color: filterType === m ? "var(--gold)" : "var(--t2)", fontSize: 11, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>{m}</button>
        ))}
      </div>

      {filtered.length === 0 && <Empty variant="videos" icon="🎬" title="No Videos Found" sub={`No ${filterType !== "All" ? filterType.toLowerCase() : ""} videos yet.`} />}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 12 }}>
        {filtered.map((v, i) => {
          const unlocked = isUnlocked(v);
          const isWatched = user.watchedVideos?.includes(v.id);
          
          let displayTitle = v.title;
          if (v.category === "Course") {
             const cIdx = courseVids.findIndex(cv => cv.id === v.id);
             const num = v.videoNumber || (cIdx > -1 ? cIdx + 1 : "?");
             displayTitle = `Video ${num}: ${v.title}`;
          }

          return (
            <div key={v.id} className={unlocked ? "nt-card-hover" : ""} style={{ ...card, padding: 0, overflow: "hidden", cursor: unlocked ? "pointer" : "not-allowed", position: "relative", border: v.isPinned ? "1px solid var(--gold)" : "1px solid var(--bdr)", opacity: unlocked ? 1 : 0.6, animation: "staggerFadeIn 0.4s ease-out both", animationDelay: `${i * 0.05}s` }} onClick={() => { if(unlocked) { setSelected(v); } }}>
              <div style={{ height: 140, background: "var(--bg2)", display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
                {unlocked ? (
                  <div style={{ width: 44, height: 44, borderRadius: "50%", background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, color: "var(--gold)" }}>▶</div>
                ) : (
                  <div style={{ width: 44, height: 44, borderRadius: "10%", background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, color: "var(--t3)" }}>🔒</div>
                )}
                {v.isPinned && <div style={{ position: "absolute", top: 8, right: 8, background: "var(--gold)", color: "#000", padding: "2px 6px", borderRadius: 4, fontSize: 10, fontWeight: 700 }}>PINNED</div>}
              </div>
              <div style={{ padding: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.3 }}>{displayTitle}</div>
                  {isAdmin && (
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={e => togglePin(e, v)} style={{ background:"none", border:"none", cursor:"pointer", color: v.isPinned ? "var(--gold)" : "var(--t3)" }}>★</button>
                      <button onClick={e => { e.stopPropagation(); setEditItem(v); }} style={{ background:"none", border:"none", cursor:"pointer", fontSize: 12 }}>✏️</button>
                      <button onClick={e => { e.stopPropagation(); setDeleting(v.id); }} style={{ background:"none", border:"none", cursor:"pointer", color:"var(--close-btn)", fontSize: 14, fontWeight: 800, transition:"color 0.15s", opacity:0.7 }}>✕</button>
                    </div>
                  )}
                </div>
                <div style={{ fontSize: 11, color: "var(--t3)", display: "flex", gap: 6, alignItems: "center" }}>
                   <span style={{color: v.category === "Course" ? "var(--blu)" : "var(--gold)", fontWeight: 600}}>{v.category || "Course"}</span>
                   <span>•</span> {v.topic || v.module}
                </div>
                {!unlocked && <div style={{ marginTop: 8, fontSize: 10, color: "var(--red)", fontWeight: 600 }}>Get previous homework approved to unlock.</div>}
                {!isAdmin && unlocked && isWatched && <div style={{ marginTop: 8, fontSize: 10, color: "var(--grn)", fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}><span>✓</span> Watched</div>}
              </div>
            </div>
          );
        })}
      </div>

      {showAdd && <VideoForm onSave={saveVideo} onClose={() => setShowAdd(false)} />}
      {editItem && <VideoForm item={editItem} onSave={saveVideo} onClose={() => setEditItem(null)} />}
      {deleting && <Confirm msg="Delete video?" onY={() => deleteVideo(deleting)} onN={() => setDeleting(null)} />}
    </div>
  );
}

// ─── STUDENT HOMEWORK SUBMISSION HUB ───────────────────────────────────────
function StudentHomework({ videos, user, homework, setHomework, assignments }) {
  const toast = useToast();
  const [activeAssignment, setActiveAssignment] = useState(null);
  const [reasoning, setReasoning] = useState("");
  const [uploading, setUploading] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [addingEntry, setAddingEntry] = useState(false);
  const [deletingEntry, setDeletingEntry] = useState(null);

  const myAssignments = (assignments || []).map(a => {
    const myHw = homework.find(h => h.assignmentId === a.id && h.studentId === user.discordId);
    return { ...a, myHw };
  });

  // Upload single image
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch("https://nexustrades.net/upload.php", { method: "POST", headers: DB.uploadHeaders(), credentials: "include", body: formData });
      const data = await res.json();
      if (data.url) setImageUrl(data.url);
    } catch (e) { _log("Upload failed:", e.name); if (toast) toast("Upload failed.", "error"); }
    setUploading(false);
  };

  const addEntry = async (assignment) => {
    if (!imageUrl && !reasoning.trim()) return;
    if (addingEntry) return;
    setAddingEntry(true);
    try {
      const latest = await DB.get(SK.homework) || [];
      const myHw = latest.find(h => h.assignmentId === assignment.id && h.studentId === user.discordId);
      const newEntry = { id: secureId(), image: imageUrl, reasoning: reasoning.trim(), date: new Date().toLocaleDateString() };

      let updated;
      if (myHw) {
        const updatedEntries = [...(myHw.entries || []), newEntry];
        updated = latest.map(h => h.id === myHw.id ? { ...h, entries: updatedEntries, status: "in_progress", date: new Date().toLocaleDateString() } : h);
      } else {
        const newHw = {
          id: secureId(), assignmentId: assignment.id, assignmentTitle: assignment.title,
          studentId: user.discordId, studentName: user.name,
          entries: [newEntry], status: "in_progress", date: new Date().toLocaleDateString()
        };
        updated = [newHw, ...latest];
      }
      setHomework(updated); await DB.set(SK.homework, updated);
      setImageUrl(""); setReasoning("");
      if (toast) toast("Entry added!");
    } finally {
      setAddingEntry(false);
    }
  };

  // Delete a single entry
  const deleteEntry = async (assignment, entryId) => {
    const latest = await DB.get(SK.homework) || [];
    const myHw = latest.find(h => h.assignmentId === assignment.id && h.studentId === user.discordId);
    if (!myHw) return;
    const updatedEntries = (myHw.entries || []).filter(e => e.id !== entryId);
    const updated = latest.map(h => h.id === myHw.id ? { ...h, entries: updatedEntries } : h);
    setHomework(updated); await DB.set(SK.homework, updated);
    setDeletingEntry(null);
    if (toast) toast("Entry removed.", "warn");
  };

  // Submit for review
  const submitForReview = async (assignment) => {
    const latest = await DB.get(SK.homework) || [];
    const myHw = latest.find(h => h.assignmentId === assignment.id && h.studentId === user.discordId);
    if (!myHw || (myHw.entries || []).length === 0) return;
    setSubmitting(true);
    const updated = latest.map(h => h.id === myHw.id ? { ...h, status: "pending", date: new Date().toLocaleDateString() } : h);
    setHomework(updated); await DB.set(SK.homework, updated);
    setSubmitting(false);
    if (toast) toast("Homework submitted for review!");
  };

  // Progress bar component
  const ProgressBar = ({ current, target }) => {
    const pct = Math.min(100, Math.round((current / target) * 100));
    const barColor = pct >= 100 ? "var(--grn)" : "var(--gold)";
    return (
      <div style={{ marginTop: 10 }}>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "var(--t3)", marginBottom: 4 }}>
          <span>{current} / {target} entries</span>
          <span style={{ fontWeight: 700, color: pct >= 100 ? "var(--grn)" : "var(--gold)" }}>{pct}%</span>
        </div>
        <div style={{ height: 6, background: "var(--bg0)", borderRadius: 3, overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${pct}%`, background: barColor, borderRadius: 3, transition: "width 0.4s ease" }} />
        </div>
      </div>
    );
  };

  // Detail view for a single assignment
  if (activeAssignment) {
    const a = activeAssignment;
    const myHw = homework.find(h => h.assignmentId === a.id && h.studentId === user.discordId);
    const entries = myHw?.entries || [];
    const target = a.targetCount || 10;
    const isFull = entries.length >= target;
    const isPending = myHw?.status === "pending";
    const isApproved = myHw?.status === "approved";
    const isRejected = myHw?.status === "rejected";
    const canAddEntries = !isPending && !isApproved;

    let statusColor = "var(--bdr)";
    if (isApproved) statusColor = "var(--grn)";
    if (isPending) statusColor = "var(--blu)";
    if (isRejected) statusColor = "var(--red)";

    return (
      <div style={{ animation: "fadeIn 0.3s" }}>
        <button onClick={() => setActiveAssignment(null)} style={{ ...btnO, marginBottom: 16, display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 16 }}>&larr;</span> Back to Assignments
        </button>

        <div style={{ ...card, borderLeft: `4px solid ${statusColor}`, marginBottom: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div style={{ flex: 1 }}>
              <h2 style={{ fontSize: 18, fontWeight: 800, marginBottom: 6 }}>{a.title}</h2>
              <p style={{ fontSize: 13, color: "var(--t2)", lineHeight: 1.6 }}>{a.prompt}</p>
              {a.linkedVideoTitle && <div style={{ fontSize: 11, color: "var(--blu)", marginTop: 6 }}>Linked Video: {a.linkedVideoTitle}</div>}
            </div>
            {myHw && (
              <span style={{ padding: "4px 10px", borderRadius: 6, fontSize: 10, fontWeight: 800, textTransform: "uppercase", background: "var(--bg4)", color: statusColor, flexShrink: 0 }}>
                {myHw.status === "in_progress" ? "In Progress" : myHw.status}
              </span>
            )}
          </div>
          <ProgressBar current={entries.length} target={target} />
        </div>

        {isRejected && (
          <div style={{ fontSize: 12, color: "var(--red)", marginBottom: 16, fontWeight: 600, background: "var(--red-d)", padding: "10px 14px", borderRadius: 8 }}>
            Your submission was rejected. You can edit your entries and resubmit.
          </div>
        )}

        {isApproved && (
          <div style={{ fontSize: 12, color: "var(--grn)", marginBottom: 16, fontWeight: 600, background: "rgba(34,197,94,0.08)", padding: "10px 14px", borderRadius: 8, border: "1px solid rgba(34,197,94,0.2)" }}>
            This assignment has been approved!
          </div>
        )}

        {/* Entry submission form */}
        {canAddEntries && !isFull && (
          <div style={{ ...card, marginBottom: 20, borderLeft: "4px solid var(--gold)" }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Add Entry #{entries.length + 1}</h3>

            <div style={{ marginBottom: 12 }}>
              <label style={lbl}>Screenshot / Image</label>
              {imageUrl ? (
                <div style={{ position: "relative", display: "inline-block", marginBottom: 8 }}>
                  <img src={imageUrl} style={{ maxWidth: "100%", maxHeight: 280, borderRadius: 8, border: "1px solid var(--bdr)" }} alt="upload" />
                  <button onClick={() => setImageUrl("")} style={{ position: "absolute", top: 6, right: 6, background: "rgba(0,0,0,0.7)", border: "none", color: "#fff", borderRadius: "50%", width: 22, height: 22, cursor: "pointer", fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center" }}>&times;</button>
                </div>
              ) : (
                <label style={{ ...btnO, background: "var(--bg2)", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
                  {uploading ? <><Spinner size={12} color="var(--t2)" /> Uploading...</> : "Upload Image"}
                  <input type="file" accept="image/*" onChange={handleImageUpload} style={{ display: "none" }} disabled={uploading} />
                </label>
              )}
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={lbl}>Reasoning / Notes</label>
              <textarea style={{ ...inp, minHeight: 90 }} value={reasoning} onChange={e => setReasoning(e.target.value)} placeholder="Explain your thought process, analysis, or findings..." />
            </div>

            <button onClick={() => addEntry(a)} disabled={addingEntry || uploading || (!imageUrl && !reasoning.trim())} className="nt-btn-hover" style={{ ...btnG, opacity: (addingEntry || uploading || (!imageUrl && !reasoning.trim())) ? 0.5 : 1, display: "flex", alignItems: "center", gap: 8 }}>
              {addingEntry ? <><Spinner size={14} /> Adding...</> : "Add Entry"}
            </button>
          </div>
        )}

        {canAddEntries && isFull && !isPending && (
          <div style={{ ...card, marginBottom: 20, textAlign: "center", borderLeft: "4px solid var(--grn)" }}>
            <p style={{ fontSize: 13, color: "var(--grn)", fontWeight: 700, marginBottom: 10 }}>Target reached! You can submit for review.</p>
            <button onClick={() => submitForReview(a)} disabled={submitting} className="nt-btn-hover" style={{ ...btnG, background: "var(--grn)", boxShadow: "none", display: "inline-flex", alignItems: "center", gap: 8 }}>
              {submitting ? <><Spinner size={14} /> Submitting...</> : "Submit for Review"}
            </button>
          </div>
        )}

        {/* Allow early submission if at least 1 entry exists */}
        {canAddEntries && entries.length > 0 && !isFull && (
          <div style={{ marginBottom: 20 }}>
            <button onClick={() => submitForReview(a)} disabled={submitting} className="nt-btn-hover" style={{ ...btnO, display: "inline-flex", alignItems: "center", gap: 8, fontSize: 11 }}>
              {submitting ? <><Spinner size={12} color="var(--t2)" /> Submitting...</> : `Submit Early (${entries.length}/${target})`}
            </button>
          </div>
        )}

        {/* Entries list */}
        {entries.length > 0 && (
          <div>
            <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Your Entries ({entries.length})</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {entries.map((entry, i) => (
                <div key={entry.id} style={{ padding: 14, background: "var(--bg2)", borderRadius: 10, border: "1px solid var(--bdr)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: "var(--gold)" }}>Entry #{i + 1}</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 10, color: "var(--t3)" }}>{entry.date}</span>
                      {canAddEntries && (
                        <button onClick={() => setDeletingEntry({ assignmentId: a.id, entryId: entry.id })} style={{ background: "none", border: "none", color: "var(--close-btn)", cursor: "pointer", fontSize: 14, padding: 0, lineHeight: 1 }}>&times;</button>
                      )}
                    </div>
                  </div>
                  {entry.image && <img src={entry.image} style={{ width: "100%", maxWidth: 500, borderRadius: 8, marginBottom: 8, border: "1px solid var(--bdr)" }} alt={`entry-${i + 1}`} />}
                  {entry.reasoning && <p style={{ fontSize: 13, color: "var(--t2)", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{entry.reasoning}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {deletingEntry && <Confirm msg="Delete this entry?" onY={() => deleteEntry(activeAssignment, deletingEntry.entryId)} onN={() => setDeletingEntry(null)} />}
      </div>
    );
  }

  // Assignment list view
  return (
    <div style={{ animation: "fadeIn 0.3s" }}>
      <div style={{ marginBottom: 26 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800 }}>My Homework</h1>
        <p style={{ color: "var(--t2)", fontSize: 13, marginTop: 4 }}>Complete assignments by submitting entries one at a time.</p>
      </div>

      {myAssignments.length === 0 ? (
        <Empty icon="📚" title="No Assignments Yet" sub="Your instructor hasn't created any assignments yet. Check back soon!" />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {myAssignments.map(a => {
            const entries = a.myHw?.entries || [];
            const target = a.targetCount || 10;
            const pct = Math.min(100, Math.round((entries.length / target) * 100));

            let statusColor = "var(--bdr)";
            let statusLabel = "Not Started";
            if (a.myHw?.status === "approved") { statusColor = "var(--grn)"; statusLabel = "Approved"; }
            else if (a.myHw?.status === "pending") { statusColor = "var(--blu)"; statusLabel = "Pending Review"; }
            else if (a.myHw?.status === "rejected") { statusColor = "var(--red)"; statusLabel = "Rejected"; }
            else if (entries.length > 0) { statusColor = "var(--gold)"; statusLabel = "In Progress"; }

            return (
              <div key={a.id} className="nt-card-hover" onClick={() => setActiveAssignment(a)} style={{ ...card, borderLeft: `4px solid ${statusColor}`, cursor: "pointer", transition: "border-color 0.2s" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>{a.title}</h2>
                    <p style={{ fontSize: 12, color: "var(--t2)", lineHeight: 1.5 }}>{a.prompt}</p>
                    {a.linkedVideoTitle && <div style={{ fontSize: 10, color: "var(--blu)", marginTop: 4 }}>Linked: {a.linkedVideoTitle}</div>}
                  </div>
                  <span style={{ padding: "4px 10px", borderRadius: 6, fontSize: 9, fontWeight: 800, textTransform: "uppercase", background: "var(--bg4)", color: statusColor, flexShrink: 0, marginLeft: 12 }}>
                    {statusLabel}
                  </span>
                </div>

                {/* Progress bar */}
                <div style={{ marginTop: 6 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "var(--t3)", marginBottom: 3 }}>
                    <span>{entries.length} / {target} entries</span>
                    <span style={{ fontWeight: 700, color: pct >= 100 ? "var(--grn)" : "var(--gold)" }}>{pct}%</span>
                  </div>
                  <div style={{ height: 5, background: "var(--bg0)", borderRadius: 3, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${pct}%`, background: pct >= 100 ? "var(--grn)" : "var(--gold)", borderRadius: 3, transition: "width 0.4s ease" }} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── ADMIN HOMEWORK (ASSIGNMENTS + REVIEW) ──────────────────────────────────
function HomeworkAdmin({ homework, setHomework, assignments, setAssignments, videos }) {
  const toast = useToast();
  const [tab, setTab] = useState("review"); // "review" | "manage"
  const [showCreate, setShowCreate] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [expandedSub, setExpandedSub] = useState(null);
  const [deleting, setDeleting] = useState(null);

  // ---- ASSIGNMENT CRUD (fetch-before-write to prevent overwrites) ----
  const saveAssignment = async (data) => {
    const latest = await DB.get(SK.assignments) || [];
    let updated;
    if (data.id) {
      updated = latest.map(a => a.id === data.id ? { ...a, ...data } : a);
    } else {
      updated = [{ ...data, id: secureId(), date: new Date().toLocaleDateString() }, ...latest];
    }
    setAssignments(updated); await DB.set(SK.assignments, updated);
    setShowCreate(false); setEditItem(null);
    if (toast) toast(data.id ? "Assignment updated!" : "Assignment created!");
  };

  const deleteAssignment = async (id) => {
    const latest = await DB.get(SK.assignments) || [];
    const updated = latest.filter(a => a.id !== id);
    setAssignments(updated); await DB.set(SK.assignments, updated);
    setDeleting(null);
    if (toast) toast("Assignment deleted.", "warn");
  };

  // ---- SUBMISSION REVIEW ----
  const updateStatus = async (id, status) => {
    const latest = await DB.get(SK.homework) || [];
    const updated = latest.map(h => h.id === id ? { ...h, status } : h);
    setHomework(updated); await DB.set(SK.homework, updated);
    if (toast) toast(status === "approved" ? "Homework approved!" : "Homework rejected.", status === "approved" ? "success" : "warn");
  };

  const pending = homework.filter(h => h.status === "pending");

  // ---- ASSIGNMENT FORM ----
  const AssignmentForm = ({ item, onClose }) => {
    const [f, setF] = useState(item || { title: "", prompt: "", linkedVideoId: "", targetCount: 10 });
    return (
      <div style={mOverlay} onClick={onClose}><div style={mBox} onClick={e => e.stopPropagation()}>
        <h2 style={{ fontSize: 17, fontWeight: 700, marginBottom: 18 }}>{item ? "Edit Assignment" : "Create Assignment"}</h2>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div><label style={lbl}>Title</label><input style={inp} value={f.title} onChange={e => setF({ ...f, title: e.target.value })} placeholder='e.g. "Week 1 — Market Structure"' /></div>
          <div><label style={lbl}>Prompt / Instructions</label><textarea style={{ ...inp, minHeight: 90 }} value={f.prompt} onChange={e => setF({ ...f, prompt: e.target.value })} placeholder='e.g. "Do 50 Markups" or "Find 10 examples of MSS"' /></div>
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={lbl}>Target Submissions</label>
              <input type="number" min="1" max="50" style={inp} value={f.targetCount} onChange={e => setF({ ...f, targetCount: Math.min(50, Math.max(1, parseInt(e.target.value) || 1)) })} />
            </div>
            <div style={{ flex: 2 }}>
              <label style={lbl}>Link to Video (Optional)</label>
              <select style={inp} value={f.linkedVideoId || ""} onChange={e => {
                const vid = videos.find(v => v.id === e.target.value);
                setF({ ...f, linkedVideoId: e.target.value || "", linkedVideoTitle: vid ? vid.title : "" });
              }}>
                <option value="">— None —</option>
                {videos.map(v => <option key={v.id} value={v.id}>{v.category === "Course" ? `Video ${v.videoNumber || "?"}: ` : ""}{v.title}</option>)}
              </select>
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 18 }}>
          <button onClick={onClose} style={btnO}>Cancel</button>
          <button onClick={() => { if (f.title && f.prompt) saveAssignment(f); }} style={btnG}>Save</button>
        </div>
      </div></div>
    );
  };

  const tabs = [
    { id: "review", label: "Review Submissions", count: pending.length },
    { id: "manage", label: "Manage Assignments", count: 0 },
  ];

  return (
    <div style={{ animation: "fadeIn 0.3s" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 22 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800 }}>Homework</h1>
          <p style={{ color: "var(--t2)", fontSize: 13, marginTop: 4 }}>Create assignments and review student submissions.</p>
        </div>
        {tab === "manage" && <button onClick={() => setShowCreate(true)} style={btnG}>+ Create Assignment</button>}
      </div>

      <div style={{ display: "flex", gap: 6, marginBottom: 20 }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ padding: "8px 16px", borderRadius: 20, border: "1px solid", borderColor: tab === t.id ? "var(--gold)" : "var(--bdr)", background: tab === t.id ? "var(--gdim)" : "var(--bg3)", color: tab === t.id ? "var(--gold)" : "var(--t2)", fontSize: 12, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontFamily: "'Outfit',sans-serif" }}>
            {t.label}
            {t.count > 0 && <span style={{ background: "var(--red)", color: "#fff", fontSize: 9, padding: "1px 6px", borderRadius: 10, fontWeight: 800 }}>{t.count}</span>}
          </button>
        ))}
      </div>

      {/* REVIEW SUBMISSIONS TAB */}
      {tab === "review" && (
        pending.length === 0 ? (
          <Empty icon="📚" title="All caught up!" sub="No pending homework submissions to review." />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {pending.map(h => {
              const asgn = (assignments || []).find(a => a.id === h.assignmentId);
              const entries = h.entries || [];
              const target = asgn?.targetCount || entries.length;
              const isExpanded = expandedSub === h.id;

              return (
                <div key={h.id} style={{ ...card, borderLeft: "4px solid var(--blu)", padding: 0 }}>
                  <div style={{ padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }} onClick={() => setExpandedSub(isExpanded ? null : h.id)}>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: "var(--t1)" }}>{h.studentName}</div>
                      <div style={{ fontSize: 11, color: "var(--t3)" }}>{asgn?.title || h.assignmentTitle || "Assignment"} — {entries.length}/{target} entries • {h.date}</div>
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }} onClick={e => e.stopPropagation()}>
                      <button onClick={() => updateStatus(h.id, "rejected")} style={{ ...btnO, color: "var(--red)", borderColor: "rgba(239,68,68,0.3)" }}>Reject</button>
                      <button onClick={() => updateStatus(h.id, "approved")} style={{ ...btnG, background: "var(--grn)", boxShadow: "none" }}>Approve</button>
                      <span style={{ color: "var(--t3)", fontSize: 14, marginLeft: 4 }}>{isExpanded ? "▲" : "▼"}</span>
                    </div>
                  </div>
                  {isExpanded && (
                    <div style={{ padding: "0 20px 20px", borderTop: "1px solid var(--bdr)" }}>
                      {entries.length === 0 && <div style={{ padding: 16, color: "var(--t3)", fontSize: 12 }}>No entries submitted.</div>}
                      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 12 }}>
                        {entries.map((entry, i) => (
                          <div key={entry.id} style={{ padding: 14, background: "var(--bg2)", borderRadius: 8, border: "1px solid var(--bdr)" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                              <span style={{ fontSize: 11, fontWeight: 700, color: "var(--gold)" }}>Entry #{i + 1}</span>
                              <span style={{ fontSize: 10, color: "var(--t3)" }}>{entry.date}</span>
                            </div>
                            {entry.image && <img src={entry.image} style={{ width: "100%", maxWidth: 500, borderRadius: 8, marginBottom: 8, border: "1px solid var(--bdr)" }} alt={`entry-${i+1}`} />}
                            <p style={{ fontSize: 13, color: "var(--t2)", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{entry.reasoning || "No reasoning provided."}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      )}

      {/* MANAGE ASSIGNMENTS TAB */}
      {tab === "manage" && (
        (assignments || []).length === 0 ? (
          <Empty icon="📝" title="No Assignments" sub="Create your first homework assignment for students." action="+ Create Assignment" onAction={() => setShowCreate(true)} />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {(assignments || []).map(a => {
              const linked = a.linkedVideoId ? videos.find(v => v.id === a.linkedVideoId) : null;
              const subCount = homework.filter(h => h.assignmentId === a.id).length;
              const approvedCount = homework.filter(h => h.assignmentId === a.id && h.status === "approved").length;
              return (
                <div key={a.id} style={{ ...card, display: "flex", alignItems: "flex-start", gap: 16 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 10, background: "var(--gdim)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, color: "var(--gold)", flexShrink: 0 }}>📝</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>{a.title}</div>
                    <div style={{ fontSize: 12, color: "var(--t2)", marginBottom: 6, lineHeight: 1.5 }}>{a.prompt}</div>
                    <div style={{ display: "flex", gap: 12, fontSize: 10, color: "var(--t3)", flexWrap: "wrap" }}>
                      <span>Target: <strong style={{ color: "var(--t1)" }}>{a.targetCount}</strong> entries</span>
                      {linked && <span>Linked: <strong style={{ color: "var(--blu)" }}>{linked.title}</strong></span>}
                      <span>{subCount} submissions ({approvedCount} approved)</span>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                    <button onClick={() => setEditItem(a)} style={btnO}>Edit</button>
                    <button onClick={() => setDeleting(a.id)} style={btnD}>Delete</button>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {showCreate && <AssignmentForm onClose={() => setShowCreate(false)} />}
      {editItem && <AssignmentForm item={editItem} onClose={() => setEditItem(null)} />}
      {deleting && <Confirm msg="Delete this assignment? Student submissions will remain." onY={() => deleteAssignment(deleting)} onN={() => setDeleting(null)} />}
    </div>
  );
}

// Format IP for display: show raw IP; if it's the old 16-char hash, show "—"
function formatLogIp(ip) {
  if (!ip || ip === "unknown") return "—";
  if (/^[a-f0-9]{16}$/i.test(ip)) return "—"; // legacy hashed value
  return ip;
}

// ─── SECURITY & ACTIVITY LOGS ──────────────────────────────────────────────
function SecurityLogs({ actLog }) {
  const [filter, setFilter] = useState("All");
  const [sortBy, setSortBy] = useState("time");
  const [sortDir, setSortDir] = useState("desc");

  const filters = [
    { label: "All", value: "All" },
    { label: "Logins", value: "login" },
    { label: "Video Views", value: "video_view" },
    { label: "Alerts", value: "security_breach" },
  ];

  const filteredLogs = (actLog || []).filter(log => filter === "All" || log.action === filter);
  const sortedLogs = React.useMemo(() => {
    const list = [...(filteredLogs || [])];
    list.sort((a, b) => {
      let va, vb;
      if (sortBy === "user") { va = (a.user || "").toLowerCase(); vb = (b.user || "").toLowerCase(); return sortDir === "asc" ? (va > vb ? 1 : va < vb ? -1 : 0) : (vb > va ? 1 : vb < va ? -1 : 0); }
      if (sortBy === "action") { va = (a.action || "").toLowerCase(); vb = (b.action || "").toLowerCase(); return sortDir === "asc" ? (va > vb ? 1 : va < vb ? -1 : 0) : (vb > va ? 1 : vb < va ? -1 : 0); }
      va = new Date(a.timestamp || 0).getTime(); vb = new Date(b.timestamp || 0).getTime(); return sortDir === "asc" ? va - vb : vb - va;
    });
    return list;
  }, [filteredLogs, sortBy, sortDir]);

  const toggleSort = (col) => { setSortBy(col); setSortDir(s => s === "asc" && sortBy === col ? "desc" : sortBy === col ? (s === "asc" ? "desc" : "asc") : "desc"); };

  return (
    <div style={{ animation: "fadeIn 0.3s" }}>
      <div style={{ marginBottom: 26 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800 }}>Security Logs</h1>
        <p style={{ color: "var(--t2)", fontSize: 13, marginTop: 4 }}>Monitor all platform access, content consumption, and security events.</p>
      </div>

      <div style={{ display: "flex", gap: 6, marginBottom: 16, overflowX: "auto", paddingBottom: 5 }}>
        {filters.map(f => (
          <button key={f.value} onClick={() => setFilter(f.value)} style={{ padding: "6px 14px", borderRadius: 20, border: "1px solid", borderColor: filter === f.value ? "var(--gold)" : "var(--bdr)", background: filter === f.value ? "var(--gdim)" : "var(--bg3)", color: filter === f.value ? "var(--gold)" : "var(--t2)", fontSize: 11, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>{f.label}</button>
        ))}
      </div>

      {sortedLogs.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 16, padding: "10px 16px", background: "var(--bg2)", borderRadius: 10, marginBottom: 8, border: "1px solid var(--bdr)" }}>
          <button type="button" onClick={() => toggleSort("user")} style={{ flex: 1, textAlign: "left", background: "none", border: "none", color: "var(--t2)", fontSize: 10, fontWeight: 700, cursor: "pointer", textTransform: "uppercase", letterSpacing: "0.06em", display: "flex", alignItems: "center", gap: 4 }}>User {sortBy === "user" && (sortDir === "asc" ? "↑" : "↓")}</button>
          <button type="button" onClick={() => toggleSort("action")} style={{ width: 100, textAlign: "left", background: "none", border: "none", color: "var(--t2)", fontSize: 10, fontWeight: 700, cursor: "pointer", textTransform: "uppercase", letterSpacing: "0.06em", display: "flex", alignItems: "center", gap: 4 }}>Action {sortBy === "action" && (sortDir === "asc" ? "↑" : "↓")}</button>
          <button type="button" onClick={() => toggleSort("time")} style={{ width: 140, textAlign: "right", background: "none", border: "none", color: "var(--t2)", fontSize: 10, fontWeight: 700, cursor: "pointer", textTransform: "uppercase", letterSpacing: "0.06em", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4 }}>Time {sortBy === "time" && (sortDir === "asc" ? "↑" : "↓")}</button>
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {(!sortedLogs || sortedLogs.length === 0) && <Empty icon="🛡️" title="No Logs" sub="No matching security logs found." />}
        {sortedLogs.map(log => {
          const isBreach = log.action === "security_breach";
          return (
            <div key={log.id} style={{ ...card, padding: "12px 16px", display: "flex", alignItems: "center", gap: 16, borderLeft: isBreach ? "4px solid var(--red)" : "1px solid var(--bdr)" }}>
              <div style={{ fontSize: 20 }}>{isBreach ? "🚨" : log.action === "login" ? "🔐" : "▶️"}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: isBreach ? "var(--red)" : "var(--t1)" }}>
                  {log.user} <span style={{fontSize:10, color:"var(--t3)", fontWeight: 400}}>({formatLogIp(log.ip)})</span>
                </div>
                <div style={{ fontSize: 11, color: isBreach ? "var(--red)" : "var(--t2)", marginTop: 2 }}>
                  {isBreach ? `Security Alert: ${log.details}` : log.action === "login" ? "Logged into platform" : `Watched: ${log.videoTitle}`}
                </div>
              </div>
              <div style={{ fontSize: 11, color: "var(--t3)" }}>{new Date(log.timestamp).toLocaleString()}</div>
            </div>
          )
        })}
      </div>
    </div>
  );
}

// ─── PERFORMANCE DASHBOARD (TRADING JOURNALS) ──────────────────────────────
function TradingJournals({ breakdowns, setBreakdowns, isAdmin, user, students, propAccounts, onNav }) {
  const toast = useToast();
  const [showAdd, setShowAdd] = useState(false);
  const [editingTrade, setEditingTrade] = useState(null);
  const [expanded, setExpanded] = useState(null);
  // Use discordId as the stable identifier for viewing/owning trades
  const [viewingUser, setViewingUser] = useState(isAdmin ? null : user.discordId);
  const [currentMonth, setCurrentMonth] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [tradeSaving, setTradeSaving] = useState(false);

  // Helper: check if a trade belongs to a given user (by discordId).
  // Matches against ALL known identifiers for that user (discordId, name, username, legacy).
  const isTradeOwnedBy = (b, targetDiscordId) => {
    const tid = b.userId;
    if (!tid) return false; // No userId = unowned legacy trade
    // Direct discordId match (new format)
    if (tid === targetDiscordId) return true;
    // Build list of all known names/aliases for the target user
    const aliases = [];
    if (targetDiscordId === user.discordId) {
      if (user.username) aliases.push(user.username);
      if (user.name && user.name !== user.username) aliases.push(user.name);
    }
    const stu = students.find(s => s.discordId === targetDiscordId);
    if (stu) {
      if (stu.name) aliases.push(stu.name);
      if (stu.username) aliases.push(stu.username);
    }
    return aliases.includes(tid);
  };
  // Legacy admin trades (no userId or LEGACY_ADMIN_USER)
  const isLegacyAdminTrade = (b) => !b.userId || b.userId === LEGACY_ADMIN_USER;

  const save = async (d) => {
    setTradeSaving(true);
    try {
      const latest = await DB.get(SK.breakdowns) || [];
      const newTrade = { ...d, id: secureId(), userId: viewingUser };
      const u = [newTrade, ...latest];
      setBreakdowns(u);
      const ok = await DB.set(SK.breakdowns, u);
      if (!ok) {
        if (toast) toast("Failed to save trade to server. Please try again.", "error");
        setBreakdowns(latest);
        return;
      }
      setShowAdd(false);
      if (toast) toast("Trade logged!");
    } catch (e) {
      console.error("Trade save error:", e);
      if (toast) toast("Error saving trade. Please try again.", "error");
    } finally { setTradeSaving(false); }
  };
  const del = async (id) => {
    try {
      // Fetch latest breakdowns from server to prevent overwriting other users' trades
      const latest = await DB.get(SK.breakdowns) || [];
      const u = latest.filter(b => b.id !== id);
      setBreakdowns(u);
      const ok = await DB.set(SK.breakdowns, u);
      if (!ok) {
        if (toast) toast("Failed to delete trade from server.", "error");
        setBreakdowns(latest); // Revert on failure
        return;
      }
      if (toast) toast("Trade deleted.", "warn");
    } catch (e) {
      console.error("Trade delete error:", e);
      if (toast) toast("Error deleting trade.", "error");
    }
  };

  const editTrade = async (updatedFields) => {
    if (!editingTrade) return;
    setTradeSaving(true);
    try {
      const latest = await DB.get(SK.breakdowns) || [];
      const u = latest.map(b => b.id === editingTrade.id ? { ...b, ...updatedFields } : b);
      setBreakdowns(u);
      const ok = await DB.set(SK.breakdowns, u);
      if (!ok) {
        if (toast) toast("Failed to update trade.", "error");
        setBreakdowns(latest);
        return;
      }
      setEditingTrade(null);
      if (toast) toast("Trade updated!");
    } catch (e) {
      console.error("Trade edit error:", e);
      if (toast) toast("Error updating trade.", "error");
    } finally { setTradeSaving(false); }
  };

  const Form = ({ onClose, initial }) => {
    const [f, setF] = useState(initial || { ticker: "NQ", direction: "Long", contracts: "1", entry: "", exit: "", pnl: "", notes: "", date: new Date().toISOString().split("T")[0], image: null, propAccountIds: [] });
    const [err, setErr] = useState("");
    
    // Auto-calculate PNL / Result completely dynamically
    useEffect(() => {
      const ent = parseFloat(f.entry);
      const ext = parseFloat(f.exit);
      const qty = parseFloat(f.contracts) || 1;

      if (!isNaN(ent) && !isNaN(ext)) {
        const points = f.direction === "Long" ? ext - ent : ent - ext;
        let calculatedPnl = 0;
        
        if (f.ticker === "NQ") calculatedPnl = points * 20 * qty;
        else if (f.ticker === "ES") calculatedPnl = points * 50 * qty;
        else calculatedPnl = points * qty; // Raw Points equivalent for Other
        
        setF(prev => ({ ...prev, pnl: calculatedPnl.toFixed(2) }));
      }
    }, [f.entry, f.exit, f.contracts, f.direction, f.ticker]);

    const handleImg = e => { const file = e.target.files[0]; if(file){ const r = new FileReader(); r.onload=ev=>setF({...f, image: ev.target.result}); r.readAsDataURL(file); } };
    const handleSubmit = () => {
      if (!f.entry || !f.exit) {
        setErr("Entry and Exit prices are required.");
        return;
      }
      setErr("");
      isEdit ? editTrade(f) : save(f);
    };
    
    const isEdit = !!initial;
    return (<div style={mOverlay} onClick={onClose}><div style={mBox} onClick={e => e.stopPropagation()}>
      <h2 style={{ fontSize: 17, fontWeight: 700, marginBottom: 18 }}>{isEdit ? "Edit Trade" : "Add Trade"}</h2>
      
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
        <div>
          <label style={lbl}>Ticker</label>
          <select style={inp} value={f.ticker} onChange={e=>setF({...f,ticker:e.target.value})}>
            <option value="NQ">NQ ($20/pt)</option>
            <option value="ES">ES ($50/pt)</option>
            <option value="Other">Other / Manual</option>
          </select>
        </div>
        <div>
          <label style={lbl}>Direction</label>
          <select style={inp} value={f.direction} onChange={e=>setF({...f,direction:e.target.value})}>
            <option value="Long">Long</option>
            <option value="Short">Short</option>
          </select>
        </div>
        <div>
          <label style={lbl}># of Contracts</label>
          <input type="number" style={inp} value={f.contracts} onChange={e=>setF({...f,contracts:e.target.value})} placeholder="e.g. 1" />
        </div>
        <div>
          <label style={lbl}>Date</label>
          <input type="date" style={inp} value={f.date} onChange={e=>setF({...f,date:e.target.value})} />
        </div>
        <div>
          <label style={lbl}>Entry Price</label>
          <input type="number" step="0.25" style={inp} value={f.entry} onChange={e=>setF({...f,entry:e.target.value})} placeholder="e.g. 18050.25" />
        </div>
        <div>
          <label style={lbl}>Exit Price</label>
          <input type="number" step="0.25" style={inp} value={f.exit} onChange={e=>setF({...f,exit:e.target.value})} placeholder="e.g. 18060.00" />
        </div>
      </div>

      <div style={{ marginBottom: 10 }}>
         <label style={lbl}>Net PNL ($)</label>
         <input style={{...inp, color: parseFloat(f.pnl) >= 0 ? "var(--grn)" : "var(--red)", fontWeight: 700}} value={f.pnl} onChange={e=>setF({...f,pnl:e.target.value})} placeholder={f.ticker !== 'Other' ? "Auto-calculated..." : "Manual PNL"} />
      </div>

      <div style={{marginBottom:10}}><label style={lbl}>Notes / Setup Analysis</label><textarea style={{...inp, minHeight:60}} value={f.notes} onChange={e=>setF({...f,notes:e.target.value})} maxLength={1000} /><div style={{ fontSize: 10, color: (f.notes||"").length >= 900 ? "var(--red)" : "var(--t3)", marginTop: 4 }}>{(f.notes||"").length} / 1000</div></div>
      <div style={{marginBottom:10}}><label style={lbl}>Chart Image</label><input type="file" accept="image/*" onChange={handleImg} style={{fontSize:11, color:"var(--t2)"}} /></div>
      {f.image && <img src={f.image} style={{width:"100%", borderRadius:8, maxHeight:150, objectFit:"cover"}} alt="preview" />}
      {err && <div style={{ fontSize: 11, color: "var(--red)", marginTop: 6 }}>{err}</div>}
      {(() => {
        const activeAccts = (propAccounts || []).filter(a => a.status === "active" && a.userId === user.discordId);
        if (activeAccts.length === 0) return null;
        const allSelected = f.propAccountIds.length === activeAccts.length;
        const toggleAcct = (id) => {
          setF(prev => ({ ...prev, propAccountIds: prev.propAccountIds.includes(id) ? prev.propAccountIds.filter(x => x !== id) : [...prev.propAccountIds, id] }));
        };
        const toggleAll = () => {
          setF(prev => ({ ...prev, propAccountIds: allSelected ? [] : activeAccts.map(a => a.id) }));
        };
        return (
          <div style={{ marginBottom: 10, marginTop: 10 }}>
            <label style={lbl}>Prop Firm Accounts {f.propAccountIds.length > 0 && <span style={{ color: "var(--gold)", fontWeight: 700 }}>— Trading on {f.propAccountIds.length} account{f.propAccountIds.length !== 1 ? "s" : ""}</span>}</label>
            <div style={{ background: "var(--bg0)", border: "1px solid var(--bdr)", borderRadius: 9, padding: "8px 12px", display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 12, fontWeight: 700, color: "var(--gold)", paddingBottom: 4, borderBottom: "1px solid var(--bdr)" }}>
                <input type="checkbox" checked={allSelected} onChange={toggleAll} style={{ accentColor: "var(--gold)" }} /> Select All
              </label>
              {activeAccts.map(a => {
                const firm = PROP_FIRMS[a.firm];
                return (
                  <label key={a.id} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 12, color: "var(--t1)" }}>
                    <input type="checkbox" checked={f.propAccountIds.includes(a.id)} onChange={() => toggleAcct(a.id)} style={{ accentColor: "var(--gold)" }} />
                    {firm?.icon || ""} {a.accountLabel || `${firm?.name || a.firm} ${firm?.plans[a.plan]?.name || a.plan} ${a.accountSize/1000}k`}
                  </label>
                );
              })}
            </div>
          </div>
        );
      })()}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}><button onClick={onClose} style={btnO}>Cancel</button><LoadingButton loading={tradeSaving} onClick={handleSubmit} style={btnG}>{isEdit ? "Save Changes" : "Add Trade"}</LoadingButton></div>
    </div></div>);
  };

  // -- ADMIN DIRECTORY VIEW --
  if (isAdmin && !viewingUser) {
    // Build list: current admin first, then other admins, then students (deduped)
    const seen = new Set();
    const allUsers = [];
    // Current admin first
    seen.add(user.discordId);
    allUsers.push({ ...user, id: 'admin', role: 'admin', _isCurrentAdmin: true });
    // Other admins (from server-annotated student records)
    students.filter(s => s._serverAdmin && !seen.has(s.discordId)).forEach(s => {
      seen.add(s.discordId);
      allUsers.push({ ...s, role: 'admin' });
    });
    // Students (not already added)
    students.forEach(s => {
      if (seen.has(s.discordId)) return;
      seen.add(s.discordId);
      allUsers.push(s);
    });

    return (
      <div style={{ animation: "fadeIn 0.3s" }}>
        <div style={{ marginBottom: 26 }}>
          <h1 style={{ fontSize: 24, fontWeight: 800 }}>Student Journals</h1>
          <p style={{ color: "var(--t2)", fontSize: 13, marginTop: 4 }}>Review execution and advanced metrics across all students.</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: 12 }}>
          {allUsers.map((u, idx) => {
            const isCurrentAdmin = !!u._isCurrentAdmin;
            const isAdminRole = u.role === 'admin';
            const cardDiscordId = u.discordId;
            const userTrades = breakdowns.filter(b => {
              return isTradeOwnedBy(b, cardDiscordId) || (isCurrentAdmin && isLegacyAdminTrade(b));
            });
            const tPnl = userTrades.reduce((sum, b) => sum + safePnl(b.pnl), 0);

            return (
              <div key={u._isCurrentAdmin ? "admin-card" : u.discordId || u.id} onClick={() => setViewingUser(cardDiscordId)} className="nt-card-hover" style={{ ...card, cursor: "pointer", display: "flex", alignItems: "center", gap: 14, border: isAdminRole ? "1px solid var(--gbdr)" : "1px solid var(--bdr)" }}>
                <Avatar src={u.avatar} name={u.name} size={44} style={isAdminRole ? { border: "2px solid var(--gold)" } : {}} />
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{u.name} {isCurrentAdmin && <span style={{fontSize:9, color:"var(--gold)", fontWeight:700, marginLeft:4}}>YOU</span>}{isAdminRole && !isCurrentAdmin && <span style={{fontSize:9, color:"var(--gold)", fontWeight:700, marginLeft:4}}>ADMIN</span>}</div>
                  <div style={{ fontSize: 11, color: tPnl >= 0 ? "var(--grn)" : "var(--red)", marginTop: 2, fontWeight: 600 }}>
                     {tPnl >= 0 ? "+" : "-"}${Math.abs(tPnl).toLocaleString()} <span style={{color:"var(--t3)", fontWeight: 400}}>({userTrades.length} Trades)</span>
                  </div>
                </div>
                <div style={{ marginLeft: "auto", color: "var(--t3)", fontSize: 16 }}>→</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // -- INDIVIDUAL JOURNAL DASHBOARD --
  const myBreakdowns = breakdowns.filter(b => {
    return isTradeOwnedBy(b, viewingUser) || (isAdmin && viewingUser === user.discordId && isLegacyAdminTrade(b));
  });

  // Sort trades by date oldest to newest for charts
  const sortedBreakdowns = [...myBreakdowns].sort((a,b) => a.date.localeCompare(b.date));

  const canEdit = user.discordId === viewingUser;
  const canDelete = canEdit || isAdmin;
  const viewingName = viewingUser === user.discordId ? user.name : (students.find(s => s.discordId === viewingUser)?.name || viewingUser);

  // Stats Logic
  let totalPnl = 0, longPnl = 0, shortPnl = 0, grossProf = 0, grossLoss = 0, wins = 0;
  let cumPnlData = []; let currentCum = 0;
  let dailyPnlMap = {};

  const todayStr = new Date().toISOString().split("T")[0];
  let todayPnl = 0;

  sortedBreakdowns.forEach(b => {
    const p = safePnl(b.pnl);
    totalPnl += p;
    currentCum += p;
    cumPnlData.push(currentCum);

    if (b.direction === "Long") longPnl += p;
    if (b.direction === "Short") shortPnl += p;
    if (p > 0) grossProf += p;
    if (p < 0) grossLoss += Math.abs(p);
    if (p > 0) wins++; // strict logic

    if (b.date === todayStr) {
      todayPnl += p;
    }

    dailyPnlMap[b.date] = (dailyPnlMap[b.date] || 0) + p;
  });
  
  const profitFactor = grossLoss === 0 ? (grossProf > 0 ? "MAX" : "0.00") : (grossProf / grossLoss).toFixed(2);
  const winPct = myBreakdowns.length > 0 ? Math.round((wins / myBreakdowns.length) * 100) : 0;
  
  const formatAxis = (num) => {
    if (num === 0) return "0";
    const abs = Math.abs(num);
    return (num < 0 ? "-" : "") + (abs >= 1000 ? (abs/1000).toFixed(1) + "K" : Math.round(abs));
  };

  // SVG Chart Generators
  const renderCumLineChart = () => {
    if (cumPnlData.length < 2) return <div style={{color:"var(--t3)", fontSize: 11, margin:"auto"}}>Not enough data</div>;
    const max = Math.max(...cumPnlData, 1);
    const min = Math.min(...cumPnlData, 0);
    const range = (max - min) || 1;
    
    // Scale to fit nicely between 5 and 95 to prevent line stroke overflow
    const pts = cumPnlData.map((d, i) => {
      const x = (i / (cumPnlData.length - 1)) * 96 + 2;
      const y = 95 - ((d - min) / range) * 90;
      return `${x},${y}`;
    }).join(" ");

    return (
      <div style={{ display: "flex", width: "100%", minHeight: 80, maxHeight: 100, marginTop: 10 }}>
        <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", fontSize: 10, color: "var(--t3)", paddingRight: 8, textAlign: "right", minWidth: 40 }}>
          <span>{formatAxis(max)}</span>
          <span>{formatAxis(min + range/2)}</span>
          <span>{formatAxis(min)}</span>
        </div>
        <svg viewBox="0 0 100 100" width="100%" height="100%" preserveAspectRatio="none" style={{overflow:"visible", flex: 1}}>
          <defs><linearGradient id="gCum" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="var(--blu)" stopOpacity="0.4"/><stop offset="100%" stopColor="var(--blu)" stopOpacity="0"/></linearGradient></defs>
          <polygon points={`2,100 ${pts} 98,100`} fill="url(#gCum)" />
          <polyline points={pts} fill="none" stroke="var(--blu)" strokeWidth="2" vectorEffect="non-scaling-stroke" />
        </svg>
      </div>
    );
  };

  const renderDonutChart = () => {
    if (myBreakdowns.length === 0) return <div style={{color:"var(--t3)", fontSize: 11, margin:"auto"}}>Not enough data</div>;
    const radius = 38;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (winPct / 100) * circumference;

    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1, marginTop: 10 }}>
        <div style={{ position: "relative", width: 120, height: 120 }}>
          <svg viewBox="0 0 100 100" width="100%" height="100%" style={{ transform: "rotate(-90deg)", overflow: "visible" }}>
            <circle cx="50" cy="50" r={radius} fill="none" stroke="var(--bg4)" strokeWidth="12" />
            <circle cx="50" cy="50" r={radius} fill="none" stroke={winPct >= 50 ? "var(--grn)" : "var(--red)"} strokeWidth="12" 
                    strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round" 
                    style={{ transition: "stroke-dashoffset 1s ease-in-out" }} />
          </svg>
          <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontSize: 24, fontWeight: 800, color: "var(--t1)", fontFamily: "'JetBrains Mono'" }}>{winPct}%</span>
            <span style={{ fontSize: 9, color: "var(--t3)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginTop: 2 }}>Win Rate</span>
          </div>
        </div>
        <div style={{ display: "flex", gap: 16, marginTop: 16, fontSize: 11, fontWeight: 600 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}><div style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--grn)" }} /> {wins} Wins</div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}><div style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--red)" }} /> {myBreakdowns.length - wins} Losses</div>
        </div>
      </div>
    );
  };

  // Calendar Logic
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const firstDayOfWeek = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const blankDays = Array.from({ length: firstDayOfWeek });
  const monthDays = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

  // Filter trades for the selected month to show under calendar
  const currentMonthTrades = [...myBreakdowns].filter(b => b.date.startsWith(`${year}-${String(month + 1).padStart(2, '0')}`)).sort((a,b)=>b.date.localeCompare(a.date));

  return (
    <div style={{ animation: "fadeIn 0.3s" }}>
      {isAdmin && (
        <button onClick={() => setViewingUser(null)} style={{ background: "none", border: "none", color: "var(--gold)", fontSize: 12, cursor: "pointer", marginBottom: 16, fontWeight: 600 }}>← Back to Directory</button>
      )}
      
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 20 }}>
        <div><h1 style={{ fontSize: 22, fontWeight: 800 }}>{isAdmin ? `${viewingName}'s Journal` : "My Trading Journal"}</h1></div>
        {canEdit && <button onClick={() => setShowAdd(true)} style={{...btnG, padding: "8px 16px", fontSize: 12}}>+ Add Trade</button>}
      </div>

      {/* TOP DASHBOARD METRICS */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginBottom: 16 }}>
        
        <div style={{ ...card, padding: "20px 24px", background: "linear-gradient(145deg, var(--bg2), var(--bg3))" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
            <div style={{ fontSize: 11, color: "var(--t3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Long vs Short</div>
            <div style={{ textAlign: "right", fontSize: 11, fontFamily: "'JetBrains Mono'", fontWeight: 600 }}>
              <div style={{ color: "var(--grn)", marginBottom: 2 }}>L +${longPnl.toLocaleString()}</div>
              <div style={{ color: "var(--red)" }}>S {shortPnl < 0 ? "" : "-"}${Math.abs(shortPnl).toLocaleString()}</div>
            </div>
          </div>
          <span style={{ fontSize: 28, fontWeight: 800, color: totalPnl >= 0 ? "var(--grn)" : "var(--red)", fontFamily: "'JetBrains Mono'" }}>
            {totalPnl >= 0 ? "+" : "-"}${Math.abs(totalPnl).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
          </span>
          <div style={{ fontSize: 11, color: "var(--t2)", marginTop: 6 }}>Total PNL</div>
        </div>

        <div style={{ ...card, padding: "20px 24px", background: "linear-gradient(145deg, var(--bg2), var(--bg3))" }}>
          <div style={{ fontSize: 11, color: "var(--t3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Total Trades</div>
          <span style={{ fontSize: 28, fontWeight: 800, color: "var(--blu)", fontFamily: "'JetBrains Mono'" }}>
            {myBreakdowns.length}
          </span>
          <div style={{ fontSize: 11, color: "var(--t2)", marginTop: 6 }}>Executed</div>
        </div>

        <div style={{ ...card, padding: "20px 24px", background: "linear-gradient(145deg, var(--bg2), var(--bg3))" }}>
          <div style={{ fontSize: 11, color: "var(--t3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Profit Factor</div>
          <span style={{ fontSize: 28, fontWeight: 800, color: profitFactor === "MAX" ? "var(--grn)" : parseFloat(profitFactor) >= 1.5 ? "var(--grn)" : parseFloat(profitFactor) >= 1 ? "var(--gold)" : "var(--red)", fontFamily: "'JetBrains Mono'" }}>
            {profitFactor}
          </span>
          <div style={{ fontSize: 11, color: "var(--t2)", marginTop: 6 }}>Win/Loss Ratio</div>
        </div>

        <div style={{ ...card, padding: "20px 24px", background: "linear-gradient(145deg, var(--bg2), var(--bg3))" }}>
          <div style={{ fontSize: 11, color: "var(--t3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Today's PNL</div>
          <span style={{ fontSize: 28, fontWeight: 800, color: todayPnl >= 0 ? "var(--grn)" : "var(--red)", fontFamily: "'JetBrains Mono'" }}>
            {todayPnl >= 0 ? "+" : "-"}${Math.abs(todayPnl).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
          </span>
          <div style={{ fontSize: 11, color: "var(--t2)", marginTop: 6 }}>Daily Return</div>
        </div>

      </div>

      {/* PAYOUT PROGRESS SECTION */}
      {(() => {
        const myAccounts = (propAccounts || []).filter(a => a.status === "active" && a.userId === viewingUser);
        if (myAccounts.length === 0) return (
          <div style={{ ...card, marginBottom: 16, padding: "16px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", border: "1px dashed var(--bdr)" }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--t2)" }}>No Prop Firm Accounts</div>
              <div style={{ fontSize: 11, color: "var(--t3)", marginTop: 2 }}>Add accounts to track payout progress</div>
            </div>
            {canEdit && onNav && <button onClick={() => onNav("propfirms")} style={{ ...btnO, fontSize: 11 }}>Manage Accounts</button>}
          </div>
        );
        return (
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ color: "var(--gold)" }}>💰</span> Payout Progress
              </h3>
              {canEdit && onNav && <button onClick={() => onNav("propfirms")} style={{ background: "none", border: "none", color: "var(--gold)", fontSize: 11, cursor: "pointer", fontWeight: 600 }}>Manage Accounts →</button>}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
              {myAccounts.map(acct => {
                const status = computePropStatus(acct, breakdowns);
                return (
                  <div key={acct.id} style={{ ...card, padding: "16px 18px", border: status.eligible ? "1px solid var(--grn)" : "1px solid var(--bdr)", background: status.eligible ? "rgba(34,197,94,0.04)" : "var(--bg2)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 18 }}>{status.firmIcon}</span>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 700 }}>{status.firmName} {status.planName}</div>
                          <div style={{ fontSize: 10, color: "var(--t3)" }}>{(acct.accountSize/1000)}k Account</div>
                        </div>
                      </div>
                      {status.eligible && <span style={{ fontSize: 9, fontWeight: 800, color: "var(--grn)", background: "rgba(34,197,94,0.1)", padding: "3px 8px", borderRadius: 6, textTransform: "uppercase" }}>Eligible</span>}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {status.checks.map((c, ci) => {
                        const pct = c.target > 0 ? Math.min(100, Math.round((c.current / c.target) * 100)) : (c.met ? 100 : 0);
                        return (
                          <div key={ci}>
                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "var(--t3)", marginBottom: 3 }}>
                              <span>{c.label}</span>
                              <span style={{ fontWeight: 700, color: c.met ? "var(--grn)" : "var(--gold)", fontFamily: "'JetBrains Mono'" }}>
                                {c.isCurrency ? "$" : ""}{typeof c.current === "number" ? c.current.toLocaleString() : c.current}{c.suffix ? ` ${c.suffix}` : ""} / {c.isCurrency ? "$" : ""}{typeof c.target === "number" ? c.target.toLocaleString() : c.target}{c.suffix ? ` ${c.suffix}` : ""}
                              </span>
                            </div>
                            <div style={{ height: 5, background: "var(--bg0)", borderRadius: 3, overflow: "hidden" }}>
                              <div style={{ height: "100%", width: `${pct}%`, background: c.met ? "var(--grn)" : "var(--gold)", borderRadius: 3, transition: "width 0.4s ease" }} />
                            </div>
                            {c.isConsistency && c.highestDay > 0 && (
                              <div style={{ fontSize: 9, color: "var(--t3)", marginTop: 2 }}>
                                Highest day: ${c.highestDay.toLocaleString()} → Target: ${c.target.toLocaleString()}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {/* Summary line */}
                    <div style={{ marginTop: 10, paddingTop: 8, borderTop: "1px solid var(--bdr)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ fontSize: 11, fontWeight: 600 }}>
                        {status.eligible ? (
                          <span style={{ color: "var(--grn)" }}>Payout available: ${status.availablePayout.toLocaleString()}</span>
                        ) : (
                          <span style={{ color: "var(--t3)" }}>
                            {status.winningDaysRemaining != null && status.winningDaysRemaining > 0 && `${status.winningDaysRemaining} winning day${status.winningDaysRemaining !== 1 ? "s" : ""} remaining`}
                            {status.winningDaysRemaining != null && status.winningDaysRemaining > 0 && status.calendarDaysRemaining != null && status.calendarDaysRemaining > 0 && " · "}
                            {status.calendarDaysRemaining != null && status.calendarDaysRemaining > 0 && `${status.calendarDaysRemaining} day${status.calendarDaysRemaining !== 1 ? "s" : ""} until cycle`}
                            {(status.winningDaysRemaining == null || status.winningDaysRemaining === 0) && (status.calendarDaysRemaining == null || status.calendarDaysRemaining === 0) && !status.eligible && "Awaiting requirements"}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 10, color: "var(--t3)" }}>Split: {Math.round(status.profitSplit * 100)}%</div>
                    </div>
                    {status.fullPayoutDaysRemaining != null && (
                      <div style={{ fontSize: 9, color: "var(--blu)", marginTop: 4 }}>
                        {status.fullPayoutUnlocked ? "100% payout unlocked" : `${status.fullPayoutDaysRemaining} winning days to unlock 100% payout`}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* DASHBOARD SPLIT VIEW (Charts Left, Calendar Right) */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginBottom: 24 }}>
        
        {/* LEFT COLUMN: CHARTS */}
        <div style={{ flex: "2 1 400px", minWidth: 0, display: "flex", flexDirection: "column", gap: 16 }}>
          
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
             {/* Score Radar Equivalent */}
             <div style={{ ...card, flex: 1, padding: "20px 24px", display: "flex", flexDirection: "column", background: "var(--bg2)" }}>
                <div style={{ fontSize: 11, color: "var(--t3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 16 }}>Performance Score</div>
                <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
                   <svg width="100" height="100" viewBox="0 0 100 100">
                     <polygon points="50,10 90,80 10,80" fill="none" stroke="var(--bdr2)" strokeWidth="1" />
                     <polygon points="50,30 75,70 25,70" fill="none" stroke="var(--bdr2)" strokeWidth="1" />
                     <polygon points={`50,${10 + (100-winPct)*0.4} ${50 + Math.min(40, profitFactor*10)},${80 - Math.min(20, profitFactor*5)} ${10 + (100-winPct)*0.4},70`} fill="var(--blu-d)" stroke="var(--blu)" strokeWidth="2" />
                   </svg>
                   <div style={{position:"absolute", top:0, fontSize:9, color:"var(--t3)"}}>Win %</div>
                   <div style={{position:"absolute", bottom:0, right:0, fontSize:9, color:"var(--t3)"}}>Profit Factor</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 16 }}>
                  <span style={{ fontSize: 24, fontWeight: 800, color: "var(--grn)", fontFamily: "'JetBrains Mono'" }}>{Math.min(99, Math.max(1, Math.round(winPct * (profitFactor/1.5))))}</span>
                  <span style={{ fontSize: 11, color: "var(--t3)" }}>/ 100</span>
                </div>
             </div>

             {/* Win Rate Donut */}
             <div style={{ ...card, flex: 1, padding: "20px 24px", display: "flex", flexDirection: "column", background: "var(--bg2)" }}>
                <div style={{ fontSize: 11, color: "var(--t3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Win Rate Overview</div>
                <div style={{ flex: 1, display: "flex" }}>{renderDonutChart()}</div>
             </div>
          </div>

          {/* Cum Line Full Width under charts */}
          <div style={{ ...card, padding: "14px 20px", display: "flex", flexDirection: "column", background: "var(--bg2)" }}>
             <div style={{ fontSize: 11, color: "var(--t3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Daily Cumulative PNL</div>
             <div style={{ display: "flex" }}>{renderCumLineChart()}</div>
          </div>
        </div>

        {/* RIGHT COLUMN: PERFORMANCE CALENDAR */}
        <div style={{ flex: "1 1 320px", minWidth: 0, ...card, padding: "20px", display: "flex", flexDirection: "column" }}>
          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", marginBottom: 16, gap: 10 }}>
            <h2 style={{ fontSize: 15, fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}><span style={{color:"var(--blu)"}}>📅</span> Performance Calendar</h2>
            <div style={{ display: "flex", alignItems: "center", gap: 10, background: "var(--bg2)", padding: "4px 8px", borderRadius: 8, border: "1px solid var(--bdr)" }}>
              <button onClick={() => setCurrentMonth(new Date(year, month - 1, 1))} style={{ background: "none", border: "none", color: "var(--t2)", cursor: "pointer", fontSize: 12 }}>◀</button>
              <span style={{ fontSize: 12, fontWeight: 600, minWidth: 70, textAlign: "center" }}>{monthNames[month].substring(0,3)} {year}</span>
              <button onClick={() => setCurrentMonth(new Date(year, month + 1, 1))} style={{ background: "none", border: "none", color: "var(--t2)", cursor: "pointer", fontSize: 12 }}>▶</button>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: 4, flex: 1 }}>
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(d => (
              <div key={d} style={{ textAlign: "center", fontSize: 10, fontWeight: 700, color: "var(--t3)", paddingBottom: 4 }}>{d}</div>
            ))}
            
            {blankDays.map((_, i) => <div key={`blank-${i}`} style={{ aspectRatio: "1.3", background: "transparent" }} />)}
            
            {monthDays.map(day => {
              const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              const dayTrades = myBreakdowns.filter(b => b.date === dateStr);
              
              let netPnl = 0;

              dayTrades.forEach(t => {
                const p = safePnl(t.pnl);
                netPnl += p;
              });

              const isGreen = netPnl > 0;
              const isRed = netPnl < 0;
              
              return (
                <div key={day} style={{ 
                  aspectRatio: "1", 
                  background: isGreen ? "var(--grn-d)" : isRed ? "var(--red-d)" : "var(--bg2)", 
                  borderRadius: 6, 
                  border: "1px solid", 
                  borderColor: isGreen ? "rgba(34,197,94,0.3)" : isRed ? "rgba(239,68,68,0.3)" : "var(--bdr)",
                  padding: "4px",
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                  overflow: "hidden"
                }}>
                  <span style={{ fontSize: 10, color: isGreen || isRed ? "var(--t1)" : "var(--t3)", fontWeight: 600 }}>{day}</span>
                  {dayTrades.length > 0 && (
                    <div style={{ margin: "auto", textAlign: "center", width: "100%" }}>
                      <div style={{ fontSize: 10, fontWeight: 800, color: isGreen ? "var(--grn)" : isRed ? "var(--red)" : "var(--t1)", fontFamily: "'JetBrains Mono'", letterSpacing: "-0.5px" }}>
                        {netPnl > 0 ? "+" : ""}${netPnl >= 1000 || netPnl <= -1000 ? (netPnl/1000).toFixed(1)+'K' : netPnl.toLocaleString()}
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>

      </div>

      {/* MONTHLY TRADES LIST */}
      {currentMonthTrades.length > 0 && (
        <div>
          <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: "var(--t2)" }}>Trades for {monthNames[month]} {year}</h3>
          {currentMonthTrades.map(b => (
            <div key={b.id} style={{ ...card, marginBottom: 8, padding: 0, borderLeft: `4px solid ${safePnl(b.pnl) >= 0 ? "var(--grn)" : "var(--red)"}` }}>
              <div onClick={()=>setExpanded(expanded===b.id?null:b.id)} style={{ padding: "12px 16px", display: "flex", justifyContent: "space-between", cursor: "pointer", alignItems: "center" }}>
                
                <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "var(--t3)", fontFamily: "'JetBrains Mono'" }}>{b.date.slice(5)}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontWeight: 800, fontFamily: "'JetBrains Mono'", fontSize: 14 }}>{b.ticker}</span>
                    <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, background: "var(--bg4)", color: b.direction === "Long" ? "var(--grn)" : "var(--red)", fontWeight: 700 }}>{b.direction}</span>
                    <span style={{ fontSize: 11, color: "var(--t2)" }}>x{b.contracts || 1}</span>
                  </div>
                  <span style={{ fontSize: 12, color: "var(--t3)", fontFamily: "'JetBrains Mono'" }}>{b.entry} → {b.exit}</span>
                  {(b.propAccountIds?.length > 0) && <span style={{ fontSize: 8, padding: "2px 6px", borderRadius: 4, background: "var(--gdim)", color: "var(--gold)", fontWeight: 700 }}>💰 {b.propAccountIds.length} acct{b.propAccountIds.length !== 1 ? "s" : ""}</span>}
                </div>

                <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
                  <span style={{ fontWeight: 800, fontSize: 15, color: safePnl(b.pnl) >= 0 ? "var(--grn)" : "var(--red)", fontFamily: "'JetBrains Mono'" }}>
                    {safePnl(b.pnl) > 0 ? "+" : ""}${safePnl(b.pnl).toLocaleString()}
                  </span>
                  {canEdit && <button onClick={e=>{e.stopPropagation(); setEditingTrade(b)}} style={{background:"none",border:"none",color:"var(--t3)",cursor:"pointer", fontSize: 12, transition:"color 0.15s"}}>✏️</button>}
                  {canDelete && <button onClick={e=>{e.stopPropagation(); del(b.id)}} style={{background:"none",border:"none",color:"var(--close-btn)",cursor:"pointer", fontSize: 16, fontWeight: 800, transition:"color 0.15s", opacity:0.7}}>✕</button>}
                </div>
              </div>
              {expanded === b.id && (
                <div style={{ padding: "0 16px 16px", borderTop: "1px solid var(--bdr)", marginTop: 4, paddingTop: 12 }}>
                  <p style={{ fontSize: 13, color: "var(--t2)", lineHeight: 1.6, marginBottom: b.image ? 12 : 0 }}>{b.notes || "No setup analysis provided."}</p>
                  {b.image && <img src={b.image} style={{ width: "100%", maxWidth: 600, borderRadius: 8, border: "1px solid var(--bdr)" }} alt="chart" />}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showAdd && <Form onClose={()=>setShowAdd(false)} />}
      {editingTrade && <Form initial={editingTrade} onClose={()=>setEditingTrade(null)} />}
    </div>
  );
}

// ─── COMMUNITY JOURNALS (FRIEND REQUEST SYSTEM) ────────────────────────────
function CommunityJournals({ breakdowns, students, user, isAdmin, setStudents, friendRequests, setFriendRequests }) {
  const toast = useToast();
  const [viewingMember, setViewingMember] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [tab, setTab] = useState("friends"); // "friends" | "requests" | "find" | "leaderboard"
  const [calMonth, setCalMonth] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));

  const myId = user.discordId;
  const myIsAdmin = isAdmin;

  // Build a master list of ALL platform members (both admins + students, deduped)
  const allMembers = useMemo(() => {
    const seen = new Set();
    const list = [];
    if (user.role === 'admin') {
      seen.add(myId);
      list.push({ discordId: myId, name: user.name, username: user.username || user.name, avatar: user.avatar, _isAdmin: true, _isMe: true });
    }
    students.filter(s => s._serverAdmin).forEach(s => {
      if (seen.has(s.discordId)) return;
      seen.add(s.discordId);
      list.push({ ...s, _isAdmin: true, _isMe: s.discordId === myId });
    });
    students.forEach(s => {
      if (seen.has(s.discordId)) return;
      seen.add(s.discordId);
      list.push({ ...s, _isAdmin: false, _isMe: s.discordId === myId });
    });
    return list;
  }, [students, myId, user.name, user.username, user.role, user.avatar]);

  // ---- FRIEND REQUEST HELPERS ----
  const getFriendship = (otherId) => {
    return (friendRequests || []).find(fr =>
      (fr.from === myId && fr.to === otherId) || (fr.from === otherId && fr.to === myId)
    );
  };

  const sendRequest = async (toId) => {
    const existing = getFriendship(toId);
    if (existing) return;
    const latest = await DB.get(SK.friendRequests) || [];
    const req = { id: secureId(), from: myId, fromName: user.name, to: toId, toName: allMembers.find(m => m.discordId === toId)?.name || "Unknown", status: "pending", date: new Date().toISOString() };
    const updated = [req, ...latest];
    setFriendRequests(updated);
    await DB.set(SK.friendRequests, updated);
    if (toast) toast("Friend request sent!");
  };

  const acceptRequest = async (frId) => {
    const latest = await DB.get(SK.friendRequests) || [];
    const updated = latest.map(fr => fr.id === frId ? { ...fr, status: "accepted" } : fr);
    setFriendRequests(updated);
    await DB.set(SK.friendRequests, updated);
    if (toast) toast("Friend request accepted!");
  };

  const rejectRequest = async (frId) => {
    const latest = await DB.get(SK.friendRequests) || [];
    const updated = latest.filter(fr => fr.id !== frId);
    setFriendRequests(updated);
    await DB.set(SK.friendRequests, updated);
    if (toast) toast("Request removed.", "warn");
  };

  const removeFriend = async (otherId) => {
    const latest = await DB.get(SK.friendRequests) || [];
    const updated = latest.filter(fr =>
      !((fr.from === myId && fr.to === otherId) || (fr.from === otherId && fr.to === myId))
    );
    setFriendRequests(updated);
    await DB.set(SK.friendRequests, updated);
    if (toast) toast("Friend removed.", "warn");
  };

  // Accepted friends list (discord IDs that are mutual)
  const myFriendIds = useMemo(() => {
    return (friendRequests || [])
      .filter(fr => fr.status === "accepted" && (fr.from === myId || fr.to === myId))
      .map(fr => fr.from === myId ? fr.to : fr.from);
  }, [friendRequests, myId]);

  // Pending requests TO me
  const incomingRequests = useMemo(() => {
    return (friendRequests || []).filter(fr => fr.to === myId && fr.status === "pending");
  }, [friendRequests, myId]);

  // Pending requests FROM me
  const outgoingRequests = useMemo(() => {
    return (friendRequests || []).filter(fr => fr.from === myId && fr.status === "pending");
  }, [friendRequests, myId]);

  // People I can view: my friends + (if admin, everyone)
  const canView = (otherId) => {
    if (otherId === myId) return true;
    if (myIsAdmin) return true;
    return myFriendIds.includes(otherId);
  };

  // Helper: get trades for a member (match by discordId + all known name aliases)
  const getTradesFor = (member) => {
    const did = member.discordId;
    const aliases = [member.name, member.username].filter(Boolean);
    // Also include names from the student record if available
    const stu = students.find(s => s.discordId === did);
    if (stu) {
      if (stu.name && !aliases.includes(stu.name)) aliases.push(stu.name);
      if (stu.username && !aliases.includes(stu.username)) aliases.push(stu.username);
    }
    return breakdowns.filter(b => {
      const tid = b.userId;
      if (!tid) return member._isAdmin; // unowned = legacy admin
      if (tid === did) return true;
      if (aliases.includes(tid)) return true;
      if (member._isAdmin && tid === LEGACY_ADMIN_USER) return true;
      return false;
    });
  };

  // ---- VIEWING A MEMBER'S JOURNAL ----
  if (viewingMember) {
    const memberTrades = getTradesFor(viewingMember);
    const sortedByDate = [...memberTrades].sort((a,b) => a.date.localeCompare(b.date));
    let mTotalPnl = 0, mLongPnl = 0, mShortPnl = 0, mGrossProf = 0, mGrossLoss = 0, mWins = 0;
    let mCumData = []; let mCum = 0;
    let mDailyMap = {};
    const mTodayStr = new Date().toISOString().split("T")[0];
    let mTodayPnl = 0;

    sortedByDate.forEach(b => {
      const p = safePnl(b.pnl);
      mTotalPnl += p; mCum += p; mCumData.push(mCum);
      if (b.direction === "Long") mLongPnl += p;
      if (b.direction === "Short") mShortPnl += p;
      if (p > 0) { mGrossProf += p; mWins++; }
      if (p < 0) mGrossLoss += Math.abs(p);
      if (b.date === mTodayStr) mTodayPnl += p;
      mDailyMap[b.date] = (mDailyMap[b.date] || 0) + p;
    });

    const mPF = mGrossLoss === 0 ? (mGrossProf > 0 ? "MAX" : "0.00") : (mGrossProf / mGrossLoss).toFixed(2);
    const mWinPct = memberTrades.length > 0 ? Math.round((mWins / memberTrades.length) * 100) : 0;
    const fmtAx = (num) => { if (num === 0) return "0"; const abs = Math.abs(num); return (num < 0 ? "-" : "") + (abs >= 1000 ? (abs/1000).toFixed(1) + "K" : Math.round(abs)); };

    // Calendar
    const cY = calMonth.getFullYear();
    const cM = calMonth.getMonth();
    const cFirstDay = new Date(cY, cM, 1).getDay();
    const cDaysInMonth = new Date(cY, cM + 1, 0).getDate();
    const cBlanks = Array.from({ length: cFirstDay });
    const cMonthDays = Array.from({ length: cDaysInMonth }, (_, i) => i + 1);
    const mNames = ["January","February","March","April","May","June","July","August","September","October","November","December"];
    const cMonthTrades = [...memberTrades].filter(b => b.date.startsWith(`${cY}-${String(cM+1).padStart(2,'0')}`)).sort((a,b)=>b.date.localeCompare(a.date));

    return (
      <div style={{ animation: "fadeIn 0.3s" }}>
        <button onClick={() => { setViewingMember(null); setCalMonth(new Date(new Date().getFullYear(), new Date().getMonth(), 1)); }} style={{ background: "none", border: "none", color: "var(--gold)", fontSize: 12, cursor: "pointer", marginBottom: 14, fontWeight: 600 }}>← Back to Community</button>
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
          <div style={{ width: 48, height: 48, borderRadius: 12, background: viewingMember._isAdmin ? "var(--gdim)" : "var(--bg4)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 700, color: viewingMember._isAdmin ? "var(--gold)" : "var(--t1)" }}>{viewingMember.name ? viewingMember.name[0] : "?"}</div>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 800 }}>{viewingMember.name}'s Journal {viewingMember._isAdmin && <span style={{fontSize:11, color:"var(--gold)", fontWeight:700}}>ADMIN</span>}</h1>
            <p style={{ color: "var(--t2)", fontSize: 12 }}>Read-only view</p>
          </div>
        </div>

        {/* TOP METRICS */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 16 }}>
          <div style={{ ...card, padding: "20px 24px", background: "linear-gradient(145deg, var(--bg2), var(--bg3))" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
              <div style={{ fontSize: 11, color: "var(--t3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Long vs Short</div>
              <div style={{ textAlign: "right", fontSize: 11, fontFamily: "'JetBrains Mono'", fontWeight: 600 }}>
                <div style={{ color: "var(--grn)", marginBottom: 2 }}>L +${mLongPnl.toLocaleString()}</div>
                <div style={{ color: "var(--red)" }}>S {mShortPnl < 0 ? "" : "-"}${Math.abs(mShortPnl).toLocaleString()}</div>
              </div>
            </div>
            <span style={{ fontSize: 28, fontWeight: 800, color: mTotalPnl >= 0 ? "var(--grn)" : "var(--red)", fontFamily: "'JetBrains Mono'" }}>
              {mTotalPnl >= 0 ? "+" : "-"}${Math.abs(mTotalPnl).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
            </span>
            <div style={{ fontSize: 11, color: "var(--t2)", marginTop: 6 }}>Total PNL</div>
          </div>
          <div style={{ ...card, padding: "20px 24px", background: "linear-gradient(145deg, var(--bg2), var(--bg3))" }}>
            <div style={{ fontSize: 11, color: "var(--t3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Total Trades</div>
            <span style={{ fontSize: 28, fontWeight: 800, color: "var(--blu)", fontFamily: "'JetBrains Mono'" }}>{memberTrades.length}</span>
            <div style={{ fontSize: 11, color: "var(--t2)", marginTop: 6 }}>Executed</div>
          </div>
          <div style={{ ...card, padding: "20px 24px", background: "linear-gradient(145deg, var(--bg2), var(--bg3))" }}>
            <div style={{ fontSize: 11, color: "var(--t3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Profit Factor</div>
            <span style={{ fontSize: 28, fontWeight: 800, color: mPF === "MAX" ? "var(--grn)" : parseFloat(mPF) >= 1.5 ? "var(--grn)" : parseFloat(mPF) >= 1 ? "var(--gold)" : "var(--red)", fontFamily: "'JetBrains Mono'" }}>{mPF}</span>
            <div style={{ fontSize: 11, color: "var(--t2)", marginTop: 6 }}>Win/Loss Ratio</div>
          </div>
          <div style={{ ...card, padding: "20px 24px", background: "linear-gradient(145deg, var(--bg2), var(--bg3))" }}>
            <div style={{ fontSize: 11, color: "var(--t3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Today's PNL</div>
            <span style={{ fontSize: 28, fontWeight: 800, color: mTodayPnl >= 0 ? "var(--grn)" : "var(--red)", fontFamily: "'JetBrains Mono'" }}>
              {mTodayPnl >= 0 ? "+" : "-"}${Math.abs(mTodayPnl).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
            </span>
            <div style={{ fontSize: 11, color: "var(--t2)", marginTop: 6 }}>Daily Return</div>
          </div>
        </div>

        {/* CHARTS & CALENDAR */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginBottom: 24 }}>
          <div style={{ flex: "2 1 400px", minWidth: 0, display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              {/* Win Rate Donut */}
              <div style={{ ...card, flex: 1, padding: "20px 24px", display: "flex", flexDirection: "column", background: "var(--bg2)" }}>
                <div style={{ fontSize: 11, color: "var(--t3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Win Rate Overview</div>
                <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", marginTop: 10 }}>
                  <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <svg width="100" height="100" viewBox="0 0 100 100">
                      <circle cx="50" cy="50" r="40" fill="none" stroke="var(--bg4)" strokeWidth="12" />
                      <circle cx="50" cy="50" r="40" fill="none" stroke="var(--grn)" strokeWidth="12"
                        strokeDasharray={`${mWinPct * 2.51} ${251.2 - mWinPct * 2.51}`}
                        strokeDashoffset="62.8" strokeLinecap="round" />
                    </svg>
                    <div style={{ position: "absolute", display: "flex", flexDirection: "column", alignItems: "center" }}>
                      <span style={{ fontSize: 20, fontWeight: 800, fontFamily: "'JetBrains Mono'" }}>{mWinPct}%</span>
                      <span style={{ fontSize: 8, color: "var(--t3)", fontWeight: 700, textTransform: "uppercase" }}>Win Rate</span>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 16, marginTop: 12, fontSize: 11, fontWeight: 600 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}><div style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--grn)" }} /> {mWins} Wins</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}><div style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--red)" }} /> {memberTrades.length - mWins} Losses</div>
                  </div>
                </div>
              </div>
              {/* Performance Score */}
              <div style={{ ...card, flex: 1, padding: "20px 24px", display: "flex", flexDirection: "column", background: "var(--bg2)" }}>
                <div style={{ fontSize: 11, color: "var(--t3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 16 }}>Performance Score</div>
                <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <span style={{ fontSize: 36, fontWeight: 800, color: "var(--grn)", fontFamily: "'JetBrains Mono'" }}>{Math.min(99, Math.max(0, Math.round(mWinPct * (parseFloat(mPF) || 0) / 1.5)))}</span>
                  <span style={{ fontSize: 14, color: "var(--t3)", marginLeft: 4 }}>/ 100</span>
                </div>
              </div>
            </div>
            {/* Cumulative PNL */}
            <div style={{ ...card, padding: "14px 20px", display: "flex", flexDirection: "column", background: "var(--bg2)" }}>
              <div style={{ fontSize: 11, color: "var(--t3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Daily Cumulative PNL</div>
              {mCumData.length < 2 ? <div style={{color:"var(--t3)", fontSize: 11, margin:"auto", padding: "10px 0"}}>Not enough data</div> : (() => {
                const mx = Math.max(...mCumData, 1); const mn = Math.min(...mCumData, 0); const rng = (mx - mn) || 1;
                const pts = mCumData.map((d, i) => { const x = (i / (mCumData.length - 1)) * 96 + 2; const y = 95 - ((d - mn) / rng) * 90; return `${x},${y}`; }).join(" ");
                return (
                  <div style={{ display: "flex", width: "100%", minHeight: 80, maxHeight: 100, marginTop: 10 }}>
                    <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", fontSize: 10, color: "var(--t3)", paddingRight: 8, textAlign: "right", minWidth: 40 }}>
                      <span>{fmtAx(mx)}</span><span>{fmtAx(mn + rng/2)}</span><span>{fmtAx(mn)}</span>
                    </div>
                    <svg viewBox="0 0 100 100" width="100%" height="100%" preserveAspectRatio="none" style={{overflow:"visible", flex: 1}}>
                      <defs><linearGradient id="gCumF" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="var(--blu)" stopOpacity="0.4"/><stop offset="100%" stopColor="var(--blu)" stopOpacity="0"/></linearGradient></defs>
                      <polygon points={`2,100 ${pts} 98,100`} fill="url(#gCumF)" />
                      <polyline points={pts} fill="none" stroke="var(--blu)" strokeWidth="2" vectorEffect="non-scaling-stroke" />
                    </svg>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* CALENDAR */}
          <div style={{ flex: "1 1 320px", minWidth: 0, ...card, padding: "20px", display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", marginBottom: 16, gap: 10 }}>
              <h2 style={{ fontSize: 15, fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}><span style={{color:"var(--blu)"}}>📅</span> Performance Calendar</h2>
              <div style={{ display: "flex", alignItems: "center", gap: 10, background: "var(--bg2)", padding: "4px 8px", borderRadius: 8, border: "1px solid var(--bdr)" }}>
                <button onClick={() => setCalMonth(new Date(cY, cM - 1, 1))} style={{ background: "none", border: "none", color: "var(--t2)", cursor: "pointer", fontSize: 12 }}>◀</button>
                <span style={{ fontSize: 12, fontWeight: 600, minWidth: 70, textAlign: "center" }}>{mNames[cM].substring(0,3)} {cY}</span>
                <button onClick={() => setCalMonth(new Date(cY, cM + 1, 1))} style={{ background: "none", border: "none", color: "var(--t2)", cursor: "pointer", fontSize: 12 }}>▶</button>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: 4, flex: 1 }}>
              {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => (
                <div key={d} style={{ textAlign: "center", fontSize: 10, fontWeight: 700, color: "var(--t3)", paddingBottom: 4 }}>{d}</div>
              ))}
              {cBlanks.map((_, i) => <div key={`cb-${i}`} style={{ aspectRatio: "1.3", background: "transparent" }} />)}
              {cMonthDays.map(day => {
                const ds = `${cY}-${String(cM+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
                const dayPnl = mDailyMap[ds] || 0;
                const isGreen = dayPnl > 0; const isRed = dayPnl < 0;
                return (
                  <div key={day} style={{ aspectRatio: "1", background: isGreen ? "var(--grn-d)" : isRed ? "var(--red-d)" : "var(--bg2)", borderRadius: 6, border: "1px solid", borderColor: isGreen ? "rgba(34,197,94,0.3)" : isRed ? "rgba(239,68,68,0.3)" : "var(--bdr)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 2 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: "var(--t1)" }}>{day}</span>
                    {dayPnl !== 0 && <span style={{ fontSize: 8, fontWeight: 700, color: isGreen ? "var(--grn)" : "var(--red)", fontFamily: "'JetBrains Mono'" }}>{dayPnl >= 0 ? "+" : ""}{dayPnl >= 1000 || dayPnl <= -1000 ? `$${(dayPnl/1000).toFixed(1)}K` : `$${dayPnl}`}</span>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* TRADES LIST */}
        {cMonthTrades.length > 0 && (
          <div>
            <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: "var(--t2)" }}>Trades for {mNames[cM]} {cY}</h3>
            {cMonthTrades.map(b => (
              <div key={b.id} style={{ ...card, marginBottom: 8, padding: 0, borderLeft: `4px solid ${safePnl(b.pnl) >= 0 ? "var(--grn)" : "var(--red)"}` }}>
                <div onClick={() => setExpanded(expanded === b.id ? null : b.id)} style={{ padding: "12px 16px", display: "flex", justifyContent: "space-between", cursor: "pointer", alignItems: "center" }}>
                  <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "var(--t3)", fontFamily: "'JetBrains Mono'" }}>{b.date.slice(5)}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontWeight: 800, fontFamily: "'JetBrains Mono'", fontSize: 14 }}>{b.ticker}</span>
                      <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, background: "var(--bg4)", color: b.direction === "Long" ? "var(--grn)" : "var(--red)", fontWeight: 700 }}>{b.direction}</span>
                      <span style={{ fontSize: 11, color: "var(--t2)" }}>x{b.contracts || 1}</span>
                      {(b.propAccountIds?.length > 0) && <span style={{ fontSize: 8, padding: "2px 6px", borderRadius: 4, background: "var(--gdim)", color: "var(--gold)", fontWeight: 700 }}>💰 {b.propAccountIds.length} acct{b.propAccountIds.length !== 1 ? "s" : ""}</span>}
                    </div>
                    <span style={{ fontSize: 12, color: "var(--t3)", fontFamily: "'JetBrains Mono'" }}>{b.entry} → {b.exit}</span>
                  </div>
                  <span style={{ fontWeight: 800, fontSize: 15, color: safePnl(b.pnl) >= 0 ? "var(--grn)" : "var(--red)", fontFamily: "'JetBrains Mono'" }}>
                    {safePnl(b.pnl) > 0 ? "+" : ""}${safePnl(b.pnl).toLocaleString()}
                  </span>
                </div>
                {expanded === b.id && (
                  <div style={{ padding: "0 16px 16px", borderTop: "1px solid var(--bdr)", marginTop: 4, paddingTop: 12 }}>
                    <p style={{ fontSize: 13, color: "var(--t2)", lineHeight: 1.6, marginBottom: b.image ? 12 : 0 }}>{b.notes || "No setup analysis provided."}</p>
                    {b.image && <img src={b.image} style={{ width: "100%", maxWidth: 600, borderRadius: 8, border: "1px solid var(--bdr)" }} alt="chart" />}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {memberTrades.length === 0 && <Empty variant="trades" icon="📊" title="No Trades Yet" sub="This member hasn't logged any trades yet." />}
      </div>
    );
  }

  // ---- LEADERBOARD DATA ----
  const leaderboardData = (() => {
    const todayStr = new Date().toISOString().split("T")[0];
    const userMap = {};

    // Build per-user stats from all breakdowns
    allMembers.forEach(m => {
      const trades = getTradesFor(m);
      if (trades.length === 0) return;
      let totalPnl = 0, grossProf = 0, grossLoss = 0, wins = 0;
      const dailyMap = {};
      trades.forEach(t => {
        const p = safePnl(t.pnl);
        totalPnl += p;
        if (p > 0) { grossProf += p; wins++; }
        if (p < 0) grossLoss += Math.abs(p);
        dailyMap[t.date] = (dailyMap[t.date] || 0) + p;
      });
      const todayPnl = dailyMap[todayStr] || 0;
      const pf = grossLoss === 0 ? (grossProf > 0 ? 999 : 0) : grossProf / grossLoss;

      // Streak: consecutive calendar days ending today where this user traded profitably
      let streak = 0;
      const d = new Date();
      for (let i = 0; i < 365; i++) {
        const ds = d.toISOString().split("T")[0];
        if (dailyMap[ds] != null && dailyMap[ds] > 0) streak++;
        else if (dailyMap[ds] != null && dailyMap[ds] <= 0) break;
        else if (i > 0) break; // no trade that day = break streak (allow today to be missing)
        d.setDate(d.getDate() - 1);
      }

      userMap[m.discordId] = { name: m.name || "Unknown", discordId: m.discordId, _isAdmin: m._isAdmin, totalPnl, todayPnl, pf, wins, trades: trades.length, streak, dailyMap };
    });

    const users = Object.values(userMap);
    const byTodayPnl = [...users].filter(u => u.todayPnl !== 0).sort((a, b) => b.todayPnl - a.todayPnl);
    const byStreak = [...users].filter(u => u.streak > 0).sort((a, b) => b.streak - a.streak || b.totalPnl - a.totalPnl);
    const byPF = [...users].filter(u => u.trades >= 3).sort((a, b) => b.pf - a.pf);
    const byTotalPnl = [...users].sort((a, b) => b.totalPnl - a.totalPnl);
    return { byTodayPnl, byStreak, byPF, byTotalPnl };
  })();

  // ---- MAIN VIEW: TABS ----
  const tabs = [
    { id: "friends", label: "Friends", count: myFriendIds.length },
    { id: "requests", label: "Requests", count: incomingRequests.length },
    { id: "find", label: "Find Members", count: 0 },
    { id: "leaderboard", label: "Leaderboard", count: 0 },
  ];

  // Friends list: members I can view journals of
  const friendMembers = allMembers.filter(m => m.discordId !== myId && canView(m.discordId));

  // "Find Members" list: everyone I haven't friended and haven't sent a request to
  const findableMembers = allMembers.filter(m => {
    if (m.discordId === myId) return false;
    const fr = getFriendship(m.discordId);
    if (fr && fr.status === "accepted") return false; // already friends
    return true;
  });

  const renderMemberCard = (m, actions) => {
    const trades = getTradesFor(m);
    const tPnl = trades.reduce((sum, b) => sum + safePnl(b.pnl), 0);
    const viewable = canView(m.discordId);

    return (
      <div key={m.discordId} className="nt-card-hover" style={{ ...card, display: "flex", alignItems: "center", gap: 14, cursor: viewable ? "pointer" : "default" }} onClick={() => { if (viewable) setViewingMember(m); }}>
        <Avatar src={m.avatar} name={m.name} size={42} style={m._isAdmin ? { border: "2px solid var(--gold)" } : {}} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
            {m.name}
            {m._isAdmin && <span style={{ fontSize: 8, padding: "1px 5px", borderRadius: 4, background: "var(--gdim)", color: "var(--gold)", fontWeight: 800 }}>ADMIN</span>}
          </div>
          <div style={{ fontSize: 11, color: tPnl >= 0 ? "var(--grn)" : "var(--red)", fontWeight: 600, marginTop: 2 }}>
            {viewable ? <>{tPnl >= 0 ? "+" : "-"}${Math.abs(tPnl).toLocaleString()} <span style={{ color: "var(--t3)", fontWeight: 400 }}>({trades.length} Trades)</span></> : <span style={{ color: "var(--t3)", fontWeight: 400 }}>Add friend to view journal</span>}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }} onClick={e => e.stopPropagation()}>
          {actions}
          {viewable && <span style={{ color: "var(--t3)", fontSize: 14, marginLeft: 4 }}>→</span>}
        </div>
      </div>
    );
  };

  return (
    <div className="nt-page-enter">
      <div style={{ marginBottom: 22 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800 }}>Community</h1>
        <p style={{ color: "var(--t2)", fontSize: 13, marginTop: 4 }}>
          {myIsAdmin ? "View any member's journal. Students must friend each other to share." : "Send friend requests to view each other's trading journals."}
        </p>
      </div>

      {/* Tab bar */}
      <div style={{ display: "flex", gap: 6, marginBottom: 20, overflowX: "auto", paddingBottom: 5 }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ padding: "8px 16px", borderRadius: 20, border: "1px solid", borderColor: tab === t.id ? "var(--gold)" : "var(--bdr)", background: tab === t.id ? "var(--gdim)" : "var(--bg3)", color: tab === t.id ? "var(--gold)" : "var(--t2)", fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 6, fontFamily: "'Outfit',sans-serif" }}>
            {t.label}
            {t.count > 0 && <span style={{ background: t.id === "requests" ? "var(--red)" : "var(--bg4)", color: t.id === "requests" ? "#fff" : "var(--t2)", fontSize: 9, padding: "1px 6px", borderRadius: 10, fontWeight: 800 }}>{t.count}</span>}
          </button>
        ))}
      </div>

      {/* FRIENDS TAB */}
      {tab === "friends" && (
        <div>
          {friendMembers.length === 0 ? (
            <Empty icon="👥" title="No Friends Yet" sub={myIsAdmin ? "All platform members will appear here." : "Send a friend request from the Find Members tab to start sharing journals!"} action="Find Members" onAction={() => setTab("find")} />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {friendMembers.map(m => renderMemberCard(m,
                !myIsAdmin && (
                  <button onClick={() => removeFriend(m.discordId)} style={{ ...btnO, fontSize: 10, padding: "4px 10px", color: "var(--t3)" }}>Unfriend</button>
                )
              ))}
            </div>
          )}
        </div>
      )}

      {/* REQUESTS TAB */}
      {tab === "requests" && (
        <div>
          {incomingRequests.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ fontSize: 13, fontWeight: 700, color: "var(--t2)", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.05em" }}>Incoming Requests</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {incomingRequests.map(fr => {
                  const fromMember = allMembers.find(m => m.discordId === fr.from);
                  if (!fromMember) return null;
                  return (
                    <div key={fr.id} style={{ ...card, display: "flex", alignItems: "center", gap: 14, borderLeft: "4px solid var(--blu)" }}>
                      <Avatar src={fromMember.avatar} name={fr.fromName} size={42} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 600 }}>{fr.fromName}</div>
                        <div style={{ fontSize: 11, color: "var(--t3)" }}>Wants to view your journal</div>
                      </div>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button onClick={() => rejectRequest(fr.id)} style={{ ...btnO, fontSize: 11, padding: "6px 12px" }}>Decline</button>
                        <button onClick={() => acceptRequest(fr.id)} style={{ ...btnG, fontSize: 11, padding: "6px 14px", background: "var(--grn)", boxShadow: "none" }}>Accept</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {outgoingRequests.length > 0 && (
            <div>
              <h3 style={{ fontSize: 13, fontWeight: 700, color: "var(--t2)", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.05em" }}>Sent Requests</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {outgoingRequests.map(fr => {
                  const toMember = allMembers.find(m => m.discordId === fr.to);
                  return (
                  <div key={fr.id} style={{ ...card, display: "flex", alignItems: "center", gap: 14, opacity: 0.7 }}>
                    <Avatar src={toMember?.avatar} name={fr.toName} size={42} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>{fr.toName}</div>
                      <div style={{ fontSize: 11, color: "var(--gold)" }}>Pending...</div>
                    </div>
                    <button onClick={() => rejectRequest(fr.id)} style={{ ...btnO, fontSize: 10, padding: "4px 10px", color: "var(--t3)" }}>Cancel</button>
                  </div>
                  );
                })}
              </div>
            </div>
          )}

          {incomingRequests.length === 0 && outgoingRequests.length === 0 && (
            <Empty icon="📬" title="No Pending Requests" sub="You don't have any friend requests right now." />
          )}
        </div>
      )}

      {/* FIND MEMBERS TAB */}
      {tab === "find" && (
        <div>
          {findableMembers.length === 0 ? (
            <Empty icon="✅" title="Everyone's Your Friend" sub="You've already connected with all members!" />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {findableMembers.map(m => {
                const fr = getFriendship(m.discordId);
                const isPendingOut = fr && fr.status === "pending" && fr.from === myId;
                const isPendingIn = fr && fr.status === "pending" && fr.to === myId;

                let actionBtn;
                if (isPendingOut) {
                  actionBtn = <span style={{ fontSize: 11, color: "var(--gold)", fontWeight: 600, padding: "4px 10px" }}>Pending</span>;
                } else if (isPendingIn) {
                  actionBtn = <button onClick={() => acceptRequest(fr.id)} style={{ ...btnG, fontSize: 11, padding: "6px 14px", background: "var(--grn)", boxShadow: "none" }}>Accept</button>;
                } else {
                  actionBtn = <button onClick={() => sendRequest(m.discordId)} style={{ ...btnG, fontSize: 11, padding: "6px 14px", boxShadow: "none" }}>Add Friend</button>;
                }

                return renderMemberCard(m, actionBtn);
              })}
            </div>
          )}
        </div>
      )}

      {/* LEADERBOARD TAB */}
      {tab === "leaderboard" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {/* Today's PNL Leaderboard */}
          <div style={card}>
            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
              <span>🏆</span> Today's PNL
            </h3>
            {leaderboardData.byTodayPnl.length === 0 ? (
              <div style={{ fontSize: 12, color: "var(--t3)", textAlign: "center", padding: "16px 0" }}>No trades today yet</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {leaderboardData.byTodayPnl.slice(0, 10).map((u, i) => (
                  <div key={u.discordId} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", background: i === 0 ? "var(--gdim)" : "var(--bg2)", borderRadius: 8, border: i === 0 ? "1px solid var(--gbdr)" : "1px solid var(--bdr)" }}>
                    <span style={{ fontSize: 14, fontWeight: 800, color: i === 0 ? "var(--gold)" : i === 1 ? "#C0C0C0" : i === 2 ? "#CD7F32" : "var(--t3)", minWidth: 24, textAlign: "center", fontFamily: "'JetBrains Mono'" }}>
                      {i === 0 ? "👑" : `#${i + 1}`}
                    </span>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: u._isAdmin ? "var(--gdim)" : "var(--bg4)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, color: u._isAdmin ? "var(--gold)" : "var(--t1)" }}>{u.name[0]}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 700 }}>{u.name} {u._isAdmin && <span style={{fontSize:8, color:"var(--gold)"}}>ADMIN</span>}</div>
                    </div>
                    <span style={{ fontSize: 15, fontWeight: 800, color: u.todayPnl >= 0 ? "var(--grn)" : "var(--red)", fontFamily: "'JetBrains Mono'" }}>
                      {u.todayPnl >= 0 ? "+" : "-"}${Math.abs(u.todayPnl).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Win Streak Leaderboard */}
          <div style={card}>
            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
              <span>🔥</span> Win Streaks
            </h3>
            {leaderboardData.byStreak.length === 0 ? (
              <div style={{ fontSize: 12, color: "var(--t3)", textAlign: "center", padding: "16px 0" }}>No active streaks</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {leaderboardData.byStreak.slice(0, 10).map((u, i) => (
                  <div key={u.discordId} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", background: i === 0 ? "var(--gdim)" : "var(--bg2)", borderRadius: 8, border: i === 0 ? "1px solid var(--gbdr)" : "1px solid var(--bdr)" }}>
                    <span style={{ fontSize: 14, fontWeight: 800, color: i === 0 ? "var(--gold)" : i === 1 ? "#C0C0C0" : i === 2 ? "#CD7F32" : "var(--t3)", minWidth: 24, textAlign: "center", fontFamily: "'JetBrains Mono'" }}>
                      {i === 0 ? "👑" : `#${i + 1}`}
                    </span>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: u._isAdmin ? "var(--gdim)" : "var(--bg4)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, color: u._isAdmin ? "var(--gold)" : "var(--t1)" }}>{u.name[0]}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 700 }}>{u.name}</div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <span style={{ fontSize: 15, fontWeight: 800, color: "var(--gold)", fontFamily: "'JetBrains Mono'" }}>{u.streak} day{u.streak !== 1 ? "s" : ""}</span>
                      <div style={{ fontSize: 9, color: "var(--t3)" }}>consecutive green</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Profit Factor Leaderboard */}
          <div style={card}>
            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
              <span>📊</span> Profit Factor <span style={{ fontSize: 10, color: "var(--t3)", fontWeight: 400 }}>(min 3 trades)</span>
            </h3>
            {leaderboardData.byPF.length === 0 ? (
              <div style={{ fontSize: 12, color: "var(--t3)", textAlign: "center", padding: "16px 0" }}>Not enough data</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {leaderboardData.byPF.slice(0, 10).map((u, i) => (
                  <div key={u.discordId} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", background: i === 0 ? "var(--gdim)" : "var(--bg2)", borderRadius: 8, border: i === 0 ? "1px solid var(--gbdr)" : "1px solid var(--bdr)" }}>
                    <span style={{ fontSize: 14, fontWeight: 800, color: i === 0 ? "var(--gold)" : i === 1 ? "#C0C0C0" : i === 2 ? "#CD7F32" : "var(--t3)", minWidth: 24, textAlign: "center", fontFamily: "'JetBrains Mono'" }}>
                      {i === 0 ? "👑" : `#${i + 1}`}
                    </span>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: u._isAdmin ? "var(--gdim)" : "var(--bg4)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, color: u._isAdmin ? "var(--gold)" : "var(--t1)" }}>{u.name[0]}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 700 }}>{u.name}</div>
                      <div style={{ fontSize: 10, color: "var(--t3)" }}>{u.trades} trades · {u.wins} wins</div>
                    </div>
                    <span style={{ fontSize: 15, fontWeight: 800, color: u.pf >= 999 ? "var(--grn)" : u.pf >= 1.5 ? "var(--grn)" : u.pf >= 1 ? "var(--gold)" : "var(--red)", fontFamily: "'JetBrains Mono'" }}>
                      {u.pf >= 999 ? "MAX" : u.pf.toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* All-Time PNL Leaderboard */}
          <div style={card}>
            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
              <span>💎</span> All-Time PNL
            </h3>
            {leaderboardData.byTotalPnl.length === 0 ? (
              <div style={{ fontSize: 12, color: "var(--t3)", textAlign: "center", padding: "16px 0" }}>No data</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {leaderboardData.byTotalPnl.slice(0, 10).map((u, i) => (
                  <div key={u.discordId} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", background: i === 0 ? "var(--gdim)" : "var(--bg2)", borderRadius: 8, border: i === 0 ? "1px solid var(--gbdr)" : "1px solid var(--bdr)" }}>
                    <span style={{ fontSize: 14, fontWeight: 800, color: i === 0 ? "var(--gold)" : i === 1 ? "#C0C0C0" : i === 2 ? "#CD7F32" : "var(--t3)", minWidth: 24, textAlign: "center", fontFamily: "'JetBrains Mono'" }}>
                      {i === 0 ? "👑" : `#${i + 1}`}
                    </span>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: u._isAdmin ? "var(--gdim)" : "var(--bg4)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, color: u._isAdmin ? "var(--gold)" : "var(--t1)" }}>{u.name[0]}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 700 }}>{u.name}</div>
                      <div style={{ fontSize: 10, color: "var(--t3)" }}>{u.trades} trades</div>
                    </div>
                    <span style={{ fontSize: 15, fontWeight: 800, color: u.totalPnl >= 0 ? "var(--grn)" : "var(--red)", fontFamily: "'JetBrains Mono'" }}>
                      {u.totalPnl >= 0 ? "+" : "-"}${Math.abs(u.totalPnl).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── RESOURCES ─────────────────────────────────────────────────────────────
function Resources({ notes, setNotes, isAdmin }) {
  const toast = useToast();
  const [showAdd, setShowAdd] = useState(false);
  const [resourceSaving, setResourceSaving] = useState(false);
  const save = async (d) => {
    setResourceSaving(true);
    try {
      const latest = await DB.get(SK.notes) || [];
      const u = [{ ...d, id: secureId(), date: new Date().toLocaleDateString() }, ...latest];
      setNotes(u); await DB.set(SK.notes, u); setShowAdd(false);
    } finally { setResourceSaving(false); }
  };
  const del = async (id) => {
    const latest = await DB.get(SK.notes) || [];
    const u = latest.filter(n => n.id !== id);
    setNotes(u); await DB.set(SK.notes, u);
  };
  
  const Form = ({ onClose }) => {
    const [f, setF] = useState({ title: "", desc: "", isPinned: false, fileUrl: null, fileName: "" });
    
    const handleFile = async e => { 
      const file = e.target.files[0]; 
      if(!file) return; 
      if (toast) toast("Uploading resource...", "warn");
      const formData = new FormData();
      formData.append("file", file);
      try {
        const res = await fetch("https://nexustrades.net/upload.php", { method: "POST", headers: DB.uploadHeaders(), credentials: "include", body: formData });
        const data = await res.json();
        if (data.url) { setF({...f, fileUrl: data.url, fileName: file.name}); if (toast) toast("Upload complete! Click Save."); }
        else { if (toast) toast(data.error || "Upload failed on the server.", "error"); }
      } catch (err) { if (toast) toast("Upload failed to connect to the server.", "error"); }
    };

    return (<div style={mOverlay} onClick={onClose}><div style={mBox} onClick={e => e.stopPropagation()}>
      <h2 style={{ fontSize: 17, fontWeight: 700, marginBottom: 18 }}>Add Resource</h2>
      <div style={{marginBottom:10}}><label style={lbl}>Title</label><input style={inp} value={f.title} onChange={e=>setF({...f,title:e.target.value})} /></div>
      <div style={{marginBottom:10}}><label style={lbl}>Description</label><textarea style={{...inp, minHeight:60}} value={f.desc} onChange={e=>setF({...f,desc:e.target.value})} maxLength={500} /><div style={{ fontSize: 10, color: (f.desc||"").length >= 480 ? "var(--red)" : "var(--t3)", marginTop: 4 }}>{(f.desc||"").length} / 500</div></div>
      <div style={{marginBottom:10}}><label style={lbl}>Upload PDF / File (or Notion Link)</label>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <input type="file" onChange={handleFile} style={{fontSize:11, color:"var(--t2)"}} />
          <span style={{ fontSize: 10, color: "var(--t3)" }}>OR</span>
          <input style={{...inp, flex:1}} placeholder="https://notion.so/..." value={!f.fileName ? f.fileUrl||"" : ""} onChange={e=>setF({...f, fileUrl:e.target.value, fileName:""})} />
        </div>
      </div>
      <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}><input type="checkbox" checked={f.isPinned} onChange={e=>setF({...f, isPinned:e.target.checked})} /> Pin Resource</label>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}><button onClick={onClose} style={btnO}>Cancel</button><LoadingButton loading={resourceSaving} onClick={()=>save(f)} style={btnG}>Add</LoadingButton></div>
    </div></div>);
  };

  const sorted = [...notes].sort((a,b) => (b.isPinned === a.isPinned ? 0 : b.isPinned ? 1 : -1));

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 22 }}>
        <div><h1 style={{ fontSize: 22, fontWeight: 800 }}>Resources</h1><p style={{ color: "var(--t2)", fontSize: 13 }}>PDFs, Notion guides, and tools.</p></div>
        {isAdmin && <button onClick={() => setShowAdd(true)} style={btnG}>+ Add Resource</button>}
      </div>
      {sorted.length === 0 && <Empty variant="resources" icon="📁" title="No Resources Yet" sub={isAdmin ? "Add your first resource to share with students." : "No resources have been shared yet."} action={isAdmin ? "+ Add Resource" : undefined} onAction={isAdmin ? () => setShowAdd(true) : undefined} />}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(250px,1fr))", gap: 12 }}>
        {sorted.map((n, i) => (
          <div key={n.id} className="nt-card-hover" style={{ ...card, position: "relative", border: n.isPinned ? "1px solid var(--gold)" : "1px solid var(--bdr)", animation: "staggerFadeIn 0.4s ease-out both", animationDelay: `${i * 0.05}s` }}>
            {n.isPinned && <div style={{ position: "absolute", top: -10, left: 14, background: "var(--gold)", color: "#000", padding: "2px 8px", borderRadius: 4, fontSize: 9, fontWeight: 700 }}>PINNED</div>}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8, marginTop: n.isPinned ? 4 : 0 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600 }}>{n.title}</h3>
              {isAdmin && <button onClick={()=>del(n.id)} style={{background:"none",border:"none",color:"var(--close-btn)",cursor:"pointer", fontSize: 16, fontWeight: 800, transition:"color 0.15s", opacity:0.7}}>✕</button>}
            </div>
            <p style={{ fontSize: 12, color: "var(--t2)", marginBottom: 14, lineHeight: 1.5 }}>{n.desc}</p>
            <button onClick={()=>{ const u = safeURL(n.fileUrl); if (u) window.open(u, "_blank"); }} style={{ ...btnO, width: "100%", background: "var(--bg2)", color: "var(--t1)", border: "1px solid var(--bdr2)" }}>
              {n.fileName ? `📄 Open ${n.fileName}` : "🔗 Open Link"}
            </button>
          </div>
        ))}
      </div>
      {showAdd && <Form onClose={()=>setShowAdd(false)} />}
    </div>
  );
}

// ─── MANAGE STUDENTS (WITH LOGS) ───────────────────────────────────────────
function ManageStudents({ students, setStudents, videos }) {
  const [showAdd, setShowAdd] = useState(false);
  const [viewLogs, setViewLogs] = useState(null);
  const [studentSaving, setStudentSaving] = useState(false);
  const [sortBy, setSortBy] = useState("name");
  const [sortDir, setSortDir] = useState("asc");

  const totalV = videos.length;
  const sortedStudents = React.useMemo(() => {
    const list = students.map(s => {
      const uniqueWatched = new Set(s.watchedVideos || []);
      const w = uniqueWatched.size;
      const pct = totalV > 0 ? Math.round((w / totalV) * 100) : 0;
      const logs = s.loginLogs || [];
      const lastLogin = logs.length ? Math.max(...logs.map(l => new Date(l.date || l.timestamp || 0).getTime())) : 0;
      return { ...s, _pct: pct, _lastLogin: lastLogin };
    });
    list.sort((a, b) => {
      if (sortBy === "name") { const va = (a.name || "").toLowerCase(); const vb = (b.name || "").toLowerCase(); return sortDir === "asc" ? (va > vb ? 1 : va < vb ? -1 : 0) : (vb > va ? 1 : vb < va ? -1 : 0); }
      if (sortBy === "progress") return sortDir === "asc" ? (a._pct - b._pct) : (b._pct - a._pct);
      return sortDir === "asc" ? (a._lastLogin - b._lastLogin) : (b._lastLogin - a._lastLogin);
    });
    return list;
  }, [students, totalV, sortBy, sortDir]);

  const toggleSort = (col) => { setSortBy(col); setSortDir(s => (s === "asc" && sortBy === col) ? "desc" : (sortBy === col ? (s === "asc" ? "desc" : "asc") : "asc")); };

  const save = async (d) => {
    setStudentSaving(true);
    try {
      const latest = await DB.get(SK.students) || [];
      const u = [...latest, { ...d, id: secureId(), watchedVideos: [], loginLogs: [] }];
      setStudents(u); await DB.set(SK.students, u); setShowAdd(false);
    } finally { setStudentSaving(false); }
  };
  const del = async (id) => {
    const latest = await DB.get(SK.students) || [];
    const u = latest.filter(s => s.id !== id);
    setStudents(u); await DB.set(SK.students, u);
  };
  
  const Form = ({ onClose }) => {
    const [f, setF] = useState({ name: "", discordId: "" });
    const [err, setErr] = useState("");
    const boxRef = React.useRef(null);
    useModalFocusTrap(boxRef, true, onClose);
    const handleAdd = () => {
      if (!f.name.trim() || !f.discordId.trim()) {
        setErr("Name and Discord ID are required.");
        return;
      }
      setErr("");
      save(f);
    };
    return (<div style={mOverlay} onClick={onClose} role="presentation"><div ref={boxRef} style={mBox} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Add Student">
      <h2 style={{ fontSize: 17, fontWeight: 700, marginBottom: 18 }}>Add Student</h2>
      <div style={{marginBottom:10}}><label style={lbl}>Name</label><input style={inp} value={f.name} onChange={e=>setF({...f,name:e.target.value})} /></div>
      <div style={{marginBottom:4}}><label style={lbl}>Discord User ID (18-19 digits)</label><input style={inp} value={f.discordId} onChange={e=>setF({...f,discordId:e.target.value})} placeholder="e.g. 123456789012345678" /></div>
      {err && <div style={{ fontSize: 11, color: "var(--red)", marginBottom: 6 }}>{err}</div>}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}><button onClick={onClose} style={btnO}>Cancel</button><LoadingButton loading={studentSaving} onClick={handleAdd} style={btnG}>Add</LoadingButton></div>
    </div></div>);
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 22 }}>
        <div><h1 style={{ fontSize: 22, fontWeight: 800 }}>Manage Students</h1><p style={{ color: "var(--t2)", fontSize: 13 }}>Accounts, Progress, and Security Logs.</p></div>
        <button onClick={() => setShowAdd(true)} style={btnG}>+ Add Student</button>
      </div>
      {sortedStudents.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 16, padding: "10px 20px", background: "var(--bg2)", borderRadius: 10, marginBottom: 8, border: "1px solid var(--bdr)" }}>
          <button type="button" onClick={() => toggleSort("name")} style={{ flex: 1, textAlign: "left", background: "none", border: "none", color: "var(--t2)", fontSize: 10, fontWeight: 700, cursor: "pointer", textTransform: "uppercase", letterSpacing: "0.06em", display: "flex", alignItems: "center", gap: 4 }}>Name {sortBy === "name" && (sortDir === "asc" ? "↑" : "↓")}</button>
          <button type="button" onClick={() => toggleSort("progress")} style={{ width: 150, textAlign: "left", background: "none", border: "none", color: "var(--t2)", fontSize: 10, fontWeight: 700, cursor: "pointer", textTransform: "uppercase", letterSpacing: "0.06em", display: "flex", alignItems: "center", gap: 4 }}>Progress {sortBy === "progress" && (sortDir === "asc" ? "↑" : "↓")}</button>
          <button type="button" onClick={() => toggleSort("lastLogin")} style={{ width: 120, textAlign: "right", background: "none", border: "none", color: "var(--t2)", fontSize: 10, fontWeight: 700, cursor: "pointer", textTransform: "uppercase", letterSpacing: "0.06em", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4 }}>Last Login {sortBy === "lastLogin" && (sortDir === "asc" ? "↑" : "↓")}</button>
          <div style={{ width: 120 }} />
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {sortedStudents.map((s, i) => {
          const uniqueWatched = new Set(s.watchedVideos || []);
          const w = uniqueWatched.size;
          const pct = s._pct ?? (totalV > 0 ? Math.round((w / totalV) * 100) : 0);
          const logs = s.loginLogs || [];
          const multiIP = new Set(logs.map(l => l.ip)).size > 1;
          const lastLoginStr = s._lastLogin ? new Date(s._lastLogin).toLocaleDateString() : "—";

          return (
            <div key={s.id} style={{ ...card, display: "flex", alignItems: "center", gap: 16, padding: "16px 20px", borderLeft: multiIP ? "4px solid var(--red)" : "4px solid var(--grn)", animation: "staggerFadeIn 0.4s ease-out both", animationDelay: `${i * 0.05}s` }}>
              <Avatar src={s.avatar} name={s.name} size={40} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{s.name} <span style={{fontSize:10, color:"var(--t3)", fontWeight:400, marginLeft:6}}>(ID: {s.discordId || s.username})</span></div>
                <div style={{ fontSize: 11, color: "var(--t3)" }}>{s.email || "No email linked"}</div>
              </div>
              <div style={{ width: 150 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, marginBottom: 4, fontWeight: 600 }}><span>Progress</span><span style={{color:"var(--gold)"}}>{Math.min(w, totalV)}/{totalV} ({Math.min(pct, 100)}%)</span></div>
                <div style={{ height: 6, background: "var(--bg4)", borderRadius: 3, overflow: "hidden" }}><div style={{ width: `${Math.min(pct, 100)}%`, height: "100%", background: "var(--gold)", borderRadius: 3 }} /></div>
              </div>
              <div style={{ width: 120, fontSize: 11, color: "var(--t3)", textAlign: "right" }}>{lastLoginStr}</div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={()=>setViewLogs(s)} style={btnO}>Logs {multiIP && "⚠️"}</button>
                <button onClick={()=>del(s.id)} style={btnD}>Remove</button>
              </div>
            </div>
          )
        })}
      </div>

      {showAdd && <Form onClose={()=>setShowAdd(false)} />}
      
      {viewLogs && (
        <div style={mOverlay} onClick={() => setViewLogs(null)}>
          <div style={mBox} onClick={e => e.stopPropagation()}>
            <h2 style={{ fontSize: 17, fontWeight: 700, marginBottom: 4 }}>Access Logs: {viewLogs.name}</h2>
            <p style={{ fontSize: 11, color: "var(--t2)", marginBottom: 16 }}>Tracking IPs to prevent account sharing.</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 300, overflowY: "auto" }}>
              {(!viewLogs.loginLogs || viewLogs.loginLogs.length === 0) && <div style={{fontSize: 12, color: "var(--t3)"}}>No logins recorded yet.</div>}
              {(viewLogs.loginLogs || []).map((l, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: 10, background: "var(--bg2)", borderRadius: 6, border: "1px solid var(--bdr)" }}>
                  <span style={{ fontSize: 12, fontFamily: "'JetBrains Mono'", color: "var(--blu)" }}>{l.ip}</span>
                  <span style={{ fontSize: 11, color: "var(--t3)" }}>{new Date(l.date).toLocaleString()}</span>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}><button onClick={() => setViewLogs(null)} style={btnO}>Close</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── GLOBAL SEARCH COMPONENT ───────────────────────────────────────────────
function GlobalSearch({ D, onNav, close }) {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("All"); // All | Videos | Resources | Trades
  const [activeIndex, setActiveIndex] = useState(0);
  const [recent, setRecent] = useState(() => {
    try {
      const raw = typeof localStorage !== "undefined" ? localStorage.getItem("nt_recent_searches") : null;
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  const results = useMemo(() => {
    if (!q) return [];
    const low = q.toLowerCase();
    const videos = (D.videos || [])
      .filter(x => (x.title || "").toLowerCase().includes(low))
      .map(x => ({ ...x, _type: "Video", _nav: "videos", title: x.title }));
    const resources = (D.notes || [])
      .filter(x => (x.title || "").toLowerCase().includes(low))
      .map(x => ({ ...x, _type: "Resource", _nav: "notes", title: x.title }));
    const trades = (D.breakdowns || [])
      .filter(x => (x.ticker || "").toLowerCase().includes(low))
      .map(x => ({
        ...x,
        _type: "Trade",
        _nav: "breakdowns",
        title: x.ticker ? `${x.ticker} — ${x.date || "Trade"}` : (x.notes || "Trade")
      }));

    let all = [...videos, ...resources, ...trades];
    if (filter === "Videos") all = all.filter(r => r._type === "Video");
    else if (filter === "Resources") all = all.filter(r => r._type === "Resource");
    else if (filter === "Trades") all = all.filter(r => r._type === "Trade");

    return all.slice(0, 8);
  }, [q, filter, D]);

  useEffect(() => {
    setActiveIndex(0);
  }, [q, filter]);

  const saveRecent = (term) => {
    const trimmed = term.trim();
    if (!trimmed) return;
    setRecent(prev => {
      const next = [trimmed, ...prev.filter(t => t !== trimmed)].slice(0, 5);
      try { localStorage.setItem("nt_recent_searches", JSON.stringify(next)); } catch (e) { _log("localStorage write failed:", e.name); }
      return next;
    });
  };

  const handleSelect = (item) => {
    if (!item) return;
    saveRecent(q);
    onNav(item._nav);
    close();
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex(i => Math.min(i + 1, Math.max(results.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (results[activeIndex]) handleSelect(results[activeIndex]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      close();
    }
  };

  const filters = [
    { label: "All", value: "All" },
    { label: "Videos", value: "Videos" },
    { label: "Resources", value: "Resources" },
    { label: "Trades", value: "Trades" },
  ];

  const panelRef = React.useRef(null);
  useModalFocusTrap(panelRef, true, close);

  return (
    <div style={mOverlay} onClick={close} role="presentation">
      <div ref={panelRef} style={{ ...mBox, position: "absolute", top: "15%", padding: 0, overflow: "hidden" }} onClick={e=>e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Search">
        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--bdr)", display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{fontSize:18, opacity:0.5}}>🔍</span>
          <input
            autoFocus
            value={q}
            onChange={e=>setQ(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search videos, resources, trades..."
            style={{ flex: 1, background: "transparent", border: "none", color: "var(--t1)", fontSize: 16, outline: "none" }}
          />
          <kbd style={{fontSize:9, padding:"2px 6px", borderRadius:4, background:"var(--bg4)", color:"var(--t3)", border:"1px solid var(--bdr)", fontFamily:"'JetBrains Mono'", cursor:"pointer"}} onClick={close}>ESC</kbd>
        </div>
        <div style={{ padding: 10 }}>
          <div style={{ display: "flex", gap: 6, marginBottom: 10, overflowX: "auto", paddingBottom: 4 }}>
            {filters.map(f => (
              <button
                key={f.value}
                onClick={() => setFilter(f.value)}
                style={{
                  padding: "4px 10px",
                  borderRadius: 999,
                  border: "1px solid",
                  borderColor: filter === f.value ? "var(--gold)" : "var(--bdr)",
                  background: filter === f.value ? "var(--gdim)" : "var(--bg3)",
                  color: filter === f.value ? "var(--gold)" : "var(--t2)",
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: "pointer",
                  whiteSpace: "nowrap"
                }}
              >
                {f.label}
              </button>
            ))}
          </div>

          {!q && recent.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, color: "var(--t3)", marginBottom: 6 }}>Recent searches</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {recent.map(term => (
                  <button
                    key={term}
                    onClick={() => setQ(term)}
                    style={{ padding: "4px 10px", borderRadius: 999, border: "1px solid var(--bdr)", background: "var(--bg3)", color: "var(--t2)", fontSize: 11, cursor: "pointer" }}
                  >
                    {term}
                  </button>
                ))}
              </div>
            </div>
          )}

          {q && results.length === 0 && (
            <Empty variant="search" icon="🔍" title="No results found" sub="Try a different search or filter." />
          )}
          {results.map((r, idx) => (
            <div
              key={r.id || r.title + idx}
              onClick={() => handleSelect(r)}
              style={{
                padding: "12px 16px",
                borderRadius: 8,
                cursor: "pointer",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                transition: "background 0.2s",
                background: idx === activeIndex ? "var(--bg2)" : "transparent"
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 500 }}>{r.title}</div>
              <div style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, background: "var(--bg4)", color: "var(--t2)" }}>{r._type}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── PROP FIRM TRACKER ──────────────────────────────────────────────────
function PropFirmTracker({ propAccounts, setPropAccounts, isAdmin, user, students, breakdowns }) {
  const toast = useToast();
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showRecordPayout, setShowRecordPayout] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [viewingUser, setViewingUser] = useState(isAdmin ? null : user.discordId);

  const saveAccounts = async (updated) => { setPropAccounts(updated); await DB.set(SK.propAccounts, updated); };

  const addAccount = async (formData) => {
    const latest = await DB.get(SK.propAccounts) || [];
    const f = PROP_FIRMS[formData.firmKey];
    const p = f?.plans[formData.planKey];
    const newAcct = {
      id: secureId(), userId: user.discordId, userName: user.name,
      firm: formData.firmKey, plan: formData.planKey, accountSize: formData.size,
      accountLabel: formData.label || `${f.name} ${p.name} ${formData.size / 1000}k`,
      status: "active", payouts: [], createdAt: new Date().toISOString(),
      lastPayoutDate: null,
    };
    await saveAccounts([newAcct, ...latest]);
    setShowAddAccount(false);
    if (toast) toast("Prop firm account added!");
  };

  const recordPayout = async (accountId, payoutData) => {
    const latest = await DB.get(SK.propAccounts) || [];
    const updated = latest.map(a => {
      if (a.id !== accountId) return a;
      return { ...a, payouts: [...(a.payouts || []), { id: secureId(), ...payoutData }], lastPayoutDate: payoutData.date };
    });
    await saveAccounts(updated);
    setSelectedAccount(updated.find(a => a.id === accountId));
    setShowRecordPayout(false);
    if (toast) toast("Payout recorded!");
  };

  const deleteAccount = async (id) => {
    const latest = await DB.get(SK.propAccounts) || [];
    await saveAccounts(latest.filter(a => a.id !== id));
    setSelectedAccount(null); setDeleting(null);
    if (toast) toast("Account removed.", "warn");
  };

  const updateStatus = async (accountId, newStatus) => {
    const latest = await DB.get(SK.propAccounts) || [];
    const updated = latest.map(a => a.id === accountId ? { ...a, status: newStatus } : a);
    await saveAccounts(updated);
    setSelectedAccount(updated.find(a => a.id === accountId));
    if (toast) toast(`Status: ${newStatus}`);
  };

  // ---- ADD ACCOUNT MODAL ----
  const AddAccountModal = ({ onClose }) => {
    const [firmKey, setFirmKey] = useState("");
    const [planKey, setPlanKey] = useState("");
    const [size, setSize] = useState("");
    const [label, setLabel] = useState("");
    const plans = firmKey ? Object.entries(PROP_FIRMS[firmKey]?.plans || {}) : [];
    const sizes = (firmKey && planKey) ? (PROP_FIRMS[firmKey]?.plans[planKey]?.sizes || []) : [];

    return (
      <div style={mOverlay} onClick={onClose}><div style={mBox} onClick={e => e.stopPropagation()}>
        <h2 style={{ fontSize: 17, fontWeight: 700, marginBottom: 18 }}>Add Prop Firm Account</h2>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <label style={lbl}>Firm</label>
            <select style={inp} value={firmKey} onChange={e => { setFirmKey(e.target.value); setPlanKey(""); setSize(""); }}>
              <option value="">— Select Firm —</option>
              {Object.entries(PROP_FIRMS).map(([k, f]) => <option key={k} value={k}>{f.icon} {f.name}</option>)}
            </select>
          </div>
          {firmKey && (
            <div>
              <label style={lbl}>Plan</label>
              <select style={inp} value={planKey} onChange={e => { setPlanKey(e.target.value); setSize(""); }}>
                <option value="">— Select Plan —</option>
                {plans.map(([k, p]) => <option key={k} value={k}>{p.name}</option>)}
              </select>
            </div>
          )}
          {planKey && (
            <div>
              <label style={lbl}>Account Size</label>
              <select style={inp} value={size} onChange={e => setSize(parseInt(e.target.value))}>
                <option value="">— Select Size —</option>
                {sizes.map(s => <option key={s} value={s}>${(s).toLocaleString()}</option>)}
              </select>
            </div>
          )}
          {size && (
            <div>
              <label style={lbl}>Nickname (Optional)</label>
              <input style={inp} value={label} onChange={e => setLabel(e.target.value)} placeholder={`e.g. "MFFU Rapid 50k #2"`} />
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 18 }}>
          <button onClick={onClose} style={btnO}>Cancel</button>
          <button disabled={!firmKey || !planKey || !size} onClick={() => addAccount({ firmKey, planKey, size, label })} style={{ ...btnG, opacity: (!firmKey || !planKey || !size) ? 0.5 : 1 }}>Add Account</button>
        </div>
      </div></div>
    );
  };

  // ---- RECORD PAYOUT MODAL ----
  const RecordPayoutModal = ({ account, onClose }) => {
    const status = computePropStatus(account, breakdowns);
    const [f, setF] = useState({ amount: status.availablePayout, date: new Date().toISOString().split("T")[0] });
    return (
      <div style={mOverlay} onClick={onClose}><div style={mBox} onClick={e => e.stopPropagation()}>
        <h2 style={{ fontSize: 17, fontWeight: 700, marginBottom: 18 }}>Record Payout</h2>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div><label style={lbl}>Payout Amount ($)</label><input type="number" style={inp} value={f.amount} onChange={e => setF({ ...f, amount: parseFloat(e.target.value) || 0 })} /></div>
          <div><label style={lbl}>Date</label><input type="date" style={inp} value={f.date} onChange={e => setF({ ...f, date: e.target.value })} /></div>
          <div style={{ fontSize: 12, color: "var(--t3)", padding: "10px 14px", background: "var(--bg2)", borderRadius: 8 }}>
            Profit split: <strong style={{ color: "var(--gold)" }}>{(status.profitSplit * 100)}%</strong> — Net to you: <strong style={{ color: "var(--grn)" }}>${(f.amount).toLocaleString()}</strong>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 18 }}>
          <button onClick={onClose} style={btnO}>Cancel</button>
          <button disabled={!f.amount || f.amount <= 0} onClick={() => recordPayout(account.id, { amount: f.amount, date: f.date })} style={{ ...btnG, background: "var(--grn)", boxShadow: "none", opacity: (!f.amount || f.amount <= 0) ? 0.5 : 1 }}>Confirm Payout</button>
        </div>
      </div></div>
    );
  };

  // ──── ADMIN DIRECTORY VIEW ────
  if (isAdmin && !viewingUser) {
    const adminDiscordId = user.discordId;
    const allUsers = [{ ...user, discordId: adminDiscordId, _isAdmin: true }, ...students.filter(s => s.discordId !== adminDiscordId)];
    return (
      <div style={{ animation: "fadeIn 0.3s" }}>
        <div style={{ marginBottom: 26 }}>
          <h1 style={{ fontSize: 24, fontWeight: 800 }}>Prop Firm Tracker</h1>
          <p style={{ color: "var(--t2)", fontSize: 13, marginTop: 4 }}>View students' prop firm accounts and payout progress.</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: 12 }}>
          {allUsers.map(u => {
            const accts = propAccounts.filter(a => a.userId === u.discordId);
            const totalPnl = accts.reduce((s, a) => s + computePropStatus(a, breakdowns).totalPnl, 0);
            const activeCount = accts.filter(a => a.status === "active").length;
            return (
              <div key={u.discordId} className="nt-card-hover" onClick={() => setViewingUser(u.discordId)} style={{ ...card, cursor: "pointer", display: "flex", alignItems: "center", gap: 14 }}>
                <Avatar src={u.avatar} name={u.name} size={42} style={u._isAdmin ? { border: "2px solid var(--gold)" } : {}} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
                    {u.name || u.username}
                    {u._isAdmin && <span style={{ fontSize: 8, padding: "1px 5px", borderRadius: 4, background: "var(--gdim)", color: "var(--gold)", fontWeight: 800 }}>ADMIN</span>}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--t3)", marginTop: 2 }}>
                    {activeCount} active account{activeCount !== 1 ? "s" : ""} &middot; <span style={{ color: totalPnl >= 0 ? "var(--grn)" : "var(--red)", fontWeight: 600 }}>{totalPnl >= 0 ? "+" : ""}${totalPnl.toLocaleString()}</span>
                  </div>
                </div>
                <span style={{ color: "var(--t3)", fontSize: 14 }}>→</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ──── ACCOUNT DETAIL VIEW ────
  if (selectedAccount) {
    const acct = propAccounts.find(a => a.id === selectedAccount.id) || selectedAccount;
    const status = computePropStatus(acct, breakdowns);
    const canEdit = acct.userId === user.discordId;
    const trades = [...(status.trades || [])].sort((a, b) => (b.date || "").localeCompare(a.date || ""));
    const payouts = acct.payouts || [];

    const statusColors = { active: "var(--blu)", blown: "var(--red)", payout_taken: "var(--grn)", live: "var(--gold)" };
    const statusBg = { active: "rgba(59,130,246,0.1)", blown: "var(--red-d)", payout_taken: "rgba(34,197,94,0.08)", live: "var(--gdim)" };

    return (
      <div className="nt-page-enter">
        <button onClick={() => setSelectedAccount(null)} style={{ ...btnO, marginBottom: 16, display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 16 }}>&larr;</span> Back
        </button>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 28 }}>{status.firmIcon}</span>
            <div>
              <h1 style={{ fontSize: 20, fontWeight: 800 }}>{acct.accountLabel}</h1>
              <p style={{ color: "var(--t2)", fontSize: 12 }}>{status.firmName} &middot; {status.planName} &middot; ${acct.accountSize.toLocaleString()}</p>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {canEdit && acct.status === "active" && (
              <select value={acct.status} onChange={e => updateStatus(acct.id, e.target.value)} style={{ ...inp, width: "auto", fontSize: 10, padding: "4px 8px", fontWeight: 700 }}>
                <option value="active">Active</option>
                <option value="blown">Blown</option>
              </select>
            )}
            <span style={{ padding: "4px 10px", borderRadius: 6, fontSize: 10, fontWeight: 800, textTransform: "uppercase", background: statusBg[acct.status] || "var(--bg4)", color: statusColors[acct.status] || "var(--t2)" }}>{acct.status.replace("_", " ")}</span>
          </div>
        </div>

        {/* Metric Cards */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10, marginBottom: 16 }}>
          {[
            { label: "Balance", val: `$${status.currentBalance.toLocaleString(undefined, { minimumFractionDigits: 2 })}`, color: status.currentBalance >= acct.accountSize ? "var(--grn)" : "var(--red)" },
            { label: "Total P&L", val: `${status.totalPnl >= 0 ? "+" : ""}$${status.totalPnl.toLocaleString(undefined, { minimumFractionDigits: 2 })}`, color: status.totalPnl >= 0 ? "var(--grn)" : "var(--red)" },
            { label: "Available Payout", val: status.eligible ? `$${status.availablePayout.toLocaleString()}` : "—", color: status.eligible ? "var(--grn)" : "var(--t3)" },
            { label: "Payouts Taken", val: status.maxPayouts ? `${status.payoutsTaken}/${status.maxPayouts}` : `${status.payoutsTaken}`, color: "var(--gold)" },
          ].map((m, i) => (
            <div key={i} style={{ ...card, padding: 14 }}>
              <div style={{ fontSize: 9, fontWeight: 600, color: "var(--t3)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>{m.label}</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: m.color, fontFamily: "'JetBrains Mono'" }}>{m.val}</div>
            </div>
          ))}
        </div>

        {/* Payout Requirements */}
        <div style={{ ...card, marginBottom: 16, borderLeft: `4px solid ${status.eligible ? "var(--grn)" : "var(--bdr)"}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: status.checks.length > 0 ? 14 : 0 }}>
            <h3 style={{ fontSize: 14, fontWeight: 700 }}>Payout Requirements</h3>
            {status.eligible && canEdit && (
              <button onClick={() => setShowRecordPayout(true)} className="nt-btn-hover" style={{ ...btnG, padding: "6px 14px", fontSize: 11, background: "var(--grn)", boxShadow: "none" }}>
                Record Payout (${status.availablePayout.toLocaleString()})
              </button>
            )}
          </div>
          {status.checks.map((c, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: c.met ? "var(--grn)" : "var(--t3)", flexShrink: 0, opacity: c.met ? 1 : 0.4 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: c.met ? "var(--t1)" : "var(--t2)" }}>{c.label}</div>
                {c.isBuffer && (
                  <div style={{ height: 4, background: "var(--bg0)", borderRadius: 2, marginTop: 4, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${Math.min(100, (c.current / c.target) * 100)}%`, background: c.met ? "var(--grn)" : "var(--gold)", borderRadius: 2, transition: "width 0.4s" }} />
                  </div>
                )}
              </div>
              <div style={{ fontSize: 11, fontWeight: 700, fontFamily: "'JetBrains Mono'", color: c.met ? "var(--grn)" : "var(--t3)" }}>
                {c.isCurrency ? `$${c.current.toLocaleString()}` : c.current}{!c.isCurrency && `/${c.target}`}{c.suffix ? ` ${c.suffix}` : ""}
              </div>
            </div>
          ))}
          {status.checks.length === 0 && <div style={{ fontSize: 12, color: "var(--t3)" }}>No specific requirements — log trades to track progress.</div>}
        </div>

        {/* Linked Journal Trades */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <h3 style={{ fontSize: 14, fontWeight: 700 }}>Linked Trades ({trades.length})</h3>
            <span style={{ fontSize: 10, color: "var(--t3)" }}>Tag trades in Trading Journals to link them here</span>
          </div>
          {trades.length === 0 ? (
            <Empty icon="📊" title="No Trades Linked" sub="Go to Trading Journals and select this account when logging a trade." />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {trades.map(t => (
                <div key={t.id} style={{ ...card, padding: "8px 14px", borderLeft: `3px solid ${safePnl(t.pnl) >= 0 ? "var(--grn)" : "var(--red)"}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                    <span style={{ fontSize: 11, color: "var(--t3)", fontFamily: "'JetBrains Mono'", minWidth: 75 }}>{t.date}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: "var(--t1)" }}>{t.ticker || ""}</span>
                    {t.direction && <span style={{ fontSize: 9, padding: "1px 5px", borderRadius: 3, background: "var(--bg4)", color: t.direction === "Long" ? "var(--grn)" : "var(--red)", fontWeight: 700 }}>{t.direction}</span>}
                    {t.notes && <span style={{ fontSize: 11, color: "var(--t2)", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.notes}</span>}
                  </div>
                  <span style={{ fontWeight: 800, fontSize: 13, color: safePnl(t.pnl) >= 0 ? "var(--grn)" : "var(--red)", fontFamily: "'JetBrains Mono'" }}>
                    {safePnl(t.pnl) > 0 ? "+" : ""}${safePnl(t.pnl).toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Payout History */}
        {payouts.length > 0 && (
          <div>
            <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Payout History</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {payouts.map(p => (
                <div key={p.id} style={{ ...card, padding: "8px 14px", borderLeft: "3px solid var(--gold)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 11, color: "var(--t3)", fontFamily: "'JetBrains Mono'" }}>{p.date}</span>
                  <span style={{ fontWeight: 800, fontSize: 13, color: "var(--gold)", fontFamily: "'JetBrains Mono'" }}>${(p.amount || 0).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Delete account */}
        {canEdit && (
          <div style={{ marginTop: 24, paddingTop: 16, borderTop: "1px solid var(--bdr)" }}>
            <button onClick={() => setDeleting(acct.id)} style={btnD}>Delete Account</button>
          </div>
        )}

        {showRecordPayout && <RecordPayoutModal account={acct} onClose={() => setShowRecordPayout(false)} />}
        {deleting && <Confirm msg="Delete this prop firm account? All trades and payout history will be lost." onY={() => deleteAccount(deleting)} onN={() => setDeleting(null)} />}
      </div>
    );
  }

  // ──── ACCOUNT LIST VIEW ────
  const myAccounts = viewingUser ? propAccounts.filter(a => a.userId === viewingUser) : [];

  return (
    <div style={{ animation: "fadeIn 0.3s" }}>
      {isAdmin && viewingUser && viewingUser !== user.discordId && (
        <button onClick={() => setViewingUser(null)} style={{ background: "none", border: "none", color: "var(--gold)", fontSize: 12, cursor: "pointer", marginBottom: 14, fontWeight: 600 }}>← Back to Directory</button>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 22 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800 }}>Prop Firm Tracker</h1>
          <p style={{ color: "var(--t2)", fontSize: 13, marginTop: 4 }}>Track your prop firm accounts and payout progress.</p>
        </div>
        {(viewingUser === user.discordId) && <button onClick={() => setShowAddAccount(true)} style={btnG}>+ Add Account</button>}
      </div>

      {myAccounts.length === 0 ? (
        <Empty icon="💰" title="No Prop Accounts" sub="Add your first prop firm account to start tracking payouts." action="+ Add Account" onAction={() => setShowAddAccount(true)} />
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 12 }}>
          {myAccounts.map(acct => {
            const st = computePropStatus(acct, breakdowns);
            const statusColors = { active: "var(--blu)", blown: "var(--red)", payout_taken: "var(--grn)", live: "var(--gold)" };
            const statusBg = { active: "rgba(59,130,246,0.1)", blown: "var(--red-d)", payout_taken: "rgba(34,197,94,0.08)", live: "var(--gdim)" };
            return (
              <div key={acct.id} onClick={() => setSelectedAccount(acct)} className="nt-card-hover" style={{ ...card, cursor: "pointer", position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: st.eligible ? "var(--grn)" : acct.status === "blown" ? "var(--red)" : "var(--gold)", opacity: 0.5 }} />
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 20 }}>{st.firmIcon}</span>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700 }}>{acct.accountLabel}</div>
                      <div style={{ fontSize: 10, color: "var(--t3)" }}>{st.planName} &middot; ${(acct.accountSize / 1000)}k</div>
                    </div>
                  </div>
                  <span style={{ padding: "3px 8px", borderRadius: 5, fontSize: 9, fontWeight: 800, textTransform: "uppercase", background: statusBg[acct.status] || "var(--bg4)", color: statusColors[acct.status] || "var(--t2)" }}>{acct.status.replace("_", " ")}</span>
                </div>
                <div style={{ fontSize: 20, fontWeight: 800, color: st.totalPnl >= 0 ? "var(--grn)" : "var(--red)", fontFamily: "'JetBrains Mono'", marginBottom: 4 }}>
                  ${st.currentBalance.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </div>
                <div style={{ fontSize: 10, color: "var(--t3)", marginBottom: 8 }}>
                  P&L: <span style={{ color: st.totalPnl >= 0 ? "var(--grn)" : "var(--red)", fontWeight: 600 }}>{st.totalPnl >= 0 ? "+" : ""}${st.totalPnl.toLocaleString()}</span> &middot; {st.trades.length} trades
                </div>
                {st.checks.length > 0 && (
                  <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                    {st.checks.slice(0, 3).map((c, i) => (
                      <div key={i} style={{ fontSize: 9, padding: "2px 6px", borderRadius: 4, background: c.met ? "rgba(34,197,94,0.1)" : "var(--bg4)", color: c.met ? "var(--grn)" : "var(--t3)", fontWeight: 700 }}>
                        {c.isCurrency ? `$${Math.round(c.current)}` : c.current}/{c.target}{c.suffix ? ` ${c.suffix}` : ""}
                      </div>
                    ))}
                    {st.eligible && <div style={{ fontSize: 9, padding: "2px 6px", borderRadius: 4, background: "rgba(34,197,94,0.12)", color: "var(--grn)", fontWeight: 800 }}>PAYOUT READY</div>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showAddAccount && <AddAccountModal onClose={() => setShowAddAccount(false)} />}
    </div>
  );
}

// ─── SIDEBAR ───────────────────────────────────────────────────────────────
function Sidebar({ active, onNav, user, onLogout, toggleSearch, theme, toggleTheme, viewAsStudent, setViewAsStudent, pendingHwCount, videos, pendingFriendCount, assignments, collapsed, setCollapsed }) {
  const isAdmin = user.role === "admin";
  const actualRole = isAdmin && !viewAsStudent ? "admin" : "student";

  const nav = [
    { id: "dashboard", label: "Dashboard", icon: "⬥" },
    { id: "videos", label: "Video Library", icon: "▶" },
    { id: "breakdowns", label: "Trading Journals", icon: "◈" }, 
    { id: "notes", label: "Resources", icon: "◇" },
    { id: "community", label: "Community", icon: "👥", badge: pendingFriendCount },
    { id: "propfirms", label: "Prop Firms", icon: "💰" },
  ];

  if (actualRole === "student" && (assignments || []).length > 0) {
    nav.push({ id: "student_homework", label: "My Homework", icon: "📝" });
  }

  if (actualRole === "admin") {
    nav.push({ id: "homework", label: `Homework Submissions`, icon: "📚", badge: pendingHwCount });
    nav.push({ id: "students", label: "Manage Students", icon: "👤" });
    nav.push({ id: "activity", label: "Security Logs", icon: "🛡️" });
  }

  const toggleCollapse = () => {
    const next = !collapsed;
    setCollapsed(next);
    try { localStorage.setItem('nt_sidebar', next ? 'collapsed' : 'expanded'); } catch (e) { _log("localStorage write failed:", e.name); }
  };

  return (
    <div style={{ width: collapsed ? 64 : 220, minHeight: "100vh", background: "var(--bg1)", borderRight: "1px solid var(--bdr)", display: "flex", flexDirection: "column", position: "fixed", left: 0, top: 0, zIndex: 200, transition: "width 0.2s ease, background 0.3s" }} aria-label="Main navigation">
      <div style={{ padding: "16px 10px", borderBottom: "1px solid var(--bdr)", display: "flex", alignItems: "center", justifyContent: collapsed ? "center" : "space-between", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <NTLogo size={30} />
          {!collapsed && (
            <div>
              <div style={{ fontSize: 14, fontWeight: 800, color: "var(--t1)" }}>Nexus Trades</div>
              <div style={{ fontSize: 9, color: "var(--t3)", letterSpacing: "0.1em", textTransform: "uppercase" }}>Mentorship</div>
            </div>
          )}
        </div>
        {!collapsed && (
          <button onClick={toggleCollapse} aria-label="Collapse sidebar" style={{ background: "none", border: "none", color: "var(--t3)", cursor: "pointer", fontSize: 14 }}>⟨</button>
        )}
        {collapsed && (
          <button onClick={toggleCollapse} aria-label="Expand sidebar" style={{ position: "absolute", right: 6, top: 10, background: "none", border: "none", color: "var(--t3)", cursor: "pointer", fontSize: 14 }}>⟩</button>
        )}
      </div>
      
      <div style={{ padding: "12px 12px 0" }}>
        <button onClick={toggleSearch} style={{ width: "100%", padding: "8px 12px", background: "var(--bg2)", border: "1px solid var(--bdr)", borderRadius: 8, color: "var(--t2)", fontSize: 12, display: "flex", alignItems: "center", gap: 8, cursor: "pointer", textAlign: "left", transition: "border-color 0.2s" }}>
          <span style={{opacity:0.6}}>🔍</span>
          {!collapsed && <span style={{flex:1}}>Search...</span>}
          {!collapsed && <kbd style={{fontSize:9, padding:"2px 5px", borderRadius:4, background:"var(--bg4)", color:"var(--t3)", border:"1px solid var(--bdr)", fontFamily:"'JetBrains Mono'"}}>Ctrl+K</kbd>}
        </button>
      </div>

      <nav style={{ flex: 1, padding: "16px 12px", display: "flex", flexDirection: "column", gap: 2 }}>
        {nav.map((it) => (
          <button
            key={it.id}
            onClick={() => onNav(it.id)}
            aria-current={active === it.id ? "page" : undefined}
            title={collapsed ? it.label : undefined}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: collapsed ? "center" : "space-between",
              width: "100%",
              padding: "10px 12px",
              border: "none",
              borderRadius: 8,
              cursor: "pointer",
              background: active === it.id ? "var(--bg3)" : "transparent",
              color: active === it.id ? "var(--t1)" : "var(--t2)",
              fontSize: 13,
              fontWeight: active === it.id ? 600 : 500,
              fontFamily: "'Outfit'",
              transition: "all 0.15s",
              textAlign: "left"
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: collapsed ? 0 : 10 }}>
              <span style={{ fontSize: 12, color: active === it.id ? "var(--gold)" : "var(--t3)" }}>{it.icon}</span>
              {!collapsed && it.label}
            </div>
            {!collapsed && it.badge > 0 && <span style={{ background: "var(--red)", color: "#fff", fontSize: 9, padding: "2px 6px", borderRadius: 10, fontWeight: 800 }}>{it.badge}</span>}
          </button>
        ))}
      </nav>

      <div style={{ padding: "16px 12px", borderTop: "1px solid var(--bdr)", display: "flex", flexDirection: "column", gap: 10 }}>
        {isAdmin && (
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, color: "var(--t2)", cursor: "pointer", padding: "4px 8px", background: "var(--bg2)", borderRadius: 6 }}>
            <input type="checkbox" checked={viewAsStudent} onChange={e => setViewAsStudent(e.target.checked)} /> {!collapsed && "Preview as Student"}
          </label>
        )}
        <button onClick={toggleTheme} style={{ ...btnO, width: "100%", padding: "8px", fontSize: 11, border: "none", background: "var(--bg2)", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
          {theme === 'dark' ? (collapsed ? "☀️" : "☀️ Light Mode") : (collapsed ? "🌙" : "🌙 Dark Mode")}
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: collapsed ? 4 : 10, padding: "8px", background: "var(--bg2)", borderRadius: 8 }}>
          <Avatar src={user.avatar} name={user.name} size={30} borderRadius={8} />
          {!collapsed && (
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis" }}>{user.name}</div>
              <div style={{ fontSize: 9, color: "var(--gold)" }}>{actualRole.toUpperCase()}</div>
            </div>
          )}
          <button onClick={onLogout} style={{ background: "none", border: "none", color: "var(--t3)", cursor: "pointer", fontSize: 16 }}>⎋</button>
        </div>
      </div>
    </div>
  );
}

// ─── ERROR BOUNDARY ────────────────────────────────────────────────────────
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error("ErrorBoundary caught:", error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--bg0)", color: "var(--t1)", textAlign: "center", padding: 40 }}>
          <div>
            <div style={{ fontSize: 48, marginBottom: 16 }}>!</div>
            <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 8 }}>Something went wrong</h1>
            <p style={{ color: "var(--t3)", fontSize: 13, marginBottom: 20, maxWidth: 400 }}>An unexpected error occurred. Please refresh the page to continue.</p>
            <button onClick={() => window.location.reload()} style={{ padding: "10px 24px", background: "linear-gradient(135deg,var(--gold2),var(--gold3))", border: "none", borderRadius: 8, color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "'Outfit',sans-serif" }}>Refresh Page</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─── MAIN APP ──────────────────────────────────────────────────────────────
function AppInner() {
  const [deviceAllowed, setDeviceAllowed] = useState(true);
  
  // Persistent Login — profile in localStorage, JWT in HttpOnly cookie (auto-sent)
  const [user, setUser] = useState(() => {
    try {
      const saved = localStorage.getItem("nt_user");
      if (!saved) return null;
      const parsed = JSON.parse(saved);
      const valid = validateSession(parsed);
      if (!valid) { localStorage.removeItem("nt_user"); return null; }
      // Restore CSRF token from localStorage (JWT is in cookie, not here)
      const csrf = localStorage.getItem('nt_csrf');
      if (csrf) DB.setCsrf(csrf);
      return valid;
    } catch (e) { _log("Session restore failed:", e.name); localStorage.removeItem("nt_user"); return null; }
  });

  // Async SHA-256 hash validation (runs after initial load)
  useEffect(() => {
    if (!user) return;
    validateSessionAsync(user).then(valid => {
      if (!valid) {
        _log("Async session hash mismatch — tamper detected");
        localStorage.removeItem("nt_user");
        DB.setToken(null);
        DB.setCsrf(null);
        setUser(null);
      }
    });
  }, [user]);

  const [page, setPage] = useState("dashboard");
  const [loading, setLoading] = useState(true);
  const ipLoggedRef = useRef(false);
  
  // States
  const [students, setStudents] = useState([]);
  const [videos, setVideos] = useState([]);
  const [breakdowns, setBreakdowns] = useState([]);
  const [notes, setNotes] = useState([]);
  const [actLog, setActLog] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [homework, setHomework] = useState([]);
  const [friendRequests, setFriendRequests] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [propAccounts, setPropAccounts] = useState([]);

  // UI States
  const [searchOpen, setSearchOpen] = useState(false);
  const [theme, setTheme] = useState(() => (typeof localStorage !== 'undefined' ? localStorage.getItem('nt_theme') : null) || 'dark');
  const [viewAsStudent, setViewAsStudent] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return (typeof localStorage !== 'undefined' ? localStorage.getItem('nt_sidebar') : null) === 'collapsed';
    } catch {
      return false;
    }
  });

  // Scroll to top on page change
  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [page]);

  // Ctrl+K keyboard shortcut for search
  useEffect(() => {
    const handleGlobalKeys = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen(prev => !prev);
      }
      if (e.key === "Escape" && searchOpen) {
        setSearchOpen(false);
      }
    };
    window.addEventListener("keydown", handleGlobalKeys);
    return () => window.removeEventListener("keydown", handleGlobalKeys);
  }, [searchOpen]);

  // PHASE 1: Device check, styles (VPN check is now server-side)
  const userRef = useRef(user);
  useEffect(() => { userRef.current = user; }, [user]);

  useEffect(() => {
    setDeviceAllowed(isAllowedDevice());
    const s = document.createElement("style"); s.textContent = CSS; document.head.appendChild(s);
    setLoading(false);

    const cleanupDevTools = detectDevTools((reason) => {
      getIP().then(ip => {
        DB.get(SK.activityLog).then(currentLogs => {
          const logEntry = { id: secureId(), action: "security_breach", details: reason, user: userRef.current?.name || "Unknown", ip, timestamp: new Date().toISOString() };
          const updated = [logEntry, ...(currentLogs || [])].slice(0, 500);
          DB.set(SK.activityLog, updated);
        });
      });
    });

    return () => { document.head.removeChild(s); cleanupDevTools(); };
  }, []);

  // PHASE 2: Load ALL data ONLY after user is authenticated (JWT in cookie, CSRF in memory)
  const dataLoadedRef = useRef(false);
  useEffect(() => {
    if (!user || dataLoadedRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        const [st, v, b, n, a, an, hw, fr, asgn, pa] = await Promise.all([
          DB.get(SK.students), DB.get(SK.videos), DB.get(SK.breakdowns),
          DB.get(SK.notes), DB.get(SK.activityLog), DB.get(SK.announcements),
          DB.get(SK.homework), DB.get(SK.friendRequests), DB.get(SK.assignments),
          DB.get(SK.propAccounts)
        ]);
        if (cancelled) return;
        dataLoadedRef.current = true;

        const safeSt = sanitizeObj(st);
        const safeV = sanitizeObj(v);
        const safeN = sanitizeObj(n);
        const safeA = sanitizeObj(a);
        const safeAn = sanitizeObj(an);
        const safeHw = sanitizeObj(hw);
        const safeFr = sanitizeObj(fr);
        const safeAsgn = sanitizeObj(asgn);
        const safePa = sanitizeObj(pa);

        let safeB = sanitizeObj(b);
        if (safeB && safeSt) {
          let migrated = false;
          const migratedB = safeB.map(trade => {
            if (!trade.userId || /^\d{17,}$/.test(trade.userId) || trade.userId === LEGACY_ADMIN_USER) return trade;
            const match = safeSt.find(s => s.name === trade.userId || s.username === trade.userId);
            if (match && match.discordId) { migrated = true; return { ...trade, userId: match.discordId }; }
            return trade;
          });
          if (migrated) { safeB = migratedB; DB.set(SK.breakdowns, migratedB); }
        }
        if (safeSt) setStudents(safeSt); if (safeV) setVideos(safeV); if (safeB) setBreakdowns(safeB); if (safeN) setNotes(safeN);
        // Activity log: merge with current state so a late data-load doesn't overwrite newer entries (avoids "logs disappearing")
        setActLog(prev => {
          const fromServer = safeA || [];
          const prevList = prev || [];
          if (fromServer.length < prevList.length) {
            const byId = new Map(prevList.map(e => [e.id, e]));
            fromServer.forEach(e => byId.set(e.id, e));
            return Array.from(byId.values()).sort((a, b) => new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime());
          }
          return fromServer;
        });
        if (safeAn) setAnnouncements(safeAn); if (safeHw) setHomework(safeHw);
        if (safeFr) setFriendRequests(safeFr); if (safeAsgn) setAssignments(safeAsgn); if (safePa) setPropAccounts(safePa);
      } catch (e) { _log("Data load failed:", e.name); }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // 3. SECURE IP LOGGING AND AUTO EMAIL ON LOGIN
  const studentsRef = useRef(students);
  useEffect(() => { studentsRef.current = students; }, [students]);
  const studentsLen = students.length;
  useEffect(() => {
    if (user && !ipLoggedRef.current && studentsLen > 0) {
      ipLoggedRef.current = true;
      getIP().then(ip => {
        DB.get(SK.activityLog).then(currentLogs => {
           const logEntry = { id: secureId(), action: "login", user: user.name, role: user.role, ip, timestamp: new Date().toISOString() };
           const updatedAct = [logEntry, ...(currentLogs || [])].slice(0, 500);
           setActLog(updatedAct);
           DB.set(SK.activityLog, updatedAct);
        });

        if (user.role === 'student') {
          const currentStudents = studentsRef.current;
          const sIndex = currentStudents.findIndex(s => s.discordId === user.discordId);
          if (sIndex > -1) {
            const s = currentStudents[sIndex];
            let updatedStudent = { ...s };
            if (user.email) updatedStudent.email = user.email;
            updatedStudent.loginLogs = [{ ip, date: new Date().toISOString() }, ...(s.loginLogs || [])].slice(0, 50);
            const updatedStudents = [...currentStudents];
            updatedStudents[sIndex] = updatedStudent;
            setStudents(updatedStudents);
            DB.set(SK.students, updatedStudents);
          }
        }
      });
    }
  }, [user, studentsLen]);

  useEffect(() => { document.documentElement.className = theme === 'light' ? 'light-theme' : ''; }, [theme]);

  const handleLogin = async (userData, jwt) => {
    DB.setToken(jwt);
    const secure = await createSecureSession(userData, jwt);
    setUser(secure);
  };

  const updateUser = async (newUserData) => {
    const secure = await createSecureSession(newUserData);
    setUser(secure);
  };

  const handleLogout = () => {
    fetch("https://nexustrades.net/logout.php", {
      method: "POST", headers: DB._authHeaders(), credentials: "include"
    }).catch(() => {});
    localStorage.removeItem("nt_user");
    localStorage.removeItem("nt_csrf");
    DB.setToken(null);
    DB.setCsrf(null);
    setUser(null);
    setPage("dashboard");
    setViewAsStudent(false);
    ipLoggedRef.current = false;
    dataLoadedRef.current = false;
    setVideos([]); setBreakdowns([]); setNotes([]); setActLog([]);
    setAnnouncements([]); setHomework([]); setFriendRequests([]);
    setAssignments([]); setPropAccounts([]);
  };

  // GLOBAL SECURITY: Block DevTools & right-click for all users except admin
  useEffect(() => {
    if (!user) return;
    if (user.role === 'admin') return;

    const blockContext = (e) => e.preventDefault();
    const blockDevTools = (e) => {
      if (
        e.key === "F12" ||
        (e.ctrlKey && e.shiftKey && (e.key === "I" || e.key === "J" || e.key === "C")) ||
        (e.ctrlKey && (e.key === "U" || e.key === "u")) ||
        (e.metaKey && e.altKey && e.key === "I")
      ) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    document.addEventListener("contextmenu", blockContext);
    document.addEventListener("keydown", blockDevTools, true);
    return () => {
      document.removeEventListener("contextmenu", blockContext);
      document.removeEventListener("keydown", blockDevTools, true);
    };
  }, [user]);

  // SECURITY: Re-validate session every 5 seconds
  const handleLogoutRef = useRef(handleLogout);
  useEffect(() => { handleLogoutRef.current = handleLogout; });
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => {
      try {
        const stored = localStorage.getItem("nt_user");
        if (!stored) { handleLogoutRef.current(); return; }
        const parsed = JSON.parse(stored);
        if (!validateSession(parsed)) { handleLogoutRef.current(); return; }
        if (parsed.role !== user.role || parsed.discordId !== user.discordId) { handleLogoutRef.current(); }
      } catch (e) { _log("Session check failed:", e.name); handleLogoutRef.current(); }
    }, 5000);
    return () => clearInterval(interval);
  }, [user]);

  // IP is now fetched server-side — no client-side blocking possible

  // DEVICE BLOCKER
  if (!deviceAllowed) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#000", color: "#fff", textAlign: "center", padding: 20 }}>
        <div><h1 style={{ fontSize: 32, marginBottom: 10 }}>🚫 Access Denied</h1><p style={{ color: "var(--t3)" }}>Nexus Trades can only be accessed on a PC/Mac using Google Chrome.</p></div>
      </div>
    );
  }

  if (loading) return <LoadingSkeleton />;
  if (!user) return <ToastProvider><LoginPage onLogin={handleLogin} /></ToastProvider>;

  // Role is trusted from server login response; server enforces authorization on every API call
  const isAdmin = user.role === "admin" && !viewAsStudent;
  const pendingHw = homework.filter(h => h.status === 'pending').length;
  const pendingFr = (friendRequests || []).filter(fr => fr.to === user.discordId && fr.status === "pending").length;
  const D = { videos, breakdowns, notes };

  const pages = {
    dashboard: <Dashboard onNav={setPage} D={D} isAdmin={isAdmin} announcements={announcements} setAnnouncements={setAnnouncements} user={user} students={students} actLog={actLog} homework={homework} />,
    videos: <VideoLibrary videos={videos} setVideos={setVideos} isAdmin={isAdmin} user={user} updateUser={updateUser} actLog={actLog} setActLog={setActLog} students={students} setStudents={setStudents} homework={homework} assignments={assignments} onNav={setPage} />,
    student_homework: <StudentHomework videos={videos} user={user} homework={homework} setHomework={setHomework} assignments={assignments} />,
    breakdowns: <TradingJournals breakdowns={breakdowns} setBreakdowns={setBreakdowns} isAdmin={isAdmin} user={user} students={students} propAccounts={propAccounts} onNav={setPage} />,
    notes: <Resources notes={notes} setNotes={setNotes} isAdmin={isAdmin} />,
    community: <CommunityJournals breakdowns={breakdowns} students={students} user={user} isAdmin={isAdmin} setStudents={setStudents} friendRequests={friendRequests} setFriendRequests={setFriendRequests} />,
    propfirms: <PropFirmTracker propAccounts={propAccounts} setPropAccounts={setPropAccounts} isAdmin={isAdmin} user={user} students={students} breakdowns={breakdowns} />,
    homework: isAdmin ? <HomeworkAdmin homework={homework} setHomework={setHomework} assignments={assignments} setAssignments={setAssignments} videos={videos} /> : null,
    students: isAdmin ? <ManageStudents students={students} setStudents={setStudents} videos={videos} /> : null,
    activity: isAdmin ? <SecurityLogs actLog={actLog} /> : null,
  };

  return (
    <ToastProvider>
      <div style={{ display: "flex", minHeight: "100vh", background: "var(--bg0)" }}>
        <Sidebar
          active={page}
          onNav={setPage}
          user={user}
          onLogout={handleLogout}
          toggleSearch={() => setSearchOpen(true)}
          theme={theme}
          toggleTheme={() => setTheme(t => { const next = t === 'dark' ? 'light' : 'dark'; try { localStorage.setItem('nt_theme', next); } catch (_) {} return next; })}
          viewAsStudent={viewAsStudent}
          setViewAsStudent={setViewAsStudent}
          pendingHwCount={pendingHw}
          pendingFriendCount={pendingFr}
          videos={videos}
          assignments={assignments}
          collapsed={sidebarCollapsed}
          setCollapsed={setSidebarCollapsed}
        />
        <main style={{ flex: 1, marginLeft: sidebarCollapsed ? 64 : 220, padding: "40px 50px", transition: "margin-left 0.2s ease" }}>
          <div key={page} className="nt-page-enter" style={{ maxWidth: 1100, margin: "0 auto" }}>
            {pages[page] || pages.dashboard}
          </div>
        </main>
        {searchOpen && <GlobalSearch D={D} onNav={setPage} close={() => setSearchOpen(false)} />}
      </div>
    </ToastProvider>
  );
}

export default function App() {
  return <ErrorBoundary><AppInner /></ErrorBoundary>;
}